import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";

// API
import ItemService from "../../../../services/customer_appCommonService";
import { getEquipmentRentFormDetail } from "../../../../redux/feature/customer_app/customerSlice";

// Components
import Layout from "../../../../Layout/Layout";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
import Loader from "../../../../Components/Loader";
import MaterialSelectionModalV5 from "./MaterialSearchModal";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";
import Constants from "../../../../common/CustomerConstants";
// eslint-disable-next-line
import TableDropdownMenu from "../../../../Components/TableDropdownMenu/TableDropdownMenu";
import BarcodeSelectorMRCustomerV5 from "./BarcodeSelectorMRCustomerV5";
// Validations
import {
  getTodaysData,
  validateRequired,
  validateContactNumber,
} from "../../../../common/Validations";

// Constants & Utils
import {
  ERF_TYPES,
  PRIVILEGE_SETTINGS,
  DEFAULT_PRIVILEGE_SETTINGS,
  PAYMENT_MODES,
  PAYMENT_MODE_RULES,
} from "./ERFConfigFileConstant";

import {
  SALE_POINT_OPTIONS,
  APPROVED_BY_OPTIONS_LEVEL_ONE,
  // APPROVED_BY_OPTIONS_LEVEL_TWO,
  ORDER_TYPE_OPTIONS,
  SOURCE_OF_CALL_OPTIONS,
  ID_PROOF_OPTIONS,
  DOCTOR_TYPE_OPTIONS,
  // HOSPITAL_OPTIONS,
  STATE_OPTIONS,
  IS_DIABETIC_OPTIONS,
  // MOCK_DOCTORS,
  // RENT_SLAB_OPTIONS,
  PATIENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  DOCTOR_TYPE_SELECTION_OPTIONS,
} from "./constants";

// eslint-disable-next-line
import { message, calculateDays, calculateEndDate } from "./utils";
import UpdateRemarksModal from "../Common/UpdateRemarksModal";
// import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import CommonItemLevelDatatableModal from "../../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import ErfExtenssionModal from "./ErfExtenssionModal";
import ERFAutocompleteSearch from "./ERFAutocompleteSearch";
import GlobalRentConfigPanel from "./GlobalRentConfigPanel";
import BarcodeScannerModal from "./BarcodeScannerModal";
import ERFServiceModal from "./ERFServiceModal";
import ERFPaymentSection from "./ERFPaymentSection";
import ERFAccessoriesModal from "./ERFAccessoriesModal";
import ERFAccessoriesSection from "./ERFAccessoriesSection";
import ERFTravelSection from "./ERFTravelSection";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import ERFCreditNoteSection from "./ERFCreditNoteSection";
import ERFTallyStatusSection from "./ERFTallyStatusSection";

// ----------Approval Process-----------------
const APPROVAL_REQUIRED_CASES = [
  "SPECIAL CASE",
  "DISCOUNT",
  "SPECIAL CASE+DISCOUNT",
];

const REMINDER_DAYS_BEFORE_END = 3;

const calculateRentTotals = (item) => {
  const qty = parseFloat(item.orderedQuantity) || 0;
  const days = parseFloat(item.days) || 0;
  const tax = parseFloat(item.taxPercentage) || 0;

  let masterNetRateForSlab = 0; // net per unit for the whole period
  switch (item.rentSlab) {
    case "Per Day":
      masterNetRateForSlab = (parseFloat(item.rentPerDay) || 0) * days;
      break;
    case "7 Days":
      masterNetRateForSlab = parseFloat(item.rent7Days) || 0;
      break;
    case "15 Days":
      masterNetRateForSlab = parseFloat(item.rent15Days) || 0;
      break;
    case "30 Days":
      masterNetRateForSlab = parseFloat(item.rent30Days) || 0;
      break;
    default:
      masterNetRateForSlab = (parseFloat(item.rentPerDay) || 0) * days;
  }

  const netVal = qty * masterNetRateForSlab;
  const taxAmt = netVal * (tax / 100);
  const grossVal = netVal + taxAmt;

  const ratePerDay =
    item.rentSlab === "Per Day"
      ? parseFloat(item.rentPerDay) || 0
      : days > 0
        ? masterNetRateForSlab / days
        : 0;

  return {
    netValue: parseFloat(netVal.toFixed(2)),
    taxAmount: parseFloat(taxAmt.toFixed(2)),
    grossValue: parseFloat(grossVal.toFixed(2)),
    sellingPrice: parseFloat(ratePerDay.toFixed(4)), // net rate/day
  };
};

const calculateReminderDate = (toDate, days) => {
  if (!toDate) return "";
  const daysCount = parseInt(days) || 0;
  const end = new Date(toDate);
  const reminder = new Date(end);
  if (daysCount <= REMINDER_DAYS_BEFORE_END)
    reminder.setDate(reminder.getDate() + 1);
  else reminder.setDate(reminder.getDate() - REMINDER_DAYS_BEFORE_END);
  return reminder.toISOString().split("T")[0];
};

const calculateSaleTotals = (item) => {
  const qty = parseFloat(item.orderedQuantity) || 0;
  const netUnitPrice = parseFloat(item.sellingPrice) || 0; // net
  const tax = parseFloat(item.taxPercentage) || 0;

  const netVal = qty * netUnitPrice;
  const taxAmt = netVal * (tax / 100);
  const grossVal = netVal + taxAmt;

  return {
    netValue: parseFloat(netVal.toFixed(2)),
    taxAmount: parseFloat(taxAmt.toFixed(2)),
    grossValue: parseFloat(grossVal.toFixed(2)),
    sellingPrice: parseFloat(netUnitPrice.toFixed(2)),
  };
};

// eslint-disable-next-line
const calculateServiceTotals = calculateSaleTotals;

const detectPriceViolation = (item, itemType) => {
  const violations = [];

  // ── SHARED RENT + NURSING LOGIC ──────────────────────────────────────────
  if (itemType === "Rent" || itemType === "Nursing") {
    const netValue = parseFloat(item.netValue) || 0;
    const days = parseFloat(item.days) || 1;
    const qty = parseFloat(item.orderedQuantity) || 1;

    if (days <= 0 || qty <= 0) return [];

    // Effective net rate/day the user has set
    const currentNetRatePerDay = netValue / (days * qty);

    // Master net rate/day for the selected slab
    let masterNetRatePerDay = 0;
    switch (item.rentSlab) {
      case "Per Day":
        masterNetRatePerDay = parseFloat(item.rentPerDay) || 0;
        break;
      case "7 Days":
        masterNetRatePerDay = (parseFloat(item.rent7Days) || 0) / 7;
        break;
      case "15 Days":
        masterNetRatePerDay = (parseFloat(item.rent15Days) || 0) / 15;
        break;
      case "30 Days":
        masterNetRatePerDay = (parseFloat(item.rent30Days) || 0) / 30;
        break;
      default:
        masterNetRatePerDay = parseFloat(item.rentPerDay) || 0;
    }

    if (Math.abs(currentNetRatePerDay - masterNetRatePerDay) > 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType,
        violationType: "Rate Change",
        masterRate: masterNetRatePerDay.toFixed(2),
        changedRate: currentNetRatePerDay.toFixed(2),
        message: `${itemType} net rate changed from ₹${masterNetRatePerDay.toFixed(2)}/day to ₹${currentNetRatePerDay.toFixed(2)}/day (${item.rentSlab})`,
      });
    }
  }

  // ── SALE LOGIC ───────────────────────────────────────────────────────────
  else if (itemType === "Sale") {
    const netValue = parseFloat(item.netValue) || 0;
    const masterMRP = parseFloat(item.masterMRP) || 0; // net
    const masterSellingPrice = parseFloat(item.masterSellingPrice) || 0; // net
    const qty = parseFloat(item.orderedQuantity) || 1;
    const netPerUnit = netValue / qty;

    if (netPerUnit > masterMRP + 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Sale",
        violationType: "Above MRP",
        masterMRP: masterMRP.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Net unit ₹${netPerUnit.toFixed(2)} exceeds Master MRP ₹${masterMRP.toFixed(2)}`,
      });
    }

    if (netPerUnit < masterSellingPrice - 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Sale",
        violationType: "Below Selling Price",
        masterSellingPrice: masterSellingPrice.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Net unit ₹${netPerUnit.toFixed(2)} below Master ₹${masterSellingPrice.toFixed(2)}`,
      });
    }
  }

  // ── SERVICE LOGIC ────────────────────────────────────────────────────────
  else if (itemType === "Service") {
    const netValue = parseFloat(item.netValue) || 0;
    const masterMRP = parseFloat(item.masterMRP) || 0;
    const masterSellingPrice = parseFloat(item.masterSellingPrice) || 0;
    const qty = parseFloat(item.orderedQuantity) || 1;
    const netPerUnit = netValue / qty;

    if (netPerUnit > masterMRP + 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Service",
        violationType: "Above MRP",
        masterMRP: masterMRP.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Service net ₹${netPerUnit.toFixed(2)} exceeds MRP ₹${masterMRP.toFixed(2)}`,
      });
    }

    if (netPerUnit < masterSellingPrice - 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Service",
        violationType: "Below Service Price",
        masterSellingPrice: masterSellingPrice.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Service net ₹${netPerUnit.toFixed(2)} below Master ₹${masterSellingPrice.toFixed(2)}`,
      });
    }
  }

  return violations;
};

//  DATA HELPERS (Outside - pure transformers)
const getDataWithTotal = (items) => {
  if (items.length === 0) return [];
  const typeTotal = items.reduce(
    (acc, item) => {
      acc.netValue += parseFloat(item.netValue || 0);
      acc.grossValue += parseFloat(item.grossValue || 0);
      acc.taxAmount += parseFloat(item.taxAmount || 0);
      if (item.itemType === "Rent") {
        acc.actualSecurityAmt += parseFloat(item.actualSecurityAmt || 0);
        acc.securityAmt += parseFloat(item.securityAmt || 0);
      }
      return acc;
    },
    {
      netValue: 0,
      grossValue: 0,
      taxAmount: 0,
      actualSecurityAmt: 0,
      securityAmt: 0,
    },
  );
  return [
    ...items,
    {
      isTotal: true,
      local_id: "total",
      netValue: typeTotal.netValue.toFixed(2),
      grossValue: typeTotal.grossValue.toFixed(2),
      taxAmount: typeTotal.taxAmount.toFixed(2),
      actualSecurityAmt: typeTotal.actualSecurityAmt.toFixed(2),
      securityAmt: typeTotal.securityAmt.toFixed(2),
    },
  ];
};

const getDataWithTotalNursing = (items) => {
  if (items.length === 0) return [];
  const typeTotal = items.reduce(
    (acc, item) => {
      acc.netValue += parseFloat(item.netValue || 0);
      acc.grossValue += parseFloat(item.grossValue || 0);
      acc.taxAmount += parseFloat(item.taxAmount || 0);
      return acc;
    },
    { netValue: 0, grossValue: 0, taxAmount: 0 },
  );
  return [
    ...items,
    {
      isTotal: true,
      local_id: "total",
      netValue: typeTotal.netValue.toFixed(2),
      grossValue: typeTotal.grossValue.toFixed(2),
      taxAmount: typeTotal.taxAmount.toFixed(2),
    },
  ];
};

// ─────────────────────────────────────────────────────────────────────────
// Assigns equipmentItemNr to items that don't have one.
// - Preserves existing numbers (so barcodes/history stay linked)
// - New items get max(existing) + 1, +2, ...
// ─────────────────────────────────────────────────────────────────────────
const assignEquipmentItemNrs = (items) => {
  if (!Array.isArray(items) || items.length === 0) return items;

  let maxNr = items.reduce(
    (max, item) => Math.max(max, Number(item.equipmentItemNr) || 0),
    0,
  );

  return items.map((item) =>
    item.equipmentItemNr ? item : { ...item, equipmentItemNr: ++maxNr },
  );
};

const ERFDetail = (props) => {
  const showEditButtons = props.showEditButtons ?? true;
  const showStoreSection = props.showStoreSection ?? true;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const barcodeBufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);
  const scanTimerRef = useRef(null);

  // Redux Data
  const userData = useSelector((state) => state?.user?.data);
  // const currentUserId = userData.userId || "M000";
  const documentNumber = useSelector(
    (state) => state?.customer?.equipmentRentForm?.documentNumber,
  );

  const currentUserName =
    userData?.data?.firstName ||
    userData?.data?.name ||
    userData?.data?.userId ||
    "";

  // eslint-disable-next-line
  // const isStoreUser = userData?.category?.toUpperCase() === "STORE";

  const [isSalesAdmin, setIsSalesAdmin] = useState(false);

  const isStoreUser = useMemo(() => {
    return userData?.roles?.some((r) => r.roleId === "STORE") || false;
  }, [userData]);

  const isPCDUser = useMemo(() => {
    return userData?.roles?.some((r) => r.roleId === "SALES-PCD") || false;
  }, [userData]);

  const isServiceExecutive = useMemo(() => {
    return (
      userData?.roles?.some((r) => r.roleId === "SERVICE-EXECUTIVE") || false
    );
  }, [userData]);

  useEffect(() => {
    if (userData?.roles) {
      const isAllowed = userData.roles.some((r) => r.roleId === "SALES-ADMIN");
      setIsSalesAdmin(isAllowed);
    }
  }, [userData]);

  const timestamp = new Date().toISOString();

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================

  const getErfTypeByName = (name) => {
    return ERF_TYPES.find((type) => type.name === name);
  };

  const convertToNumbers = (obj) => {
    const converted = { ...obj };

    const numericFields = [
      "orderedQuantity",
      "sellingPrice",
      "rentPerDay",
      "rent7Days",
      "rent15Days",
      "rent30Days",
      "days",
      "netValue",
      "grossValue",
      "taxPercentage",
      "taxAmount",
      "actualSecurityAmt",
      "securityAmt",
      "masterMRP",
      "masterSellingPrice",
      "masterUnitPrice",
      "refundAmt",
      "refundDays",
    ];

    numericFields.forEach((field) => {
      if (converted[field] !== undefined && converted[field] !== "") {
        converted[field] = parseFloat(converted[field]) || 0;
      }
    });

    return converted;
  };

  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================

  // UI State
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(true); // true = View Mode, false = Edit Mode
  const [disabled, setDisabled] = useState(true); // Controls input fields
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [getDropdownData, setDropdownData] = useState("");
  const [activeItemType, setActiveItemType] = useState("Rent");
  const [activeItemTypeCode, setActiveItemTypeCode] = useState("");

  const [editForStore, setEditForStore] = useState(true); // true = View Mode, false = Edit Mode
  const [storeDisabled, setStoreDisabled] = useState(true); // Controls input fields

  const [erfDispatchIndicator, setIsReadyToDispatch] = useState(false);
  const [dispatchRemarks, setDispatchRemarks] = useState("");

  const [barcodeOptionsMap, setBarcodeOptionsMap] = useState({});
  const [assigningBarcode, setAssigningBarcode] = useState({});

  // Accordion Toggles
  const [isCardVisible, setCardVisible] = useState(true);
  const [isCardVisible2, setCardVisible2] = useState(true);
  const [isCardVisible3, setCardVisible3] = useState(true);
  const [visible, setVisible] = useState({
    PAY: true,
    SEC: true,
    SMOD: true,
  });

  // Modal State
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);
  const [failureModalData] = useState({
    title: "Validation Error",
    ErrorMsg: "Please check the required fields.",
  });

  const [priceViolations, setPriceViolations] = useState([]);
  // eslint-disable-next-line
  const [acknowledgedViolations, setAcknowledgedViolations] = useState(
    new Set(),
  );
  const [showApprovalRequired, setShowApprovalRequired] = useState(false);

  // Patient Loading States (from CreateERF)
  const [patientList, setPatientList] = useState([]);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);

  const [selectedNurse, setSelectedNurse] = useState(null);
  const [erfNurseList, setErfNurseList] = useState([]); // Dynamic Nurse List
  const [isLoadingErfNurse, setIsLoadingErfNurse] = useState(false);

  const [erfForType, setErfForType] = useState("Patient"); // "Patient" or "Hospital"
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [erfHospitalList, setErfHospitalList] = useState([]);
  const [isErfLoadingHospitals, setErfIsLoadingHospitals] = useState(false);

  // Address copy checkbox
  const [isSameAddress, setIsSameAddress] = useState(false);

  // Data State
  const [hospitalList, setHospitalList] = useState([]); // Dynamic Hospital List
  const [rentItems, setRentItems] = useState([]);
  const [saleItems, setSaleItems] = useState([]); // reused for Service Items
  const [nursingItems, setNursingItems] = useState([]);

  const [validation, setValidation] = useState({});

  const [doctorsList, setDoctorsList] = useState([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(false);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);

  const [isAssigningBarcode, setIsAssigningBarcode] = useState(false);
  const isAssigningBarcodeRef = useRef(false);

  const [selectedERF, setSelectedERF] = useState(null);
  const [isERFLoaded, setIsERFLoaded] = useState(false);

  const [companyList, setCompanyList] = useState([]);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [accessoryItems, setAccessoryItems] = useState([]);
  const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);
  const [selectedParentForAccessory, setSelectedParentForAccessory] =
    useState(null);
  const [assigningAccessoryBarcode, setAssigningAccessoryBarcode] = useState(
    {},
  );

  // Main Form Data
  const [formInputs, setFormInputs] = useState({
    // Order Details
    erfType: "Select",
    erfTypeCode: "",
    erfTypeDescription: "",
    orderDate: "",
    salePoint: userData?.organisationName || "",
    costCenter: userData?.firstName || "",
    erfCostCenterSubGroup: "SALESTEAM",
    privilegeCase: "Select",
    billingName: "Patient",
    orderType: "Select",
    sourceOfCall: "Select",
    erfStatus: "",
    erfBarcodeAssignedDateAndTime: "",

    companyName: "",
    companyOrganisation: "",
    companyNr: "",
    companyCode: "",

    // Doctor Details
    doctorName: "",
    drType: "Select",
    drCode: "",
    drListedType: "Select",
    patientErfHospitalName: "",

    // Patient Details
    patientName: "",
    mobileNr: "",
    age: "",
    weight: "",
    gender: "",
    gstNo: "",
    panNo: "",
    isDiabetic: "No",
    aadharNo: "",
    patientChallenges: "",

    // Caregiver Details
    caregiverName: "",
    caregiverRelation: "",
    idProof: "Select",
    idProofNumber: "",
    emailId: "",

    // Address
    billingAddress: "",
    billingState: "Select",
    billingPinCode: "",
    deliveryAddress: "",
    deliveryState: "Select",
    deliveryPinCode: "",

    // Payment Details (Left)
    cartage: "",
    installationCharges: "",
    receivedAmount: "",

    // Payment Breakup (Right)
    paymentMode: "Select",
    cashAmount: "",
    balanceAmtBreakup: "0",
    chequeNo: "",
    cardTransNo: "",
    bankName: "",
    receiptNo: "",

    // Security Details (Left)
    standardSecurityAmt: 0,
    receivedSecurityAmt: "",

    // Security Breakup (Right)
    securityPincode: "",
    securityAmtMode: "Select",
    securityCashAmt: "",
    balanceSecurityBreakup: "0",
    securityChequeNo: "",
    securityCardTransNo: "",
    securityBankName: "",

    // Security Mode
    securityMode: "Select",
    securityModeCash: "",
    securityModeBal: "0",
    remarks: "",

    // Approval process
    approvalLevel: "1", // Stores "1" or "2"
    approvers: [],
    approverList: [],
    version: "V0",

    patientType: "Select",
    patient: "",

    nurseUserId: "",
    nurseName: "",
    nurseAssociateNumber: "",
    nurseMobileNr: "",
    nurseEmail: "",
    nurseAddress: "",
    nurseAddress2: "",
    nurseCity: "",
    nursePanNo: "",
    nurseBillingAddress: "",
    nurseBillingState: "",
    nurseBillingPinCode: "",
    serviceProviderCode: "",
    serviceProviderName: "",

    hospitalName: "",
    hospitalAssociateNumber: "",
    hospitalMobileNr: "",
    hospitalEmail: "",
    hospitalGstNo: "",
    hospitalAddress: "",
    hospitalAddress2: "",
    hospitalCity: "",
    hospitalBillingAddress: "",
    hospitalState: "",
    hospitalPinCode: "",
  });

  // ────────────────────────────────────────────────────────────────────────────
  // EDIT PERMISSION FLAGS
  // ────────────────────────────────────────────────────────────────────────────
  // Adjust EDITABLE_STATUSES per client requirement
  const EDITABLE_STATUSES = ["In progress", "Approved"];
  // eslint-disable-next-line
  const NON_EDITABLE_ERF_STATUSES = ["Dispatched", "Cancelled"];
  const EDITABLE_ERF_STATUS_CODES = ["SERV", "ATS", "RTD", "USE"];
  const EDITABLE_PC_CALL_CODES = [""];

  // Normal direct edit on a live ERF
  // const canEditERF =
  //   isSalesAdmin &&
  //   EDITABLE_STATUSES.includes(formInputs.status) &&
  //   !NON_EDITABLE_ERF_STATUSES.includes(formInputs.erfStatus);

  const canEditERF =
    isSalesAdmin &&
    EDITABLE_STATUSES.includes(formInputs.status) &&
    EDITABLE_ERF_STATUS_CODES.includes(formInputs.erfStatusCode) &&
    EDITABLE_PC_CALL_CODES.includes(formInputs.pcCallStatusCode ?? "");

  // Edit-and-resubmit a Cancelled ERF
  const canEditCancelledERF = isSalesAdmin && formInputs.status === "Cancelled";

  // Barcode assignment & accessory management are only allowed when status is "Assign To Store"
  const isBarcodeAssignmentAllowed =
    isStoreUser &&
    formInputs.status !== "In progress" &&
    formInputs.status !== "Cancelled";

  // ── Travel Section state ──────────────────────────────────────────────────────
  const [travelHistory, setTravelHistory] = useState([]);

  const isHospital = useMemo(
    () => formInputs.erfForType === "Hospital",
    [formInputs.erfForType],
  );

  const [globalRentConfig, setGlobalRentConfig] = useState({
    rentSlab: "Per Day",
    rentSlabCode: "PER_DAY",
    rentSlabDescription: "PER_DAY",
    fromDate: getTodaysData(),
    toDate: getTodaysData(),
    days: 1,
  });
  // eslint-disable-next-line
  const [financeExectiveUserRoleData, setFinanceExecutiveUserRoleData] =
    useState();

  useEffect(() => {
    if (userData?.roles) {
      const allowedRoles = ["FI-EXECUTIVE"];
      const isAllowed = userData?.roles.some((r) =>
        allowedRoles.includes(r.roleId),
      );
      setFinanceExecutiveUserRoleData(isAllowed);
    }
  }, [userData]);

  // =============================================================================
  // DERIVED STATE & OPTIONS
  // =============================================================================

  const erfTypeOptions = useMemo(() => ERF_TYPES, []);

  const paymentModeDropdown = useMemo(
    () =>
      Object.values(PAYMENT_MODES).map((mode, i) => ({
        id: i + 1,
        name: mode,
      })),
    [],
  );

  const privilegeOptions = useMemo(() => {
    // Find the ERF object that matches the selected name
    const selectedType = ERF_TYPES.find(
      (type) => type.name === formInputs.erfType,
    );

    // Return its embedded privileges, or empty array if not found
    return selectedType?.privileges || [];
  }, [formInputs.erfType]);

  // Calculate Totals
  const allItems = useMemo(
    () => [...rentItems, ...saleItems, ...nursingItems], // ← add nursingItems
    [rentItems, saleItems, nursingItems],
  );

  // const totals = useMemo(() => {
  //   return allItems.reduce(
  //     (acc, item) => {
  //       acc.net += parseFloat(item.netValue || 0);
  //       acc.tax += parseFloat(item.taxAmount || 0);
  //       if (item.itemType === "Rent") {
  //         acc.security += parseFloat(item.securityAmt || 0);
  //       }
  //       return acc;
  //     },
  //     { net: 0, tax: 0, security: 0 },
  //   );
  // }, [allItems]);
  // const totalPayable = (totals.net + totals.tax).toFixed(2);

  const totals = useMemo(() => {
    return allItems.reduce(
      (acc, item) => {
        acc.net += parseFloat(item.netValue || 0);
        acc.tax += parseFloat(item.taxAmount || 0);
        acc.gross += parseFloat(item.grossValue || 0);
        if (item.itemType === "Rent") {
          acc.security += parseFloat(item.securityAmt || 0);
          acc.standardSecurityAmt += parseFloat(item.actualSecurityAmt || 0);
        }
        return acc;
      },
      { net: 0, tax: 0, gross: 0, security: 0, standardSecurityAmt: 0 },
    );
  }, [allItems]);

  const totalPayable = Math.round(totals.gross);
  const itemTypeTotals = useMemo(() => {
    const sumItems = (items) =>
      items.reduce(
        (acc, item) => {
          acc.netTotal += parseFloat(item.netValue || 0);
          acc.taxTotal += parseFloat(item.taxAmount || 0);
          acc.grossTotal += parseFloat(item.grossValue || 0);
          acc.securityTotal += parseFloat(item.securityAmt || 0);
          return acc;
        },
        { netTotal: 0, taxTotal: 0, grossTotal: 0, securityTotal: 0 },
      );

    const saleOnly = saleItems.filter((i) => i.itemType === "Sale");
    const serviceOnly = saleItems.filter((i) => i.itemType === "Service");

    const rent = sumItems(rentItems);
    const sale = sumItems(saleOnly);
    const service = sumItems(serviceOnly);
    const nursing = sumItems(nursingItems);

    const fmt = (n) => parseFloat(n.toFixed(2));

    return {
      rentNetTotal: fmt(rent.netTotal),
      rentTaxTotal: fmt(rent.taxTotal),
      rentGrossTotal: fmt(rent.grossTotal),
      rentSecurityTotal: fmt(rent.securityTotal),

      saleNetTotal: fmt(sale.netTotal),
      saleTaxTotal: fmt(sale.taxTotal),
      saleGrossTotal: fmt(sale.grossTotal),

      serviceNetTotal: fmt(service.netTotal),
      serviceTaxTotal: fmt(service.taxTotal),
      serviceGrossTotal: fmt(service.grossTotal),

      nursingNetTotal: fmt(nursing.netTotal),
      nursingTaxTotal: fmt(nursing.taxTotal),
      nursingGrossTotal: fmt(nursing.grossTotal),

      lineItemNetTotal: fmt(
        rent.netTotal + sale.netTotal + service.netTotal + nursing.netTotal,
      ),
      lineItemTaxTotal: fmt(
        rent.taxTotal + sale.taxTotal + service.taxTotal + nursing.taxTotal,
      ),
      lineItemGrossTotal: fmt(
        rent.grossTotal +
          sale.grossTotal +
          service.grossTotal +
          nursing.grossTotal,
      ),
      lineItemGrossRounded: Math.round(
        rent.grossTotal +
          sale.grossTotal +
          service.grossTotal +
          nursing.grossTotal,
      ),
      roundOffAmount: fmt(
        Math.round(
          rent.grossTotal +
            sale.grossTotal +
            service.grossTotal +
            nursing.grossTotal,
        ) -
          (rent.grossTotal +
            sale.grossTotal +
            service.grossTotal +
            nursing.grossTotal),
      ),
    };
  }, [rentItems, saleItems, nursingItems]);

  const netAmount = (
    parseFloat(totalPayable) +
    (parseFloat(formInputs.cartage) || 0) +
    (parseFloat(formInputs.installationCharges) || 0)
  ).toFixed(2);

  const balanceAmount = (
    parseFloat(netAmount) - (parseFloat(formInputs.receivedAmount) || 0)
  ).toFixed(2);

  const balanceSecurityAmt = (
    totals.security - (parseFloat(formInputs.receivedSecurityAmt) || 0)
  ).toFixed(2);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  const userFirstName = userData?.firstName || "";

  // 1. Load Initial Data
  const loadInitialPageData = useCallback(async () => {
    if (documentNumber) {
      // setLoading(true);
      try {
        const payload = {
          queryName: "ERFDetail",
          page: 1,
          per_page: 1,
          timeZone: "Asia/Calcutta",
          documentNumber: documentNumber,
        };
        const resultAction = await dispatch(
          getEquipmentRentFormDetail(payload),
        );
        if (getEquipmentRentFormDetail.fulfilled.match(resultAction)) {
          const data = resultAction.payload.data?.[0];
          if (data) {
            // setFormInputs((prev) => ({ ...prev, ...data }));
            setFormInputs((prev) => ({
              ...prev,
              ...data,
              // Pre-populate local approvers from saved approverList
              approvers:
                data.approverList?.length > 0 ? [...data.approverList] : [],
            }));
            // Populate dropdown lists based on loaded ERF data
            if (data.drType === "Listed") {
              if (data.drListedType === "Doctor") {
                fetchAllDoctors();
              } else if (data.drListedType === "Hospital") {
                fetchHospitalList();
                if (data.patientErfHospitalName) {
                  fetchDoctorsByHospital(data.patientErfHospitalName);
                }
              }
            }

            if (data.patientType === "Listed") {
              fetchPatientList();
            }

            // if (data.rentItems && data.rentItems.length > 0) {
            //   setRentItems(data.rentItems);

            //   const firstRentItem = data.rentItems[0];
            //   if (firstRentItem) {
            //     setGlobalRentConfig({
            //       rentSlab: firstRentItem.rentSlab || "Per Day",
            //       rentSlabCode: firstRentItem.rentSlabCode || "PER_DAY",
            //       rentSlabDescription:
            //         firstRentItem.rentSlabDescription || "PER_DAY",
            //       fromDate: firstRentItem.fromDate || getTodaysData(),
            //       toDate: firstRentItem.toDate || "",
            //       days: firstRentItem.days || 0,
            //     });
            //   }
            // }

            if (data.rentItems?.length > 0) {
              setRentItems(data.rentItems);
              const firstRentItem = data.rentItems[0];
              if (firstRentItem)
                setGlobalRentConfig({
                  rentSlab: firstRentItem.rentSlab || "Per Day",
                  rentSlabCode: firstRentItem.rentSlabCode || "PER_DAY",
                  rentSlabDescription:
                    firstRentItem.rentSlabDescription || "PER_DAY",
                  fromDate: firstRentItem.fromDate || getTodaysData(),
                  toDate: firstRentItem.toDate || "",
                  days: firstRentItem.days || 0,
                });
            }
            if (data.saleItems && data.saleItems.length > 0) {
              setSaleItems(data.saleItems);
            }
            // if (data.nursingItems?.length > 0) {
            //   setNursingItems(data.nursingItems);
            //   const firstNursingItem = data.nursingItems[0];
            //   if (firstNursingItem) {
            // setGlobalRentConfig({
            //   rentSlab: firstNursingItem.rentSlab || "Per Day",
            //   rentSlabCode: firstNursingItem.rentSlabCode || "PER_DAY",
            //   rentSlabDescription:
            //     firstNursingItem.rentSlabDescription || "PER_DAY",
            //   fromDate: firstNursingItem.fromDate || getTodaysData(),
            //   toDate: firstNursingItem.toDate || "",
            //   days: firstNursingItem.days || 0,
            // });
            //   }

            if (data.nursingItems?.length > 0) {
              setNursingItems(data.nursingItems);
              // Only set globalRentConfig from nursing if there are NO rent items
              if (!data.rentItems?.length) {
                const firstNursingItem = data.nursingItems[0];
                if (firstNursingItem)
                  setGlobalRentConfig({
                    rentSlab: firstNursingItem.rentSlab || "Per Day",
                    rentSlabCode: firstNursingItem.rentSlabCode || "PER_DAY",
                    rentSlabDescription:
                      firstNursingItem.rentSlabDescription || "PER_DAY",
                    fromDate: firstNursingItem.fromDate || getTodaysData(),
                    toDate: firstNursingItem.toDate || "",
                    days: firstNursingItem.days || 0,
                  });
              }
            } else if (
              data.serviceItems &&
              data.serviceItems.length > 0 &&
              data.erfType === "Service"
            ) {
              setSaleItems(data.serviceItems);
            } else if (data.items) {
              setRentItems(data.items.filter((i) => i.itemType === "Rent"));
              setSaleItems(
                data.items.filter(
                  (i) => i.itemType !== "Rent" && i.itemType !== "Nursing",
                ),
              );
              setNursingItems(
                data.items.filter((i) => i.itemType === "Nursing"),
              );
            }

            if (data?.accessoryItems && data?.accessoryItems.length > 0) {
              // Build Set of sale item local_ids to recalculate parentGodownCode

              // This fixes records saved with wrong godownCode due to stale closure

              // const saleLocalIds = new Set(
              //   (data.saleItems || []).map((s) => s.local_id),
              // );
              const saleLocalIds = new Set(
                [...(data.saleItems || []), ...(data.serviceItems || [])].map(
                  (s) => s.local_id,
                ),
              );

              setAccessoryItems(
                data.accessoryItems.map((item) => {
                  const isParentSale = saleLocalIds.has(item.parentLocalId);

                  return {
                    ...item,

                    savedToBackend: true,

                    parentGodownCode: isParentSale ? "SL" : "RE",

                    // itemType: isParentSale ? "Sale" : item.itemType || "Rent",
                    itemType: isParentSale
                      ? item.itemType || "Sale"
                      : item.itemType || "Rent",
                  };
                }),
              );
            }

            // Also restore erfForType from loaded data
            if (data.erfForType) {
              setErfForType(data.erfForType); // "Patient" or "Hospital"
            }

            // Restore selected hospital if hospital ERF
            if (data.erfForType === "Hospital" && data.hospitalName) {
              setSelectedHospital({
                hospitalName: data.hospitalName,
                AssociateNumber: data.hospitalAssociateNumber,
                mobileNr: data.hospitalMobileNr,
                email: data.hospitalEmail,
              });
            }

            // ADD after the erfForType restore block:
            if (data.erfForType === "Hospital") {
              setFormInputs((prev) => ({
                ...prev,
                costCenter: data?.hospitalName || data?.costCenter || "",
                erfCostCenterSubGroup: "HOSPITAL",
              }));
            } else {
              setFormInputs((prev) => ({
                ...prev,
                costCenter: data.costCenter || userFirstName || "",
                erfCostCenterSubGroup:
                  data.erfCostCenterSubGroup || "SALESTEAM",
              }));
            }

            // Restore selected company from loaded ERF data
            if (data.companyCode && data.companyName) {
              setSelectedCompany({
                name: data.companyCode, // this is what ComboboxField shows
                companyNr: data.companyNr || "",
                companyCode: data.companyCode || "",
                companyName: data.companyName || "",
                organisationName: data.companyOrganisation || "",
              });
            }

            // Restore selected nurse if nursing service ERF
            if (data.erfType === "Nursing Service" && data.nurseName) {
              setSelectedNurse({
                name: data.nurseName,
                hospitalName: data.nurseName,
                organisationName: data.nurseName,
              });
            }
          }
        } else {
          toast.error("Failed to fetch ERF details");
        }
      } catch (err) {
        console.error("Load Error:", err);
        toast.error("Error loading ERF details");
      } finally {
        setLoading(false);
      }
    }
  }, [documentNumber, dispatch, userFirstName]);

  useEffect(() => {
    loadInitialPageData();
    // eslint-disable-next-line
  }, [documentNumber]);

  useEffect(() => {
    setTravelHistory(formInputs.travelHistory ?? []);
  }, [formInputs.travelHistory]);

  // Enable/Disable Form
  useEffect(() => {
    const isEnabled =
      formInputs.erfType !== "Select" && formInputs.privilegeCase !== "Select";
    setIsFormEnabled(isEnabled);
  }, [formInputs.erfType, formInputs.privilegeCase]);

  // Handle Dropdown Selections
  useEffect(() => {
    if (getDropdownData) {
      const { textField, name } = getDropdownData;
      // console.log("Dropdown changed:", textField, name);

      if (name === undefined || name === null) {
        return;
      }

      const currentErfHospitalList = erfHospitalList;
      const currentErfNurseList = erfNurseList;
      const currentCompanyList = companyList;
      const currentDoctorsList = doctorsList;
      const currentPatientList = patientList;

      setFormInputs((prev) => {
        const newData = { ...prev, [textField]: name };

        if (textField === "erfType") {
          const selectedErfType = getErfTypeByName(name);
          if (selectedErfType) {
            newData.erfTypeCode = selectedErfType.code;
            newData.erfTypeDescription = selectedErfType.description;
          }
          newData.privilegeCase = "Select";
        }

        if (textField === "selectedCompanyId") {
          const company = currentCompanyList.find((c) => c.name === name);
          if (company) {
            setSelectedCompany(company);
            newData.companyName = company.companyName;
            newData.companyOrganisation = company.organisationName;
            newData.companyNr = company.companyNr;
            newData.companyCode = company.companyCode;
          }
        }

        // Reset Doctor Logic
        // if (textField === "drType") {
        //   newData.patientErfHospitalName = "";
        //   newData.doctorName = "";
        //   newData.drCode = "";
        //   setHospitalList([]);
        //   setDoctorsList([]);

        //   if (name === "Hospital") {
        //     // User selected "Hospital" → Fetch hospital list
        //     fetchHospitalList();
        //   } else if (name === "Doctor") {
        //     // User selected "Doctor" → Fetch all doctors directly
        //     fetchAllDoctors();
        //   } else if (name === "Listed") {
        //     // Keep existing "Listed" logic if needed
        //     fetchHospitalList(); // Or keep your old logic
        //   }
        // }

        if (textField === "drType") {
          newData.drListedType = "Select";
          newData.patientErfHospitalName = "";
          newData.doctorName = "";
          newData.drCode = "";
          setHospitalList([]);
          setDoctorsList([]);
        }

        if (textField === "drListedType") {
          newData.patientErfHospitalName = "";
          newData.doctorName = "";
          newData.drCode = "";
          setHospitalList([]);
          setDoctorsList([]);

          if (name === "Doctor") {
            fetchAllDoctors();
          } else if (name === "Hospital") {
            fetchHospitalList();
          }
        }

        if (textField === "patientErfHospitalName") {
          newData.doctorName = "";
          newData.drCode = "";

          if (name) {
            // Fetch doctors for this hospital
            fetchDoctorsByHospital(name);
          }
        }

        // KEEP existing doctorName block unchanged:
        if (textField === "doctorName") {
          const selectedDoc = currentDoctorsList.find(
            (doc) => doc.name === name,
          );
          if (selectedDoc) {
            newData.drCode = selectedDoc.userId || selectedDoc.code || "";
            if (!newData.patientErfHospitalName && selectedDoc.hospitalName) {
              newData.patientErfHospitalName = selectedDoc.hospitalName;
            }
          }
        }

        // Reset ERF Type / Privilege
        // if (textField === "erfType" || textField === "privilegeCase") {
        if (
          (textField === "erfType" || textField === "privilegeCase") &&
          !disabled
        ) {
          setRentItems([]);
          setSaleItems([]);
          newData.cartage = "";
          newData.installationCharges = "";
          newData.receivedAmount = "";
          newData.paymentMode = "Select";
          newData.cashAmount = "";
          newData.balanceAmtBreakup = "0";
          newData.chequeNo = "";
          newData.cardTransNo = "";
          newData.bankName = "";
          newData.receiptNo = "";
          newData.receivedSecurityAmt = "";
          newData.securityPincode = "";
          newData.securityAmtMode = "Select";
          newData.securityCashAmt = "";
          newData.balanceSecurityBreakup = "0";
          newData.securityChequeNo = "";
          newData.securityCardTransNo = "";
          newData.securityBankName = "";
          newData.securityMode = "Select";
          newData.securityModeCash = "";
          newData.securityModeBal = "0";

          if (textField === "erfType" && prev.erfType !== name) {
            newData.privilegeCase = "Select";
          }
        }

        // Reset Payment Mode Logic
        if (textField === "paymentMode") {
          newData.cashAmount = "";
          newData.balanceAmtBreakup = "0";
          newData.chequeNo = "";
          newData.cardTransNo = "";
          newData.bankName = "";
          newData.receiptNo = "";

          if (name === "NR") {
            newData.receivedAmount = "0"; //  Auto-zero when NR
          }
        }

        // Reset Security Amt Mode Logic
        if (textField === "securityAmtMode") {
          newData.securityCashAmt = "";
          newData.balanceSecurityBreakup = "0";
          newData.securityChequeNo = "";
          newData.securityCardTransNo = "";
          newData.securityBankName = "";
        }

        // Reset Security Mode Logic
        if (textField === "securityMode") {
          newData.securityModeCash = "";
          newData.securityModeBal = "0";
        }

        // Reset ID Proof
        if (textField === "idProof") {
          newData.idProofNumber = "";
        }

        // ADD inside the getDropdownData useEffect, before "return newData":

        // Patient Type selection
        if (textField === "patientType") {
          newData.patient = "";
          setPatientList([]);
          if (name === "Listed") {
            fetchPatientList();
          }
        }

        // Patient selection - auto-fill all patient fields
        if (textField === "patient") {
          const selectedPatient = currentPatientList.find(
            (p) => p.name === name,
          );

          if (selectedPatient) {
            newData.patientName =
              selectedPatient.employeeName ||
              selectedPatient.organisationName ||
              "";
            newData.mobileNr = selectedPatient.mobileNr || "";
            newData.emailId = selectedPatient.email || "";
            newData.aadharNo = selectedPatient.aadharNo || "";
            newData.panNo = selectedPatient.panNo || "";
            newData.AssociateNumber = selectedPatient.AssociateNumber || "";

            newData.age = selectedPatient.age || "";
            newData.weight = selectedPatient.weight || "";
            newData.gender = selectedPatient.gender || "";
            newData.patientChallenges = selectedPatient.patientChallenges || "";

            newData.caregiverName = selectedPatient.caregiverName || "";
            newData.emailId = selectedPatient.emailId || "";
            newData.caregiverRelation = selectedPatient.caregiverRelation || "";
            newData.idProof = selectedPatient.idProof || "Select";
            newData.idProofNumber = selectedPatient.idProofNumber || "";

            // Auto-fill billing address from patient registration data
            const fullAddress = [
              selectedPatient.address,
              selectedPatient.address2,
              selectedPatient.city,
            ]
              .filter(Boolean)
              .join(", ");

            newData.billingAddress = fullAddress;
            newData.billingState = selectedPatient.state || "Select";
            newData.billingPinCode = selectedPatient.cityPostalCode || "";

            newData.deliveryAddress = fullAddress;
            newData.deliveryState = selectedPatient.state || "Select";
            newData.deliveryPinCode = selectedPatient.cityPostalCode || "";
          }
        }

        // Hospital selection for ERF (B2B)
        // if (textField === "selectedHospitalId") {
        //   const erfHospital = erfHospitalList.find((h) => h.name === name);

        //   if (erfHospital) {
        //     setSelectedHospital(erfHospital);

        //     newData.hospitalName =
        //       erfHospital.hospitalName || erfHospital.organisationName || "";
        //     newData.AssociateNumber = erfHospital.AssociateNumber || "";
        //     newData.mobileNr = erfHospital.mobileNr || "";
        //     newData.email = erfHospital.email || "";
        //     newData.address = erfHospital.address || "";
        //     newData.address2 = erfHospital.address2 || "";
        //     newData.city = erfHospital.city || "";

        //     // Auto-extract GST from certifications
        //     const gstCert = erfHospital.certifications?.find(
        //       (cert) => cert.documentTypeCode === "101",
        //     );
        //     newData.gstNo = gstCert ? gstCert.documentNumber : "";

        //     newData.billingAddress = [
        //       erfHospital.address,
        //       erfHospital.address2,
        //       erfHospital.city,
        //     ]
        //       .filter(Boolean)
        //       .join(", ");
        //     newData.billingState = erfHospital.state || "";
        //     newData.billingPinCode = erfHospital.cityPostalCode || "";
        //   }
        // }
        if (textField === "selectedHospitalId") {
          if (!name || name === "" || name === "Select") return newData; // guard clause

          const erfHospital = currentErfHospitalList.find(
            (h) => h.name === name,
          );
          if (erfHospital) {
            setSelectedHospital(erfHospital);

            // ── All keys use "hospital" prefix — no collision with patient fields ──
            newData.hospitalName =
              erfHospital.hospitalName || erfHospital.organisationName || "";
            newData.hospitalAssociateNumber = erfHospital.AssociateNumber || "";
            newData.hospitalMobileNr = erfHospital.mobileNr || "";
            newData.hospitalEmail = erfHospital.email || "";
            newData.hospitalAddress = erfHospital.address || "";
            newData.hospitalAddress2 = erfHospital.address2 || "";
            newData.hospitalCity = erfHospital.city || "";

            const gstCert = erfHospital.certifications?.find(
              (cert) => cert.documentTypeCode === "101",
            );
            newData.hospitalGstNo = gstCert ? gstCert.documentNumber : "";

            newData.hospitalBillingAddress = [
              erfHospital.address,
              erfHospital.address2,
              erfHospital.city,
            ]
              .filter(Boolean)
              .join(", ");
            newData.hospitalState = erfHospital.state || "";
            newData.hospitalPinCode = erfHospital.cityPostalCode || "";

            // ── Cost Center = Hospital Name when erfForType is Hospital ──
            newData.costCenter =
              erfHospital.hospitalName || erfHospital.organisationName || "";
          }
        }

        if (textField === "selectedNurseId") {
          const erfNurse = currentErfNurseList.find(
            (nurse) => nurse.name === name,
          );
          if (erfNurse) {
            setSelectedNurse(erfNurse);

            newData.nurseUserId = erfNurse.userId || "";
            newData.nurseName =
              erfNurse.employeeName || erfNurse.organisationName || "";
            newData.nurseAssociateNumber = erfNurse.AssociateNumber || "";
            newData.nurseMobileNr = erfNurse.mobileNr || "";
            newData.nurseEmail = erfNurse.email || "";
            newData.nurseAddress = erfNurse.address || "";
            newData.nurseAddress2 = erfNurse.address2 || "";
            newData.nurseCity = erfNurse.city || "";
            newData.serviceProviderName = erfNurse.serviceProviderName || "";
            newData.serviceProviderCode = erfNurse.serviceProviderCode || "";

            const panCert = erfNurse.certifications?.find(
              (cert) => cert.documentTypeCode === "100",
            );
            newData.nursePanNo = panCert ? panCert.documentNumber : "";
            newData.nurseBillingAddress = [
              erfNurse.address,
              erfNurse.address2,
              erfNurse.city,
            ]
              .filter(Boolean)
              .join(", ");
            newData.nurseBillingState = erfNurse.state || "";
            newData.nurseBillingPinCode = erfNurse.cityPostalCode || "";
          }
        }

        return newData;
      });
    }
    // eslint-disable-next-line
  }, [getDropdownData]);

  useEffect(() => {
    if (!globalRentConfig.fromDate) return;
    if (disabled) {
      return;
    }

    const applyConfig = (item) => {
      const updatedItem = {
        ...item,
        rentSlab: globalRentConfig.rentSlab,
        rentSlabCode: globalRentConfig.rentSlabCode,
        rentSlabDescription: globalRentConfig.rentSlabDescription,
        fromDate: globalRentConfig.fromDate,
        toDate: globalRentConfig.toDate,
        days: globalRentConfig.days,
      };
      const calculatedTotals = calculateRentTotals(updatedItem);
      return { ...updatedItem, ...calculatedTotals };
    };

    setRentItems((prev) => prev.map(applyConfig));
    setNursingItems((prev) => prev.map(applyConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalRentConfig]);

  // Update Dynamic Balances
  useEffect(() => {
    const receivedAmount = parseFloat(formInputs.receivedAmount) || 0;
    const cashAmount = parseFloat(formInputs.cashAmount) || 0;
    const balanceAmtBreakup = (receivedAmount - cashAmount).toFixed(2);

    const receivedSecurityAmt = parseFloat(formInputs.receivedSecurityAmt) || 0;
    const securityCashAmt = parseFloat(formInputs.securityCashAmt) || 0;
    const balanceSecurityBreakup = (
      receivedSecurityAmt - securityCashAmt
    ).toFixed(2);

    const securityModeCash = parseFloat(formInputs.securityModeCash) || 0;
    const securityModeBal = (
      parseFloat(balanceSecurityAmt) - securityModeCash
    ).toFixed(2);

    setFormInputs((prev) => ({
      ...prev,
      balanceAmtBreakup,
      balanceSecurityBreakup,
      securityModeBal,
    }));
  }, [
    formInputs.receivedAmount,
    formInputs.cashAmount,
    formInputs.receivedSecurityAmt,
    formInputs.securityCashAmt,
    balanceSecurityAmt,
    formInputs.securityModeCash,
  ]);

  // Auto-fill Cash Amt for Security Mode
  useEffect(() => {
    if (!formInputs.securityMode || formInputs.securityMode === "Select")
      return;

    const balSec = parseFloat(balanceSecurityAmt) || 0;

    if (["Cash", "Euro", "Dollar"].includes(formInputs.securityMode)) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: balSec.toString(),
        securityModeBal: "0",
      }));
    } else if (
      ["Card", "Cheque", "Online", "Paytm", "UPI", "Card&Cheque"].includes(
        formInputs.securityMode,
      )
    ) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: "0",
        securityModeBal: balSec.toString(),
      }));
    } else if (["Card&Cash", "Cheque&Cash"].includes(formInputs.securityMode)) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: "",
        securityModeBal: balSec.toString(),
      }));
    }
  }, [formInputs.securityMode, balanceSecurityAmt]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const toggleCardVisibility = () => setCardVisible(!isCardVisible);
  const toggleCardVisibility2 = () => setCardVisible2(!isCardVisible2);
  const toggleCardVisibility3 = () => setCardVisible3(!isCardVisible3);
  const toggleVisibility = (key) =>
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  // Edit/View Handlers
  // // eslint-disable-next-line
  // const handleEdit = () => {
  //   setEdit(false);
  //   setDisabled(false);
  // };

  // // eslint-disable-next-line
  // const handleCancelEdit = () => {
  //   setEdit(true);
  //   setDisabled(true);
  //   loadInitialPageData(); // Revert changes
  // };

  // Direct edit on live ERF — preserves status on save
  // eslint-disable-next-line
  const handleEdit = () => {
    setEditMode("DIRECT");
    setEdit(false);
    setDisabled(false);
  };

  // Edit-and-resubmit a Cancelled ERF — flips status back to Approved on save
  const handleResubmitCancelled = () => {
    setEditMode("RESUBMIT");
    setEdit(false);
    setDisabled(false);
  };

  // eslint-disable-next-line
  const handleCancelEdit = () => {
    setEditMode(null);
    setEdit(true);
    setDisabled(true);
    loadInitialPageData(); // Revert changes
  };

  const handleEditForStore = () => {
    setEditForStore(false);
    setStoreDisabled(false);
  };

  const handleCancelEditForStore = () => {
    setEditForStore(true);
    setStoreDisabled(true);
    setIsReadyToDispatch(false);
    setDispatchRemarks("");
    setValidation((prev) => ({ ...prev, dispatchRemarks: "" }));
  };

  const handleInputChange = ({ target: { name, value } }) => {
    const errors = {};

    const dateFields = ["expectedDeliveryDate", "orderDate"];
    if (dateFields.includes(name) && value) {
      const today = new Date().toISOString().split("T")[0];
      if (value < today) {
        toast.error("Cannot select a past date");
        setValidation((prev) => ({
          ...prev,
          [name]: "Date cannot be in the past",
        }));
        return;
      } else {
        setValidation((prev) => ({ ...prev, [name]: "" }));
      }
    }

    const numericFields = [
      "cartage",
      "installationCharges",
      "receivedAmount",
      "cashAmount",
      "receivedSecurityAmt",
      "securityCashAmt",
      "securityModeCash",
      "age",
      "weight",
    ];

    if (numericFields.includes(name) && parseFloat(value) < 0) {
      toast.error(`${name} cannot be negative`);
      return;
    }

    setFormInputs((prev) => ({ ...prev, [name]: value }));

    if (name === "mobileNr") {
      !validateContactNumber(value)
        ? (errors.mobileNr = message.VALID_CONTACT_NUMBER)
        : (errors.mobileNr = "");
    }
    setValidation((prev) => ({ ...prev, [name]: errors[name] || "" }));
  };

  const handleApproverSelection = useCallback(
    (index, selectedData) => {
      if (
        !selectedData ||
        !selectedData.name ||
        selectedData.name === "Select"
      ) {
        return;
      }

      // Check if user is trying to CLEAR an existing approver
      const hasExistingApprover =
        formInputs.approvers[index]?.userId ||
        (formInputs.approverList || [])[index]?.userId;

      const approvalIsRequired =
        !edit && // enforce in edit mode
        (priceViolations.length > 0 ||
          APPROVAL_REQUIRED_CASES.includes(
            formInputs.privilegeCase?.toUpperCase(),
          ));

      // Only block REMOVAL (when new selection would clear existing) — not initial selection
      if (
        hasExistingApprover &&
        approvalIsRequired &&
        (selectedData.userId === "" || selectedData.userId === "CLEAR")
      ) {
        toast.error("Cannot remove approver while approval is required");
        return;
      }

      // Update approvers
      setFormInputs((prev) => {
        const newApprovers = [...prev.approvers];
        newApprovers[index] = {
          userId: selectedData.userId || "M000",
          category: selectedData.category || "SALES",
          name: selectedData.name,
        };

        const hasChanged =
          JSON.stringify(prev.approvers) !== JSON.stringify(newApprovers);
        if (!hasChanged) return prev;

        return { ...prev, approvers: newApprovers };
      });

      // Acknowledge violations
      if (priceViolations.length > 0) {
        // const violatedItemIds = new Set(priceViolations.map((v) => v.itemId));
        // setAcknowledgedViolations(violatedItemIds);
        setShowApprovalRequired(false);
        toast.success(
          `Approver selected. ${priceViolations.length} price violation(s) acknowledged.`,
          { duration: 4000 },
        );
      }
    },
    //eslint-disable-next-line
    [
      edit,
      priceViolations,
      formInputs.privilegeCase,
      formInputs.approvers,
      formInputs.approverList,
    ],
  );

  const handleERFSelection = (erfData) => {
    setSelectedERF(erfData);
    setIsERFLoaded(true);

    // Auto-fill all patient details
    setFormInputs((prev) => ({
      ...prev,

      // Patient Details
      patientName: erfData.patientName || "",
      mobileNr: erfData.mobileNr || "",
      age: erfData.age || "",
      weight: erfData.weight || "",
      gender: erfData.gender || "",
      gstNo: erfData.gstNo || "",
      panNo: erfData.panNo || "",
      isDiabetic: erfData.isDiabetic || "No",
      aadharNo: erfData.aadharNo || "",
      patientChallenges: erfData.patientChallenges || "",

      // Caregiver Details
      caregiverName: erfData.caregiverName || "",
      caregiverRelation: erfData.caregiverRelation || "",
      idProof: erfData.idProof || "Select",
      idProofNumber: erfData.idProofNumber || "",
      emailId: erfData.emailId || "",

      // Billing Address
      billingAddress: erfData.billingAddress || "",
      billingState: erfData.billingState || "Select",
      billingPinCode: erfData.billingPinCode || "",

      // Delivery Address
      deliveryAddress: erfData.deliveryAddress || "",
      deliveryState: erfData.deliveryState || "Select",
      deliveryPinCode: erfData.deliveryPinCode || "",

      // Doctor Details (optional)
      doctorName: erfData.doctorName || "",
      drType: erfData.drType || "Select",
      drCode: erfData.drCode || "",
      // hospital: erfData.hospital || "",
      patientErfHospitalName:
        erfData.patientErfHospitalName || erfData.hospital || "",
    }));

    // Clear validation errors
    setValidation({});

    // Show success message
    toast.success(`Patient details loaded from ERF ${erfData.documentNumber}`, {
      duration: 3000,
      icon: "",
    });
  };

  const handleTravelSave = async (newRows) => {
    try {
      const updated = [...travelHistory, ...newRows];
      await ItemService.updateEquipmentRentForm({
        documentNumber,
        travelHistory: updated,
      });
      setTravelHistory(updated);
      toast.success("Travel entries saved successfully");
    } catch (err) {
      toast.error("Failed to save travel entries");
      throw err; // re-throw so child keeps newRows
    }
  };

  const handleTravelConfirmRow = async (rowId, stage, { remarks }) => {
    try {
      const idx = travelHistory.findIndex((r) => r._id === rowId);
      if (idx === -1) return;

      const stageKey = {
        se: "seConfirmation",
        dispatch: "dispatchConfirmation",
        finance: "financeConfirmation",
      }[stage];

      const updatedRow = {
        ...travelHistory[idx],
        [stageKey]: {
          confirmed: true,
          confirmedBy: currentUserName,
          confirmedAt: new Date().toISOString(),
          remarks,
        },
      };

      const updated = [
        ...travelHistory.slice(0, idx),
        updatedRow,
        ...travelHistory.slice(idx + 1),
      ];

      await ItemService.updateEquipmentRentForm({
        documentNumber,
        travelHistory: updated,
      });

      setTravelHistory(updated);

      const stageMessages = {
        se: "Travel entry submitted",
        dispatch: "Distance approved by Dispatch",
        finance: "Marked as paid by Finance",
      };
      toast.success(stageMessages[stage]);
    } catch {
      toast.error("Failed to confirm travel entry");
    }
  };

  const fetchBarcodesForMaterial = useCallback(
    async (materialCode) => {
      if (barcodeOptionsMap[materialCode]) return;

      try {
        const payload = {
          queryName: "Get_Barcodes_By_MaterialCode",
          materialCode: materialCode,
        };

        const response = await ItemService.generateQuery(payload);

        if (response?.status === 200 && Array.isArray(response.data)) {
          setBarcodeOptionsMap((prev) => ({
            ...prev,
            [materialCode]: response.data.map((item, idx) => ({
              id: idx + 1,
              name: item.barcode || item.id || item.name,
              ...item,
            })),
          }));
        } else {
          toast.success(`No barcodes available for ${materialCode}`);
        }
      } catch (err) {
        console.error("Error fetching barcodes:", err);
        toast.error("Failed to load barcodes");
      }
    },
    [barcodeOptionsMap],
  );

  // const handleBarcodeAssignment = useCallback(
  //   async (row) => {
  //     if (!row?.barcode) {
  //       toast.error("Please select a barcode first");
  //       return;
  //     }

  //     const { local_id, barcode, equipmentItemNr, itemType } = row;

  //     const sourceItems = itemType === "Rent" ? rentItems : saleItems;
  //     const item = sourceItems.find((i) => i.local_id === local_id);

  //     if (!item) {
  //       toast.error("Item not found");
  //       return;
  //     }

  //     setAssigningBarcode((prev) => ({
  //       ...prev,
  //       [local_id]: true,
  //     }));

  //     try {
  //       const payload = {
  //         equipmentRentNr: documentNumber,
  //         erfTypeCode: formInputs.erfTypeCode,
  //         materialCode: item.materialCode,
  //         barcode: barcode,
  //         equipmentItemNr: equipmentItemNr,
  //         assignBarcode: true,
  //       };

  //       if (formInputs?.erfTypeCode !== "SV") {
  //         // 1️⃣ Assign barcode
  //         const response =
  //           await ItemService.assignBarcodeForEquipmentRentForm(payload);

  //         if (response?.status !== 200) {
  //           toast.error(response?.message || "Failed to assign barcode");
  //           return;
  //         }
  //       }

  //       toast.success(`Barcode ${barcode} assigned successfully!`);

  //       // 2️⃣ Determine which key to update
  //       const key =
  //         formInputs?.erfTypeCode === "SV"
  //           ? "serviceItems"
  //           : itemType === "Rent"
  //             ? "rentItems"
  //             : "saleItems";

  //       // 3️⃣ Update local state
  //       const updatedFormInputs = {
  //         ...formInputs,
  //         [key]: (formInputs[key] ?? []).map((i) =>
  //           i.local_id === local_id ? { ...i, ...payload } : i,
  //         ),
  //       };

  //       const { _id, ...formWithoutId } = updatedFormInputs;

  //       // 4️⃣ Update state
  //       setFormInputs(updatedFormInputs);

  //       // 5️⃣ Send updated full form to backend
  //       await ItemService.updateEquipmentRentForm(formWithoutId);
  //       await loadInitialPageData();

  //       if (formInputs?.erfTypeCode !== "SV") {
  //         // 6️⃣ Update material barcode status
  //         await ItemService.updateMaterialBarcodeItem({
  //           barcode: barcode,
  //           materialStatus: "StockOut",
  //           barCodeAssignedDateTime: timestamp,
  //           barCode: "Assigned",
  //         });
  //       }
  //     } catch (err) {
  //       console.error("Error assigning barcode:", err);
  //       toast.error(err?.response?.data?.message || "Failed to assign barcode");
  //     } finally {
  //       setAssigningBarcode((prev) => ({
  //         ...prev,
  //         [local_id]: false,
  //       }));
  //     }
  //   },
  //   // eslint-disable-next-line
  //   [documentNumber, formInputs, rentItems, saleItems],
  // );

  const handleBarcodeAssignment = useCallback(
    async (row) => {
      if (!row?.barcode) {
        toast.error("Please select a barcode first");
        return;
      }

      // Guard — ref check is race-condition-proof
      if (isAssigningBarcodeRef.current) {
        toast.warning("Please wait for the current assignment to complete");
        return;
      }

      const { local_id, barcode, equipmentItemNr, itemType } = row;

      let sourceItems, setSourceItems, key;
      if (formInputs?.erfTypeCode === "SV") {
        // Service ERF — service items live in saleItems state, DB field is serviceItems
        sourceItems = saleItems;
        setSourceItems = setSaleItems;
        key = "serviceItems";
      } else if (itemType === "Nursing") {
        sourceItems = nursingItems;
        setSourceItems = setNursingItems;
        key = "nursingItems";
      } else if (itemType === "Rent") {
        sourceItems = rentItems;
        setSourceItems = setRentItems;
        key = "rentItems";
      } else {
        sourceItems = saleItems;
        setSourceItems = setSaleItems;
        key = "saleItems";
      }

      const item = sourceItems.find((i) => i.local_id === local_id);
      if (!item) {
        toast.error("Item not found");
        return;
      }

      isAssigningBarcodeRef.current = true;
      setIsAssigningBarcode(true);
      setAssigningBarcode((prev) => ({ ...prev, [local_id]: true }));

      try {
        //  For SLRE: narrow erfTypeCode so backend touches ONLY the array
        // matching itemType. Prevents cross-contamination when same materialCode
        // exists in both saleItems and rentItems with the same equipmentItemNr.
        const backendErfTypeCode =
          formInputs.erfTypeCode === "SLRE"
            ? itemType === "Rent"
              ? "RE"
              : "SL"
            : formInputs.erfTypeCode;

        const payload = {
          equipmentRentNr: documentNumber,
          erfTypeCode: backendErfTypeCode,
          materialCode: item.materialCode,
          barcode: barcode,
          equipmentItemNr: equipmentItemNr,
          assignBarcode: true,
        };

        // 1️⃣ Surgical backend write — skipped for SV (no-op there)
        if (formInputs?.erfTypeCode !== "SV") {
          const response =
            await ItemService.assignBarcodeForEquipmentRentForm(payload);
          if (response?.status !== 200) {
            toast.error(response?.message || "Failed to assign barcode");
            return;
          }
        }

        toast.success(`Barcode ${barcode} assigned successfully!`);

        // 2️⃣ Build updated items from SOURCE state (canonical truth)
        //    NEVER from formInputs[key] — that can be stale
        const updatedItems = sourceItems.map((i) =>
          i.local_id === local_id ? { ...i, ...payload } : i,
        );

        // 3️⃣ Sync both source state AND formInputs
        setSourceItems(updatedItems);
        setFormInputs((prev) => ({ ...prev, [key]: updatedItems }));

        // 4️⃣  Minimal payload — backend $set only touches the matching key
        //    Mirrors the accessories pattern. Other arrays untouched in DB.
        await ItemService.updateEquipmentRentForm({
          documentNumber: formInputs.documentNumber,
          equipmentRentNr: documentNumber,
          [key]: updatedItems,
        });

        await loadInitialPageData();

        // 5️⃣ Mark barcode as assigned in MaterialBarcode master
        if (formInputs?.erfTypeCode !== "SV") {
          await ItemService.updateMaterialBarcodeItem({
            barcode: barcode,
            materialStatus: "StockOut",
            barCodeAssignedDateTime: timestamp,
            barCode: "Assigned",
          });
        }
      } catch (err) {
        console.error("Error assigning barcode:", err);
        toast.error(err?.response?.data?.message || "Failed to assign barcode");
      } finally {
        isAssigningBarcodeRef.current = false;
        setIsAssigningBarcode(false);
        setAssigningBarcode((prev) => ({ ...prev, [local_id]: false }));
      }
    },
    // eslint-disable-next-line
    [
      documentNumber,
      formInputs.erfTypeCode,
      formInputs.documentNumber,
      rentItems,
      saleItems,
      nursingItems,
      loadInitialPageData,
      timestamp,
    ],
  );

  const handleBarcodeRemoval = useCallback(
    async (lineItemId, barcode, equipmentItemNr) => {
      if (!barcode) {
        toast.error("No barcode to remove");
        return;
      }

      const allItems = [...rentItems, ...saleItems];
      const item = allItems.find((i) => i.local_id === lineItemId);

      if (!item) {
        toast.error("Item not found");
        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to remove barcode "${barcode}" from this item?`,
      );

      if (!confirmed) return;

      setAssigningBarcode((prev) => ({ ...prev, [lineItemId]: true }));

      try {
        const payload = {
          equipmentRentNr: documentNumber,
          erfTypeCode: formInputs.erfTypeCode,
          materialCode: item.materialCode,
          barcode: barcode,
          equipmentItemNr: equipmentItemNr,
        };

        const response =
          await ItemService.removeBarcodeForEquipmentRentForm(payload);

        if (response?.status === 200) {
          toast.success(`Barcode ${barcode} removed successfully!`);
          await loadInitialPageData();
        } else {
          toast.error(response?.message || "Failed to remove barcode");
        }
      } catch (err) {
        console.error("Error removing barcode:", err);
        toast.error(err?.response?.data?.message || "Failed to remove barcode");
      } finally {
        setAssigningBarcode((prev) => ({ ...prev, [lineItemId]: false }));
      }
    },
    [
      documentNumber,
      formInputs.erfTypeCode,
      rentItems,
      saleItems,
      loadInitialPageData,
    ],
  );

  const hideFailureModal = () => setShowFailureModal(false);

  // eslint-disable-next-line
  const handleSlabSelection = (data) => {
    setRentItems((prevRows) =>
      prevRows.map((row) => {
        if (row.local_id !== data.index) return row;

        let updatedItem = {
          ...row,
          rentSlab: data.value,
          rentSlabCode: data.codeValue,
          rentSlabDescription: data.descValue,
        };

        const selectedSlab = data.value;

        switch (selectedSlab) {
          case "Per Day":
            updatedItem.days = updatedItem.days || 0;
            break;
          case "7 Days":
            updatedItem.days = 7;
            updatedItem.toDate = calculateEndDate(updatedItem.fromDate, 7);
            break;
          case "15 Days":
            updatedItem.days = 15;
            updatedItem.toDate = calculateEndDate(updatedItem.fromDate, 15);
            break;
          case "30 Days":
            updatedItem.days = 30;
            updatedItem.toDate = calculateEndDate(updatedItem.fromDate, 30);
            break;
          default:
            break;
        }

        const calculatedTotals = calculateRentTotals(updatedItem);

        return { ...updatedItem, ...calculatedTotals };
      }),
    );
  };

  const handleGetSelectedMaterial = (selectedData) => {
    if (!Array.isArray(selectedData) || selectedData.length === 0) return;

    const processItems = (items) =>
      items
        .map((item, index) => {
          const qty = 1;
          const taxPercentage = parseFloat(item.taxCode) || 0;

          // ── RENT ─────────────────────────────────────────────────────────
          // if (activeItemType === "Rent") {
          //   const RENT_TAX_RATE = 18;
          //   const days = Math.max(globalRentConfig.days, 1);
          //   const rentPerDay = parseFloat(item.rentPerDayPrice) || 0; // net rate/day

          //   const netVal = parseFloat((qty * rentPerDay * days).toFixed(2));
          //   const taxAmt = parseFloat(
          //     (netVal * (RENT_TAX_RATE / 100)).toFixed(2),
          //   );
          //   const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
          if (activeItemType === "Rent") {
            const RENT_TAX_RATE = 18;
            const days = Math.max(globalRentConfig.days, 1);
            const rentSlab = globalRentConfig.rentSlab;

            // Determine net slab amount per unit based on selected slab
            let masterNetRateForSlab = 0;
            switch (rentSlab) {
              case "Per Day":
                masterNetRateForSlab =
                  (parseFloat(item.rentPerDayPrice) || 0) * days;
                break;
              case "7 Days":
                masterNetRateForSlab = parseFloat(item.rent7DaysPrice) || 0;
                break;
              case "15 Days":
                masterNetRateForSlab = parseFloat(item.rent15DaysPrice) || 0;
                break;
              case "30 Days":
                masterNetRateForSlab = parseFloat(item.rent30DaysPrice) || 0;
                break;
              default:
                masterNetRateForSlab =
                  (parseFloat(item.rentPerDayPrice) || 0) * days;
            }

            // Net rate/day for display
            const ratePerDay =
              rentSlab === "Per Day"
                ? parseFloat(item.rentPerDayPrice) || 0
                : days > 0
                  ? masterNetRateForSlab / days
                  : 0;

            const netVal = parseFloat((qty * masterNetRateForSlab).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (RENT_TAX_RATE / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item.materialCode,
              materialCategory: item.materialCategory || item.category || "",
              hsnCode: item.hsnCode || item.hsn || "",
              materialDescription: item.materialDescription,
              godown: item?.godown || "",
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,

              // barcode: item.barcode || "",
              barcode: "",
              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Rent",
              orderedQuantity: qty,
              // sellingPrice: parseFloat(rentPerDay.toFixed(4)), // net rate/day
              // rentPerDay: rentPerDay,
              sellingPrice: parseFloat(ratePerDay.toFixed(4)),
              rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
              rent7Days: parseFloat(item.rent7DaysPrice) || 0,
              rent15Days: parseFloat(item.rent15DaysPrice) || 0,
              rent30Days: parseFloat(item.rent30DaysPrice) || 0,
              rentSlab: globalRentConfig.rentSlab,
              rentSlabCode: globalRentConfig.rentSlabCode,
              rentSlabDescription: globalRentConfig.rentSlabDescription,
              fromDate: globalRentConfig.fromDate,
              toDate: globalRentConfig.toDate,
              days: globalRentConfig.days,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: RENT_TAX_RATE,
              taxAmount: taxAmt,
              actualSecurityAmt: parseFloat(item.securityAmt) || 0,
              securityAmt: parseFloat(item.securityAmt) || 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
            };
          }

          // ── NURSING ──────────────────────────────────────────────────────
          if (activeItemType === "Nursing") {
            const days = Math.max(globalRentConfig.days, 1);
            const rentSlab = globalRentConfig.rentSlab;

            let netPerUnit = 0;
            switch (rentSlab) {
              case "Per Day":
                netPerUnit = (parseFloat(item.rentPerDayPrice) || 0) * days;
                break;
              case "7 Days":
                netPerUnit = parseFloat(item.rent7DaysPrice) || 0;
                break;
              case "15 Days":
                netPerUnit = parseFloat(item.rent15DaysPrice) || 0;
                break;
              case "30 Days":
                netPerUnit = parseFloat(item.rent30DaysPrice) || 0;
                break;
              default:
                netPerUnit = (parseFloat(item.rentPerDayPrice) || 0) * days;
            }

            const netVal = parseFloat((qty * netPerUnit).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (taxPercentage / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            const ratePerDay =
              rentSlab === "Per Day"
                ? parseFloat(item.rentPerDayPrice) || 0
                : days > 0
                  ? netPerUnit / days
                  : 0;

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item.materialCode,
              materialCategory: item.materialCategory || item.category || "",
              hsnCode: item.hsnCode || item.hsn || "",
              materialDescription: item.materialDescription,
              godown: item?.godown || "",
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,

              // barcode: item.barcode || "",
              barcode: "",
              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Nursing",
              orderedQuantity: qty,
              sellingPrice: parseFloat(ratePerDay.toFixed(4)), // net rate/day
              rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
              rent7Days: parseFloat(item.rent7DaysPrice) || 0,
              rent15Days: parseFloat(item.rent15DaysPrice) || 0,
              rent30Days: parseFloat(item.rent30DaysPrice) || 0,
              rentSlab: globalRentConfig.rentSlab,
              rentSlabCode: globalRentConfig.rentSlabCode,
              rentSlabDescription: globalRentConfig.rentSlabDescription,
              fromDate: globalRentConfig.fromDate,
              toDate: globalRentConfig.toDate,
              days: globalRentConfig.days,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: taxPercentage,
              taxAmount: taxAmt,
              actualSecurityAmt: 0,
              securityAmt: 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
            };
          }

          // ── SALE ─────────────────────────────────────────────────────────
          if (activeItemType === "Sale") {
            const netUnitPrice = parseFloat(item.salePrice) || 0; // net
            const netVal = parseFloat((qty * netUnitPrice).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (taxPercentage / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item.materialCode,
              materialCategory: item.materialCategory || item.category || "",
              hsnCode: item.hsnCode || item.hsn || "",
              materialDescription: item.materialDescription,
              godown: item?.godown || "",
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,

              // barcode: item.barcode || "",
              barcode: "",
              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Sale",
              orderedQuantity: qty,
              sellingPrice: netUnitPrice, // net unit price
              rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
              rent7Days: parseFloat(item.rent7DaysPrice) || 0,
              rent15Days: parseFloat(item.rent15DaysPrice) || 0,
              rent30Days: parseFloat(item.rent30DaysPrice) || 0,
              fromDate: getTodaysData(),
              toDate: "",
              days: 0,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: taxPercentage,
              taxAmount: taxAmt,
              actualSecurityAmt: 0,
              securityAmt: 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
            };
          }

          // ── SERVICE ──────────────────────────────────────────────────────
          if (activeItemType === "Service") {
            const netUnitPrice = parseFloat(item.salePrice) || 0; // net
            const netVal = parseFloat((qty * netUnitPrice).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (taxPercentage / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item.materialCode,
              materialCategory: item.materialCategory || item.category || "",
              hsnCode: item.hsnCode || item.hsn || "",
              materialDescription: item.materialDescription,
              godown: item?.godown || "",
              // barcode: item.barcode || "",
              barcode: "",
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,

              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Service",
              orderedQuantity: qty,
              sellingPrice: netUnitPrice, // net unit price
              rentPerDay: 0,
              rent7Days: 0,
              rent15Days: 0,
              rent30Days: 0,
              fromDate: getTodaysData(),
              toDate: "",
              days: 0,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: taxPercentage,
              taxAmount: taxAmt,
              actualSecurityAmt: 0,
              securityAmt: 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
            };
          }

          return null;
        })
        .filter(Boolean);

    // if (activeItemType === "Rent") {
    //   setRentItems((prev) => [...prev, ...processItems(selectedData)]);
    // } else if (activeItemType === "Nursing") {
    //   setNursingItems((prev) => [...prev, ...processItems(selectedData)]);
    // } else {
    //   setSaleItems((prev) => [...prev, ...processItems(selectedData)]);
    // }

    const newItems = processItems(selectedData);

    if (activeItemType === "Rent") {
      setRentItems((prev) => assignEquipmentItemNrs([...prev, ...newItems]));
    } else if (activeItemType === "Nursing") {
      setNursingItems((prev) => assignEquipmentItemNrs([...prev, ...newItems]));
    } else {
      setSaleItems((prev) => assignEquipmentItemNrs([...prev, ...newItems]));
    }

    setModalShow(false);
  };

  // const removeItemRow = (local_id, type) => {
  //   // Prevent deleting items with assigned barcodes
  //   const allItems = [...rentItems, ...saleItems, ...nursingItems];
  //   const item = allItems.find((i) => i.local_id === local_id);
  //   if (item?.assignBarcode) {
  //     toast.error(
  //       "Cannot delete item with assigned barcode. Remove barcode first.",
  //     );
  //     return;
  //   }
  //   if (type === "Rent") {
  //     setRentItems((prev) => prev.filter((item) => item.local_id !== local_id));
  //   } else if (type === "Nursing") {
  //     setNursingItems((prev) =>
  //       prev.filter((item) => item.local_id !== local_id),
  //     );
  //   } else {
  //     setSaleItems((prev) => prev.filter((item) => item.local_id !== local_id));
  //   }
  // };

  const removeItemRow = (local_id, type) => {
    // Prevent deleting items with assigned barcodes
    const allItems = [...rentItems, ...saleItems, ...nursingItems];
    const item = allItems.find((i) => i.local_id === local_id);
    if (item?.assignBarcode) {
      toast.error(
        "Cannot delete item with assigned barcode. Remove barcode first.",
      );
      return;
    }

    // Prevent deleting a parent that still has linked accessories
    const linkedAccessories = accessoryItems.filter(
      (a) => a.parentLocalId === local_id,
    );
    if (linkedAccessories.length > 0) {
      const hasAssignedBarcodeAccessory = linkedAccessories.some(
        (a) => a.assignBarcode,
      );
      if (hasAssignedBarcodeAccessory) {
        toast.error(
          `Cannot delete "${item?.materialCode}". ${linkedAccessories.length} accessory item(s) are linked, and at least one has an assigned barcode. Remove the accessory barcode first, then delete the accessories, then delete this item.`,
        );
      } else {
        toast.error(
          `Cannot delete "${item?.materialCode}". ${linkedAccessories.length} accessory item(s) are still linked. Please remove the accessories first.`,
        );
      }
      return;
    }

    if (type === "Rent") {
      setRentItems((prev) => prev.filter((item) => item.local_id !== local_id));
    } else if (type === "Nursing") {
      setNursingItems((prev) =>
        prev.filter((item) => item.local_id !== local_id),
      );
    } else {
      setSaleItems((prev) => prev.filter((item) => item.local_id !== local_id));
    }
  };

  const runViolationCheck = useCallback((updatedItem, itemType, local_id) => {
    const violations = detectPriceViolation(updatedItem, itemType);
    setPriceViolations((prevViolations) => {
      const filtered = prevViolations.filter((v) => v.itemId !== local_id);
      if (violations.length > 0) {
        // Only show banner if no approver is already selected
        setFormInputs((prev) => {
          const hasApprover =
            prev.approvers[0]?.userId || (prev.approverList || [])[0]?.userId;
          if (!hasApprover) {
            setShowApprovalRequired(true);
          }
          return prev; // don't modify formInputs
        });
        return [...filtered, ...violations];
      }
      if (filtered.length === 0) {
        setShowApprovalRequired(false);
      }
      return filtered;
    });
  }, []);

  const handleLineItemChange = useCallback(
    (local_id, e, type) => {
      const { name, value } = e.target;

      const updateLogic = (prev) =>
        prev.map((item) => {
          if (item.local_id !== local_id) return item;

          let updatedItem = { ...item, [name]: value };

          // Barcode edit — no recalc
          if (name === "barcode") return updatedItem;

          // ═════════════════════════════════════════════════════════════════
          // RENT
          // ═════════════════════════════════════════════════════════════════
          if (item.itemType === "Rent") {
            if (name === "grossValue") {
              // REVERSE: user typed Total (gross)
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const days = parseFloat(updatedItem.days) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const ratePerDay =
                days * qty > 0
                  ? parseFloat((netVal / (days * qty)).toFixed(4))
                  : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = ratePerDay; // net rate/day
              runViolationCheck(updatedItem, "Rent", local_id);
            } else if (name === "taxPercentage") {
              // Keep NET fixed, recompute gross
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              // FORWARD: sellingPrice is net rate/day
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const days = parseFloat(updatedItem.days) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0; // net
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = parseFloat((price * days * qty).toFixed(2));
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Rent", local_id);
              }
            }
          }

          // ═════════════════════════════════════════════════════════════════
          // NURSING
          // ═════════════════════════════════════════════════════════════════
          else if (item.itemType === "Nursing") {
            if (name === "rentSlab") {
              // Slab changed — recalculate from master
              const calculated = calculateRentTotals(updatedItem);
              updatedItem = { ...updatedItem, ...calculated };
              runViolationCheck(updatedItem, "Nursing", local_id);
            } else if (name === "grossValue") {
              // REVERSE
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const days = parseFloat(updatedItem.days) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const ratePerDay =
                days * qty > 0
                  ? parseFloat((netVal / (days * qty)).toFixed(4))
                  : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = ratePerDay;
              runViolationCheck(updatedItem, "Nursing", local_id);
            } else if (name === "taxPercentage") {
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              // FORWARD (slab-aware)
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const days = parseFloat(updatedItem.days) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              let netVal = 0;
              switch (updatedItem.rentSlab) {
                case "Per Day":
                  netVal = price * days * qty;
                  break;
                case "7 Days":
                  netVal = (parseFloat(updatedItem.rent7Days) || 0) * qty;
                  break;
                case "15 Days":
                  netVal = (parseFloat(updatedItem.rent15Days) || 0) * qty;
                  break;
                case "30 Days":
                  netVal = (parseFloat(updatedItem.rent30Days) || 0) * qty;
                  break;
                default:
                  netVal = price * days * qty;
              }

              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Nursing", local_id);
              }
            }
          }

          // ═════════════════════════════════════════════════════════════════
          // SALE
          // ═════════════════════════════════════════════════════════════════
          else if (item.itemType === "Sale") {
            if (name === "netValue") {
              // user typed a new Net
              const netVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Sale", local_id);
            } else if (name === "grossValue") {
              // REVERSE: user typed Total
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Sale", local_id);
            } else if (name === "taxPercentage") {
              // Keep NET fixed, recompute gross
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              // FORWARD: sellingPrice is NET unit price
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat((qty * price).toFixed(2));
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Sale", local_id);
              }
            }
          }

          // ═════════════════════════════════════════════════════════════════
          // SERVICE  (same math as Sale)
          // ═════════════════════════════════════════════════════════════════
          else if (item.itemType === "Service") {
            if (name === "netValue") {
              const netVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Service", local_id);
            } else if (name === "grossValue") {
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Service", local_id);
            } else if (name === "taxPercentage") {
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat((qty * price).toFixed(2));
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Service", local_id);
              }
            }
          }

          return updatedItem;
        });

      if (type === "Rent") {
        setRentItems(updateLogic);
      } else if (type === "Nursing") {
        setNursingItems(updateLogic);
      } else {
        setSaleItems(updateLogic);
      }
    },
    [runViolationCheck],
  );

  // const addProductDetails = (type) => {
  //   setActiveItemType(type || formInputs.erfType);
  //   setModalShow(true);
  // };
  const addProductDetails = (type) => {
    setActiveItemType(type || formInputs.erfType);
    setActiveItemTypeCode(formInputs.erfTypeCode);
    setModalShow(true);
  };
  const hideModal = () => setModalShow(false);

  // Fetch All Hospitals
  const fetchHospitalList = async () => {
    setIsLoadingHospitals(true);
    try {
      const payload = {
        queryName: "Get_All_Hospital_Names",
        module: "",
        page: 1,
        per_page: 3000, // Get all hospitals
        timeZone: "Asia/Calcutta",
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        setHospitalList(response.data);
      } else {
        setHospitalList([]);
      }
    } catch (err) {
      console.error("Error fetching hospitals:", err);
      toast.error("Failed to load hospital list");
      setHospitalList([]);
    } finally {
      setIsLoadingHospitals(false);
    }
  };

  // Fetch All Doctors (for "Doctor" type)
  const fetchAllDoctors = async () => {
    setIsLoadingDoctors(true);
    try {
      const payload = {
        queryName: "Get_All_Doctors",
        module: "",
        page: 1,
        per_page: 3000, // Get all doctors
        timeZone: "Asia/Calcutta",
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format for dropdown
        const formattedDoctors = response.data.map((doc) => ({
          id: doc.userId,
          name: doc.doctorName,
          code: doc.userId,
          userId: doc.userId,
          doctorName: doc.doctorName,
          hospitalName: doc.hospitalName || doc.doctorHospitalName,
          description: `${doc.doctorName} - ${doc.hospitalName || doc.doctorHospitalName}`,
          ...doc, // Keep all original data
        }));

        setDoctorsList(formattedDoctors);
      } else {
        setDoctorsList([]);
      }
    } catch (err) {
      console.error("Error fetching doctors:", err);
      toast.error("Failed to load doctors list");
      setDoctorsList([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Fetch Doctors by Hospital
  const fetchDoctorsByHospital = async (hospitalName) => {
    setIsLoadingDoctors(true);
    try {
      const payload = {
        queryName: "Get_All_Doctors",
        module: "",
        page: 1,
        per_page: 3000,
        timeZone: "Asia/Calcutta",
        rowFilter: {
          $and: [
            {
              hospitalName: hospitalName,
            },
          ],
        },
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format for dropdown
        const formattedDoctors = response.data.map((doc) => ({
          id: doc.userId,
          name: doc.doctorName,
          code: doc.userId,
          userId: doc.userId,
          doctorName: doc.doctorName,
          hospitalName: doc.hospitalName || doc.doctorHospitalName,
          description: `${doc.doctorName} (${doc.userId})`,
          ...doc, // Keep all original data for auto-fill
        }));

        setDoctorsList(formattedDoctors);
      } else {
        setDoctorsList([]);
        toast.success(`No doctors found for ${hospitalName}`);
      }
    } catch (err) {
      console.error("Error fetching doctors by hospital:", err);
      toast.error("Failed to load doctors for selected hospital");
      setDoctorsList([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyList = async () => {
      setIsLoadingCompany(true);
      try {
        const payload = {
          queryName: "Get_CompanyMaster_List",
          module: "",
          page: 1,
          per_page: 200,
          timeZone: "Asia/Calcutta",
        };

        const response = await ItemService.generateCustomerQuery(payload);

        if (
          isMounted &&
          response?.status === 200 &&
          Array.isArray(response.data)
        ) {
          const formatted = response.data.map((company) => ({
            id: company.companyNr || company.documentNumber,
            name: company.name || company.organisationName,
            companyNr: company.companyNr,
            companyCode: company.companyCode,
            companyName: company.companyCodeDesc || company.organisationName,
            organisationName: company.organisationName,
          }));
          setCompanyList(formatted);
        }
      } catch (err) {
        console.error("Error fetching company list:", err);
        if (isMounted) toast.error("Failed to load company list");
      } finally {
        if (isMounted) setIsLoadingCompany(false);
      }
    };

    fetchCompanyList();
    return () => {
      isMounted = false;
    };
  }, []);

  // Call this on component mount if needed
  useEffect(() => {
    let isMounted = true;

    const loadHospitals = async () => {
      if (erfForType === "Hospital") {
        setErfIsLoadingHospitals(true);

        try {
          const payload = {
            queryName: "getSupplierList",
            module: "",
            page: 1,
            per_page: 1000,
            timeZone: "Asia/Calcutta",
            rowFilter: {
              $and: [
                {
                  "vendorCategory.vendorCategoryCode": "V04",
                },
              ],
            },
          };

          const response = await ItemService.generateQuery(payload);

          if (
            isMounted &&
            response?.status === 200 &&
            Array.isArray(response.data)
          ) {
            const formattedHospitals = response.data.map((hospital) => ({
              id: hospital.userId || hospital._id,
              name: hospital.hospitalName || hospital.organisationName,
              userId: hospital.userId,
              hospitalName: hospital.hospitalName,
              organisationName: hospital.organisationName,
              AssociateNumber: hospital.AssociateNumber,
              mobileNr: hospital.mobileNr,
              email: hospital.email,
              address: hospital.address,
              address2: hospital.address2,
              city: hospital.city,
              state: hospital.state,
              cityPostalCode: hospital.cityPostalCode,
              certifications: hospital.certifications || [], // Needed for GST auto-fill
            }));

            setErfHospitalList(formattedHospitals);
          } else if (isMounted) {
            setErfHospitalList([]);
          }
        } catch (err) {
          console.error("Error fetching ERF hospitals:", err);
          if (isMounted) {
            toast.error("Failed to load ERF hospital list");
            setErfHospitalList([]);
          }
        } finally {
          if (isMounted) {
            setErfIsLoadingHospitals(false);
          }
        }
      } else {
        if (isMounted) {
          setErfHospitalList([]);
        }
      }
    };

    loadHospitals();

    // Cleanup: prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [erfForType]);

  useEffect(() => {
    let isMounted = true;

    const loadNurse = async () => {
      if (formInputs.erfType === "Nursing Service") {
        setIsLoadingErfNurse(true);
        try {
          const payload = {
            queryName: "getSupplierList",
            module: "",
            page: 1,
            per_page: 1000,
            timeZone: "Asia/Calcutta",
            rowFilter: {
              $and: [{ "vendorCategory.vendorCategoryCode": "V13" }],
            },
          };
          const response = await ItemService.generateQuery(payload);
          if (
            isMounted &&
            response?.status === 200 &&
            Array.isArray(response.data)
          ) {
            setErfNurseList(
              response.data.map((nurse) => ({
                id: nurse.userId || nurse._id,
                name: nurse.employeeName || nurse.organisationName,
                userId: nurse.userId,
                employeeName: nurse.employeeName,
                organisationName: nurse.organisationName,
                AssociateNumber: nurse.AssociateNumber,
                mobileNr: nurse.mobileNr,
                email: nurse.email,
                address: nurse.address,
                address2: nurse.address2,
                city: nurse.city,
                state: nurse.state,
                cityPostalCode: nurse.cityPostalCode,

                serviceProviderCode: nurse.serviceProviderCode,
                serviceProviderName: nurse.serviceProviderName,
                certifications: nurse.certifications || [],
              })),
            );
          }
        } catch (err) {
          console.error("Error fetching nurses:", err);
        } finally {
          if (isMounted) setIsLoadingErfNurse(false);
        }
      } else {
        if (isMounted) setErfNurseList([]);
      }
    };

    loadNurse();
    return () => {
      isMounted = false;
    };
  }, [formInputs.erfType]);

  // ADD after fetchDoctorsByHospital function
  const fetchPatientList = async () => {
    setIsLoadingPatient(true);
    try {
      const payload = {
        queryName: "getSupplierList",
        module: "",
        page: 1,
        per_page: 1000,
        timeZone: "Asia/Calcutta",
        rowFilter: {
          $and: [
            {
              "vendorCategory.vendorCategoryCode": "V12", // Patient category code
            },
          ],
        },
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        const formattedPatients = response.data.map((patient) => ({
          id: patient.userId,
          name: patient.employeeName, // Key from backend
          userId: patient.userId,
          hospitalName: patient.hospitalName,
          organisationName: patient.organisationName,
          AssociateNumber: patient.AssociateNumber,
          mobileNr: patient.mobileNr,
          email: patient.email,
          address: patient.address,
          address2: patient.address2,
          city: patient.city,
          state: patient.state,
          cityPostalCode: patient.cityPostalCode,
          age: patient.age,
          weight: patient.weight,
          aadharNo: patient.aadharNo,
          panNo: patient.panNo,
          patientChallenges: patient.patientChallenges,
          caregiverName: patient.caregiverName,
          emailId: patient.emailId,
          caregiverRelation: patient.caregiverRelation,
          idProof: patient.idProof,
          idProofNumber: patient.idProofNumber,
        }));

        setPatientList(formattedPatients);
      } else {
        setPatientList([]);
      }
    } catch (err) {
      console.error("Error fetching patients:", err);
      toast.error("Failed to load patient list");
      setPatientList([]);
    } finally {
      setIsLoadingPatient(false);
    }
  };

  const incrementVersion = (version) => {
    // remove 'V' and convert to number
    const num = parseInt(version.replace("V", ""), 10);

    // increment and return with V prefix
    return `V${num + 1}`;
  };

  const handleUpdateForStore = async () => {
    const validationErrors = {};
    let hasError = false;

    // Rule 1: Checkbox must be checked before updating
    if (!erfDispatchIndicator) {
      toast.error('Please check "Ready To Dispatch" before updating.');
      return;
    }

    // Rule 2: Dispatch remarks are mandatory when checkbox is checked
    if (!validateRequired(dispatchRemarks)) {
      validationErrors.dispatchRemarks = message.FILL_REQUIRED_FIELD;
      hasError = true;
    }

    setValidation((prev) => ({ ...prev, ...validationErrors }));

    if (hasError) {
      const fieldLabelMap = {
        dispatchRemarks: "Dispatch Remarks",
      };
      setErrorFields(
        Object.keys(validationErrors).map((key) => fieldLabelMap[key] || key),
      );
      setShowFailureModal(true);
      return;
    }

    setLoading(true);
    try {
      const processedRentItems = rentItems.map((item) =>
        convertToNumbers(item),
      );
      const processedSaleItems = saleItems.map((item) =>
        convertToNumbers(item),
      );

      const processedNursingItems = nursingItems.map((item) =>
        convertToNumbers(item),
      );

      const safeApproverList = formInputs.approverList || [];

      // Build payload EXPLICITLY — no ...formInputs spread
      const payloadData = {
        // Document identity
        documentNumber: formInputs.documentNumber,
        documentType: formInputs.documentType,

        // ERF Type
        erfType: formInputs.erfType,
        erfTypeCode: formInputs.erfTypeCode,
        erfTypeDescription: formInputs.erfTypeDescription,

        // Order Details
        orderDate: formInputs.orderDate,
        salePoint: formInputs.salePoint,
        costCenter: formInputs.costCenter,
        erfCostCenterSubGroup: formInputs.erfCostCenterSubGroup || "SALESTEAM",
        privilegeCase: formInputs.privilegeCase,
        billingName: formInputs.billingName,
        orderType: formInputs.orderType,
        sourceOfCall: formInputs.sourceOfCall,

        companyName: formInputs.companyName,
        companyOrganisation: formInputs.companyOrganisation,
        companyNr: formInputs.companyNr,
        companyCode: formInputs.companyCode,

        // Doctor Details
        doctorName: formInputs.doctorName,
        drType: formInputs.drType,
        drCode: formInputs.drCode,
        drListedType: formInputs.drListedType || "",
        patientErfHospitalName: formInputs.patientErfHospitalName,

        // Patient Details
        patientName: formInputs.patientName,
        mobileNr: formInputs.mobileNr,
        age: formInputs.age ? parseFloat(formInputs.age) : undefined,
        weight: formInputs.weight ? parseFloat(formInputs.weight) : undefined,
        gender: formInputs.gender,
        gstNo: formInputs.gstNo,
        panNo: formInputs.panNo,
        isDiabetic: formInputs.isDiabetic,
        aadharNo: formInputs.aadharNo,
        patientChallenges: formInputs.patientChallenges,
        patientType: formInputs.patientType,
        patient: formInputs.patient,
        AssociateNumber: formInputs.AssociateNumber,

        // Hospital (B2B)
        hospitalName: formInputs.hospitalName || "",
        hospitalAssociateNumber: formInputs.hospitalAssociateNumber || "",
        hospitalMobileNr: formInputs.hospitalMobileNr || "",
        hospitalEmail: formInputs.hospitalEmail || "",
        hospitalGstNo: formInputs.hospitalGstNo || "",
        hospitalAddress: formInputs.hospitalAddress || "",
        hospitalAddress2: formInputs.hospitalAddress2 || "",
        hospitalCity: formInputs.hospitalCity || "",
        hospitalBillingAddress: formInputs.hospitalBillingAddress || "",
        hospitalState: formInputs.hospitalState || "",
        hospitalPinCode: formInputs.hospitalPinCode || "",

        // Caregiver Details
        caregiverName: formInputs.caregiverName,
        caregiverRelation: formInputs.caregiverRelation,
        idProof: formInputs.idProof,
        idProofNumber: formInputs.idProofNumber,
        emailId: formInputs.emailId,

        // Address
        billingAddress: formInputs.billingAddress,
        billingState: formInputs.billingState,
        billingPinCode: formInputs.billingPinCode,
        deliveryAddress: formInputs.deliveryAddress,
        deliveryState: formInputs.deliveryState,
        deliveryPinCode: formInputs.deliveryPinCode,

        // Payment Details
        cartage: parseFloat(formInputs.cartage) || 0,
        installationCharges: parseFloat(formInputs.installationCharges) || 0,
        receivedAmount: parseFloat(formInputs.receivedAmount) || 0,
        paymentMode: formInputs.paymentMode,
        cashAmount: parseFloat(formInputs.cashAmount) || 0,
        balanceAmtBreakup: formInputs.balanceAmtBreakup,
        chequeNo: formInputs.chequeNo,
        cardTransNo: formInputs.cardTransNo,
        bankName: formInputs.bankName,
        receiptNo: formInputs.receiptNo,

        // Security Details
        standardSecurityAmt: formInputs.standardSecurityAmt,
        receivedSecurityAmt: parseFloat(formInputs.receivedSecurityAmt) || 0,
        securityPincode: formInputs.securityPincode,
        securityAmtMode: formInputs.securityAmtMode,
        securityCashAmt: parseFloat(formInputs.securityCashAmt) || 0,
        balanceSecurityBreakup: formInputs.balanceSecurityBreakup,
        securityChequeNo: formInputs.securityChequeNo,
        securityCardTransNo: formInputs.securityCardTransNo,
        securityBankName: formInputs.securityBankName,
        securityMode: formInputs.securityMode,
        securityModeCash: parseFloat(formInputs.securityModeCash) || 0,
        securityModeBal: formInputs.securityModeBal,
        remarks: formInputs.remarks,

        // ERF Dates
        erfStartDate: formInputs.erfStartDate,
        erfEndDate: formInputs.erfEndDate,
        erfReminderDate: calculateReminderDate(
          formInputs.erfEndDate || globalRentConfig.toDate,
          globalRentConfig.days,
        ),
        erfReminderDaysBeforeEnd: REMINDER_DAYS_BEFORE_END,

        // Approval — preserve existing, don't override
        levelOfApproval: "1",
        approvalLevel: formInputs.approvalLevel,
        approvalReason: formInputs.approvalReason,
        triggerWorkflow: formInputs.triggerWorkflow,
        approverList: safeApproverList.slice(0, 1),
        approvers:
          formInputs.approvers.length > 0
            ? formInputs.approvers.slice(0, 1)
            : safeApproverList.slice(0, 1),
        approverUserId1:
          formInputs.approverUserId1 || safeApproverList[0]?.userId || "",
        approverUsername1:
          formInputs.approverUsername1 || safeApproverList[0]?.name || "",
        approverUserId2: "",
        approverUsername2: "",

        // ERF For Type
        erfForType: formInputs.erfForType || erfForType,
        erfForTypeName:
          formInputs.erfForTypeName ||
          (erfForType === "Patient" ? "Patient" : "Hospital"),
        erfForTypeCode:
          formInputs.erfForTypeCode ||
          (erfForType === "Patient" ? "PAT" : "HSP"),

        // Computed Amounts
        netAmount: parseFloat(netAmount),
        balanceAmount: parseFloat(balanceAmount),
        balanceSecurityAmt: parseFloat(balanceSecurityAmt),

        // Version & Remarks
        // version: incrementVersion(formInputs?.version || "V0"),
        updateRemarks: formInputs.updateRemarks || [],

        // --Store Dispatch Fields
        erfBarcodeAssignedDateAndTime: erfDispatchIndicator
          ? new Date().toISOString()
          : formInputs?.erfBarcodeAssignedDateAndTime || "",

        erfStatus: erfDispatchIndicator
          ? "Ready To Dispatch"
          : formInputs.erfStatus,
        erfStatusCode: "RTD",
        erfDispatchIndicator: erfDispatchIndicator,
        dispatchRemarks: erfDispatchIndicator ? dispatchRemarks : "",

        // Metadata
        accessoryItems: accessoryItems,
      };

      // Items
      if (formInputs.erfType === "Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = processedSaleItems;
        payloadData.nursingItems = [];

        // --Store Dispatch Fields
        // payloadData.erfStatus = "";
        // payloadData.erfStatusCode = "";
      } else if (formInputs.erfType === "Nursing Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = [];
        payloadData.nursingItems = processedNursingItems;
        // --Store Dispatch Fields
        payloadData.erfStatus = "";
        payloadData.erfStatusCode = "";
      } else {
        payloadData.rentItems = processedRentItems;
        payloadData.saleItems = processedSaleItems;
        payloadData.serviceItems = [];
        payloadData.nursingItems = [];
      }

      await ItemService.updateEquipmentRentForm(payloadData);

      toast.success("ERF Updated Successfully");
      setIsReadyToDispatch(false);
      setDispatchRemarks("");
      setEditForStore(true);
      setStoreDisabled(true);
      navigate(Constants.CUSTOMER_V5_MR_ERF_LIST);
    } catch (err) {
      toast.error("Failed to update ERF");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (newRemarks) => {
    const validationErrors = {};
    let hasError = false;

    // ===== 1. Basic Fields Validation =====
    if (!validateRequired(formInputs.patientName)) {
      validationErrors.patientName = message.FILL_REQUIRED_FIELD;
      hasError = true;
    }
    if (!validateRequired(formInputs.mobileNr)) {
      validationErrors.mobileNr = message.FILL_REQUIRED_FIELD;
      hasError = true;
    }
    // if (!validateRequired(formInputs.aadharNo)) {
    //   validationErrors.aadharNo = message.FILL_REQUIRED_FIELD;
    //   hasError = true;
    // }
    if (formInputs.idProof && formInputs.idProof !== "Select") {
      if (!validateRequired(formInputs.idProofNumber)) {
        validationErrors.idProofNumber = `Please enter ${formInputs.idProof} number`;
        hasError = true;
      }
    }

    // ===== NEW: Price Violation Approval Check =====
    if (priceViolations.length > 0) {
      const primaryApprover =
        formInputs.approvers[0] || (formInputs.approverList || [])[0];

      if (!primaryApprover || !primaryApprover.userId) {
        toast.error(
          "Price violations detected. Please select an approver or correct the prices.",
        );
        setShowApprovalRequired(true);
        return;
      }
    }

    // ===== 3. Specific Item Logic Validation =====
    switch (formInputs.erfType) {
      case "Sale":
        if (saleItems.length === 0) {
          toast.error("Please add at least one Sale product.");
          return;
        }
        break;
      case "Rent":
        if (rentItems.length === 0) {
          toast.error("Please add at least one Rent product.");
          return;
        }
        break;
      case "Sale & Rent":
        if (rentItems.length === 0) {
          toast.error("Please add at least one Rent product.");
          return;
        }
        if (saleItems.length === 0) {
          toast.error("Please add at least one Sale product.");
          return;
        }
        break;
      case "Service":
        if (saleItems.length === 0) {
          toast.error("Please add at least one Service item.");
          return;
        }
        break;
      case "Nursing Service":
        // No product validation needed
        break;
      default:
        toast.error("Please select a valid ERF Type.");
        return;
    }

    // ===== 4. Qty & Price Validation =====
    const finalItems = [...rentItems, ...saleItems];
    const invalidQty = finalItems.filter(
      (item) => (parseFloat(item.orderedQuantity) || 0) < 1,
    );

    if (invalidQty.length > 0) {
      toast.error("Quantity must be at least 1 for all products");
      return;
    }

    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;

    if (!privilegeSettings.allowSellBelowLRP) {
      const invalidSaleItems = saleItems.filter(
        (item) =>
          parseFloat(item.sellingPrice) < parseFloat(item.masterSellingPrice),
      );
      if (invalidSaleItems.length > 0) {
        toast.error(
          "Sale/Service Unit Price cannot be less than Master Selling Price for this Privilege Case",
        );
        return;
      }
    }

    setValidation((prev) => ({ ...prev, ...validationErrors }));

    if (hasError) {
      setErrorFields(Object.keys(validationErrors));
      setShowFailureModal(true);
      return;
    }

    setLoading(true);
    try {
      const triggerWorkflow =
        APPROVAL_REQUIRED_CASES.includes(
          formInputs.privilegeCase?.toUpperCase(),
        ) || priceViolations.length > 0;

      // ===== 5. Convert items to numbers =====
      const processedRentItems = rentItems.map((item) =>
        convertToNumbers(item),
      );
      const processedSaleItems = saleItems.map((item) =>
        convertToNumbers(item),
      );

      const processedNursingItems = nursingItems.map((item) =>
        convertToNumbers(item),
      );

      const safeApproverList = formInputs.approverList || [];

      // ===== 6. Construct Payload =====
      const payloadData = {
        ...formInputs,
        erfTypeCode: formInputs.erfTypeCode,
        erfTypeDescription: formInputs.erfTypeDescription,
        costCenter: formInputs.costCenter,
        erfCostCenterSubGroup: formInputs.erfCostCenterSubGroup || "SALESTEAM",

        companyName: formInputs.companyName,
        companyOrganisation: formInputs.companyOrganisation,
        companyNr: formInputs.companyNr,
        companyCode: formInputs.companyCode,

        // Doctor Details
        doctorName: formInputs.doctorName,
        drType: formInputs.drType,
        drCode: formInputs.drCode,
        drListedType: formInputs.drListedType || "",
        patientErfHospitalName: formInputs.patientErfHospitalName,

        ...((formInputs.erfType === "Rent" ||
          formInputs.erfType === "Sale & Rent" ||
          formInputs.erfType === "Nursing Service") && {
          erfStartDate: globalRentConfig.fromDate,
          erfEndDate: globalRentConfig.toDate,
          // erfReminderDate: (() => {
          //   if (!globalRentConfig.toDate) return "";
          //   const end = new Date(globalRentConfig.toDate);
          //   end.setDate(end.getDate() - 3);
          //   return end.toISOString().split("T")[0];
          // })(),
          erfReminderDate: calculateReminderDate(
            globalRentConfig.toDate,
            globalRentConfig.days,
          ),
          erfReminderDaysBeforeEnd: REMINDER_DAYS_BEFORE_END,
        }),

        levelOfApproval: "1", // Force to 1
        // approverList:
        //   formInputs.approvers.length > 0
        //     ? formInputs.approvers.slice(0, 1)
        //     : formInputs.approverList.slice(0, 1),

        // triggerWorkflow: triggerWorkflow,
        // approverUserId1: triggerWorkflow
        //   ? formInputs.approvers[0]?.userId ||
        //     formInputs.approverList[0]?.userId
        //   : "",
        // approverUsername1: triggerWorkflow
        //   ? formInputs.approvers[0]?.name || formInputs.approverList[0]?.name
        //   : "",

        approverList:
          formInputs.approvers.length > 0
            ? formInputs.approvers.slice(0, 1)
            : safeApproverList.slice(0, 1),

        approverUserId1: triggerWorkflow
          ? formInputs.approvers[0]?.userId || safeApproverList[0]?.userId || ""
          : "",
        approverUsername1: triggerWorkflow
          ? formInputs.approvers[0]?.name || safeApproverList[0]?.name || ""
          : "",

        // Explicitly clear level 2 to maintain data integrity
        approverUserId2: "",
        approverUsername2: "",

        approvalReason:
          priceViolations.length > 0
            ? "Price Violation"
            : formInputs.privilegeCase,

        // Convert all calculated amounts to numbers
        netAmount: parseFloat(netAmount),
        balanceAmount: parseFloat(balanceAmount),
        balanceSecurityAmt: parseFloat(balanceSecurityAmt),

        erfForType: formInputs.erfForType || erfForType,
        erfForTypeName: erfForType === "Patient" ? "Patient" : "Hospital",
        erfForTypeCode: erfForType === "Patient" ? "PAT" : "HSP",

        // Convert form input amounts to numbers
        cartage: parseFloat(formInputs.cartage) || 0,
        installationCharges: parseFloat(formInputs.installationCharges) || 0,
        receivedAmount: parseFloat(formInputs.receivedAmount) || 0,
        cashAmount: parseFloat(formInputs.cashAmount) || 0,
        receivedSecurityAmt: parseFloat(formInputs.receivedSecurityAmt) || 0,
        securityCashAmt: parseFloat(formInputs.securityCashAmt) || 0,
        securityModeCash: parseFloat(formInputs.securityModeCash) || 0,
        age: formInputs.age ? parseFloat(formInputs.age) : undefined,
        weight: formInputs.weight ? parseFloat(formInputs.weight) : undefined,
        version: incrementVersion(formInputs?.version),
        updateRemarks: newRemarks,
        accessoryItems: accessoryItems,
        // ── Line Item Totals (Header Level — for Reports) ──────────────
        rentNetTotal: itemTypeTotals.rentNetTotal,
        rentTaxTotal: itemTypeTotals.rentTaxTotal,
        rentGrossTotal: itemTypeTotals.rentGrossTotal,
        rentSecurityTotal: itemTypeTotals.rentSecurityTotal,

        saleNetTotal: itemTypeTotals.saleNetTotal,
        saleTaxTotal: itemTypeTotals.saleTaxTotal,
        saleGrossTotal: itemTypeTotals.saleGrossTotal,

        serviceNetTotal: itemTypeTotals.serviceNetTotal,
        serviceTaxTotal: itemTypeTotals.serviceTaxTotal,
        serviceGrossTotal: itemTypeTotals.serviceGrossTotal,

        nursingNetTotal: itemTypeTotals.nursingNetTotal,
        nursingTaxTotal: itemTypeTotals.nursingTaxTotal,
        nursingGrossTotal: itemTypeTotals.nursingGrossTotal,

        lineItemNetTotal: itemTypeTotals.lineItemNetTotal,
        lineItemTaxTotal: itemTypeTotals.lineItemTaxTotal,
        lineItemGrossTotal: itemTypeTotals.lineItemGrossTotal,
        lineItemGrossRounded: itemTypeTotals.lineItemGrossRounded,
        roundOffAmount: itemTypeTotals.roundOffAmount,
      };

      // if (canEditCancelledERF) {
      //   payloadData.status = "Approved";
      //   payloadData.erfStatus = "Assign To Store";
      //   payloadData.erfStatusCode = "ATS";
      // }

      // Status mutation policy — branches by which edit mode the user entered
      if (editMode === "RESUBMIT") {
        payloadData.status = "Approved";
        payloadData.erfStatus = "Assign To Store";
        payloadData.erfStatusCode = "ATS";
      } else if (editMode === "DIRECT") {
        // ...formInputs already carries existing status / erfStatus / erfStatusCode
      }

      // Delete unnecessary fields from update response
      if (payloadData.data) {
        delete payloadData.data;
      }
      if (payloadData._id) {
        delete payloadData._id;
      }

      if (formInputs.erfType === "Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = processedSaleItems;
        payloadData.nursingItems = [];
      } else if (formInputs.erfType === "Nursing Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = [];
        payloadData.nursingItems = processedNursingItems;
      } else {
        payloadData.rentItems = processedRentItems;
        payloadData.saleItems = processedSaleItems;
        payloadData.serviceItems = [];
        payloadData.nursingItems = [];
      }

      await ItemService.updateEquipmentRentForm(payloadData);

      // toast.success("ERF Updated Successfully");
      // setEdit(true);
      // setDisabled(true);
      // navigate(Constants.CUSTOMER_V5_MR_ERF_LIST);
      toast.success(
        editMode === "RESUBMIT"
          ? "ERF Resubmitted Successfully"
          : "ERF Updated Successfully",
      );
      setEditMode(null);
      setEdit(true);
      setDisabled(true);
      navigate(Constants.CUSTOMER_V5_MR_ERF_LIST);
    } catch (err) {
      toast.error("Failed to update ERF");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // TABLE COLUMNS CONFIG
  // =============================================================================
  const showExtenssionModla = useCallback((data) => {
    setModalShowExtenssionModal(true);
    setExtenssionData(data);
  }, []);

  const getColumns = useCallback(
    (type) => {
      const privilegeSettings =
        PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
        DEFAULT_PRIVILEGE_SETTINGS;

      const isEditable = !disabled && isFormEnabled;

      const isRentAmountEditable =
        type === "Rent" && privilegeSettings.editAmount;
      // eslint-disable-next-line
      const isRentTaxEditable = type === "Rent" && privilegeSettings.editTax;

      const isRentSecurityEditable =
        type === "Rent" && privilegeSettings.editAmount;

      const isSaleAmountEditable =
        (type === "Sale" || type === "Service") && privilegeSettings.editAmount;
      // eslint-disable-next-line
      const isSaleTaxEditable =
        (type === "Sale" || type === "Service") && privilegeSettings.editTax;

      // ── NURSING COLUMNS ───────────────────────────────────────────────
      if (type === "Nursing") {
        return [
          // {
          //   name: "Product Code",
          //   width: "180px",
          //   cell: (row) =>
          //     row.isTotal ? (
          //       <div className="fw-bold text-end w-100">Total</div>
          //     ) : (
          //       <InputField
          //         className="inputBox"
          //         value={row.materialCode}
          //         disabled
          //       />
          //     ),
          // },
          // {
          //   name: "Product Details",
          //   width: "250px",
          //   cell: (row) =>
          //     row.isTotal ? null : (
          //       <TextAreaField
          //         className="inputBox"
          //         value={row.materialDescription}
          //         disabled
          //       />
          //     ),
          // },
          {
            name: "Product Code",
            width: "180px",
            cell: (row) =>
              row.isTotal ? (
                <div className="fw-bold text-end w-100">Total</div>
              ) : (
                <InputField
                  className="inputBox"
                  name="materialCode"
                  value={row.materialCode}
                  disabled={true}
                />
              ),
          },
          {
            name: "Product Details",
            width: "250px",
            cell: (row) =>
              row.isTotal ? null : (
                <TextAreaField
                  className="inputBox"
                  name="materialDescription"
                  value={row.materialDescription}
                  onChange={(e) =>
                    handleLineItemChange(
                      row.local_id,
                      e,
                      type === "Rent" ? "Rent" : "Sale",
                    )
                  }
                  // disabled={!isEditable}
                  disabled={true}
                />
              ),
          },
          {
            name: "Scan Barcode",
            width: "120px",
            cell: (row) =>
              row.isTotal ? null : (
                <>
                  {row?.assignBarcode && row?.assignBarcode === true ? (
                    <>
                      <button
                        className="
    w-10 h-10
    flex items-center justify-center
    rounded-full
    bg-[#7fc5c4]
    transition-all duration-150 ease-in-out
 
    hover:bg-[#5fa9a8]
    hover:scale-105
  "
                        disabled={false}
                      >
                        {/* <i className="bi bi-webcam text-white text-xl"></i> */}
                        <i class="bi bi-camera-video-off text-xl"></i>
                        {/* <i class="bi bi-upc-scan"></i> */}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setSelectedRowId(row.local_id);
                          setSelectProduct(row?.materialCode);
                          setSelectedType(type);
                          setShowBarcodeModal(true);
                        }}
                        className="
    w-10 h-10
    flex items-center justify-center
    rounded-full
    bg-[#7fc5c4]
    transition-all duration-150 ease-in-out
 
    hover:bg-[#5fa9a8]
    hover:scale-105
  "
                        disabled={false}
                      >
                        {/* <i className="bi bi-webcam text-white text-xl"></i> */}
                        <i className="bi bi-upc-scan text-white text-xl"></i>
                      </button>
                    </>
                  )}
                </>
              ),
            // omit: !isStoreUser,
            omit: !isBarcodeAssignmentAllowed,
          },
          {
            name: "Barcode Value",
            width: "200px",
            // omit: !isStoreUser,
            omit: !isBarcodeAssignmentAllowed,
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="text"
                  name="barcode"
                  value={row?.barcode || ""}
                  // disabled={row?.assignBarcode}
                  disabled={true}
                  onChange={(e) => {
                    handleManualBarcodeChange(
                      row.local_id,
                      e.target.value,
                      type,
                    );
                    setSelectProduct(row?.materialCode);
                  }}
                  onBlur={(e) =>
                    handleManualBarcodeSubmit(row, e.target.value, type)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleManualBarcodeSubmit(row, e.target.value, type);
                    }
                  }}
                />
              ),
          },
          {
            name: "Assign",
            width: "160px",
            cell: (row) =>
              row.isTotal ? null : (
                <>
                  {row?.assignBarcode && row?.assignBarcode === true ? (
                    <>
                      <button className="btn btnTable" disabled={true}>
                        {" "}
                        Assigned
                      </button>
                    </>
                  ) : (
                    <>
                      {/* <button
                        className="btn btnTable"
                        onClick={() => handleBarcodeAssignment(row)}
                        // disabled={row?.barcode ? false : true}
                        disabled={!row?.barcodeValidated}
                      >
                        Assign
                      </button> */}
                      <button
                        className="btn btnTable"
                        onClick={() => handleBarcodeAssignment(row)}
                        disabled={!row?.barcodeValidated || isAssigningBarcode}
                      >
                        Assign
                      </button>
                    </>
                  )}
                </>
              ),
            // omit: !isStoreUser,
            omit: !isBarcodeAssignmentAllowed,
          },
          {
            name: "Nursing Slab",
            width: "140px",
            cell: (row) =>
              row.isTotal ? null : (
                <select
                  className="form-select form-select-sm inputBox"
                  value={row.rentSlab || "Per Day"}
                  disabled
                >
                  <option value="Per Day">Per Day</option>
                  <option value="7 Days">7 Days</option>
                  <option value="15 Days">15 Days</option>
                  <option value="30 Days">30 Days</option>
                </select>
              ),
          },
          {
            name: "From",
            width: "150px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="date"
                  value={row.fromDate}
                  disabled
                />
              ),
          },
          {
            name: "To",
            width: "150px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="date"
                  value={row.toDate}
                  disabled
                />
              ),
          },
          {
            name: "Days",
            width: "110px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row.days}
                  disabled
                />
              ),
          },
          {
            name: "Qty",
            width: "100px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row.orderedQuantity}
                  disabled
                />
              ),
          },
          {
            name: "MRP",
            width: "120px",
            omit: type === "Rent" || type === "Nursing",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row.masterMRP || 0}
                  disabled
                />
              ),
          },
          {
            name: "Rate/Day (₹)",
            width: "130px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="number"
                  name="sellingPrice"
                  value={row.sellingPrice}
                  onChange={(e) =>
                    handleLineItemChange(row.local_id, e, "Nursing")
                  }
                  // disabled={true}
                  disabled={!isEditable || !privilegeSettings.editAmount}
                />
              ),
          },
          {
            name: "Net Value",
            width: "140px",
            cell: (row) =>
              row.isTotal ? (
                <div className="fw-bold">{row.netValue}</div>
              ) : (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row.netValue}
                  disabled
                />
              ),
          },
          {
            name: "GST (%)",
            width: "100px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="number"
                  name="taxPercentage"
                  value={row.taxPercentage}
                  onChange={(e) =>
                    handleLineItemChange(row.local_id, e, "Nursing")
                  }
                  // disabled={!isEditable || !privilegeSettings.editTax}
                  disabled
                />
              ),
          },
          {
            name: "Tax Amount",
            width: "120px",
            cell: (row) =>
              row.isTotal ? (
                <div className="fw-bold">{row.taxAmount}</div>
              ) : (
                <InputField
                  className="inputBox"
                  type="number"
                  value={row.taxAmount}
                  disabled
                />
              ),
          },
          {
            name: "Total (₹)",
            width: "130px",
            cell: (row) =>
              row.isTotal ? (
                <div className="fw-bold">{row.grossValue}</div>
              ) : (
                <InputField
                  className="inputBox"
                  type="number"
                  name="grossValue"
                  value={row.grossValue}
                  onChange={(e) =>
                    handleLineItemChange(row.local_id, e, "Nursing")
                  }
                  // disabled
                  disabled={!isEditable || !privilegeSettings.editAmount}
                />
              ),
          },
          {
            name: "CN / Stock Status",
            cell: (row) =>
              row.creditNoteStatus === "CreditNote" ? (
                <span
                  className="badge"
                  style={{ backgroundColor: "#dc3545", color: "#fff" }}
                >
                  Credit Note
                </span>
              ) : row.creditNoteStatus === "StockIn" || row.productReturned ? (
                <span
                  className="badge"
                  style={{ backgroundColor: "#28a745", color: "#fff" }}
                >
                  Returned / Stock In
                </span>
              ) : (
                <span className="text-muted">--</span>
              ),
            width: "150px",
          },
          {
            name: "Action",
            width: "80px",
            cell: (row) =>
              row.isTotal || !isEditable || row.assignBarcode ? null : (
                <button
                  className="btn btn-danger btn-sm mt-1"
                  onClick={() => removeItemRow(row.local_id, "Nursing")}
                >
                  <i className="bi bi-trash3"></i>
                </button>
              ),
          },
        ];
      }

      // const baseCols = [
      //   {
      //     name: "Product Code",
      //     width: "180px",
      //     cell: (row) =>
      //       row.isTotal ? (
      //         <div className="fw-bold text-end w-100">Total</div>
      //       ) : (
      //         <InputField
      //           className="inputBox"
      //           name="materialCode"
      //           value={row.materialCode}
      //           disabled={true}
      //         />
      //       ),
      //   },
      //   {
      //     name: "Product Details",
      //     width: "250px",
      //     cell: (row) =>
      //       row.isTotal ? null : (
      //         <TextAreaField
      //           className="inputBox"
      //           name="materialDescription"
      //           value={row.materialDescription}
      //           disabled={!isEditable}
      //         />
      //       ),
      //   },
      // ];

      const baseCols = [
        {
          name: "Product Code",
          width: "180px",
          cell: (row) =>
            row.isTotal ? (
              <div className="fw-bold text-end w-100">Total</div>
            ) : (
              <InputField
                className="inputBox"
                name="materialCode"
                value={row.materialCode}
                disabled={true}
              />
            ),
        },
        {
          name: "Product Details",
          width: "250px",
          cell: (row) =>
            row.isTotal ? null : (
              <TextAreaField
                className="inputBox"
                name="materialDescription"
                value={row.materialDescription}
                onChange={(e) =>
                  handleLineItemChange(
                    row.local_id,
                    e,
                    type === "Rent" ? "Rent" : "Sale",
                  )
                }
                // disabled={!isEditable}
                disabled={true}
              />
            ),
        },
        {
          name: "Scan Barcode",
          width: "120px",
          cell: (row) =>
            row.isTotal ? null : (
              <>
                {row?.assignBarcode && row?.assignBarcode === true ? (
                  <>
                    <button
                      className="
    w-10 h-10
    flex items-center justify-center
    rounded-full
    bg-[#7fc5c4]
    transition-all duration-150 ease-in-out
 
    hover:bg-[#5fa9a8]
    hover:scale-105
  "
                      disabled={false}
                    >
                      {/* <i className="bi bi-webcam text-white text-xl"></i> */}
                      <i class="bi bi-camera-video-off text-xl"></i>
                      {/* <i class="bi bi-upc-scan"></i> */}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setSelectedRowId(row.local_id);
                        setSelectProduct(row?.materialCode);
                        setSelectedType(type);
                        setShowBarcodeModal(true);
                      }}
                      className="
    w-10 h-10
    flex items-center justify-center
    rounded-full
    bg-[#7fc5c4]
    transition-all duration-150 ease-in-out
 
    hover:bg-[#5fa9a8]
    hover:scale-105
  "
                      disabled={false}
                    >
                      {/* <i className="bi bi-webcam text-white text-xl"></i> */}
                      <i class="bi bi-upc-scan"></i>
                    </button>
                  </>
                )}
              </>
            ),
          // omit: !isStoreUser,
          omit: !isBarcodeAssignmentAllowed,
        },
        {
          name: "Barcode Value",
          width: "200px",
          // omit: !isStoreUser,
          omit: !isBarcodeAssignmentAllowed,
          cell: (row) =>
            row.isTotal ? null : (
              <InputField
                className="inputBox"
                type="text"
                name="barcode"
                value={row?.barcode || ""}
                // disabled={row?.assignBarcode}
                disabled={true}
                onChange={(e) => {
                  handleManualBarcodeChange(row.local_id, e.target.value);
                  setSelectProduct(row?.materialCode);
                }}
                onBlur={(e) => handleManualBarcodeSubmit(row, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleManualBarcodeSubmit(row, e.target.value);
                  }
                }}
              />
            ),
        },

        {
          name: "Assign",
          width: "160px",
          cell: (row) =>
            row.isTotal ? null : (
              <>
                {row?.assignBarcode && row?.assignBarcode === true ? (
                  <>
                    <button className="btn btnTable" disabled={true}>
                      {" "}
                      Assigned
                    </button>
                  </>
                ) : (
                  <>
                    {/* <button
                      className="btn btnTable"
                      onClick={() => handleBarcodeAssignment(row)}
                      // disabled={row?.barcode ? false : true}
                      disabled={!row?.barcodeValidated}
                    >
                      Assign
                    </button> */}
                    <button
                      className="btn btnTable"
                      onClick={() => handleBarcodeAssignment(row)}
                      disabled={!row?.barcodeValidated || isAssigningBarcode}
                    >
                      Assign
                    </button>
                  </>
                )}
              </>
            ),
          // omit: !isStoreUser,
          omit: !isBarcodeAssignmentAllowed,
        },
        {
          name: "Barcode Value",
          width: "200px",
          omit: isBarcodeAssignmentAllowed,
          cell: (row) =>
            row.isTotal ? null : (
              <InputField
                className="inputBox"
                type="text"
                value={row?.barcode || ""}
                disabled={true}
                placeholder="Not Assigned"
              />
            ),
        },
      ];

      let middleCols = [];
      if (type === "Rent") {
        middleCols = [
          {
            name: "Extenssion",
            cell: (row) =>
              row.isTotal ? null : (
                <button
                  className="btn btnTable"
                  onClick={() => showExtenssionModla(row)}
                >
                  Add Extenssion
                </button>
              ),
            sortable: true,
            width: "200px",
            omit: !formInputs?.extension,
          },
          {
            name: "From",
            width: "150px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="date"
                  name="fromDate"
                  value={row.fromDate}
                  // onChange={(e) => handleLineItemChange(row.local_id, e, "Rent")}
                  disabled={true}
                />
              ),
          },
          {
            name: "To",
            width: "150px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="date"
                  name="toDate"
                  value={row.toDate}
                  disabled={true}
                />
              ),
          },
          {
            name: "Days",
            width: "120px",
            cell: (row) =>
              row.isTotal ? null : (
                <InputField
                  className="inputBox"
                  type="number"
                  name="days"
                  value={row.days}
                  // onChange={(e) => handleLineItemChange(row.local_id, e, "Rent")}
                  // disabled={!isEditable || row.rentSlab !== "Per Day"} // CRITICAL
                  disabled={true}
                />
              ),
          },
          // {
          //   name: "Rent/Day",
          //   width: "120px",
          //   cell: (row) =>
          //     row.isTotal ? null : (
          //       <InputField
          //         className="inputBox"
          //         type="number"
          //         name="rentPerDay"
          //         value={row.rentPerDay || 0}
          //         disabled={true}
          //       />
          //     ),
          // },
        ];
      }

      const financialCols = [
        {
          name: "Qty.",
          width: "120px",
          cell: (row) =>
            row.isTotal ? null : (
              <InputField
                className="inputBox"
                type="number"
                name="orderedQuantity"
                value={row.orderedQuantity}
                min="1"
                // onChange={(e) =>
                //   handleLineItemChange(
                //     row.local_id,
                //     e,
                //     type === "Rent" ? "Rent" : "Sale",
                //   )
                // }
                disabled={true}
              />
            ),
        },
        {
          name: "MRP.",
          width: "120px",
          omit: type === "Rent" || type === "Nursing",
          cell: (row) =>
            row.isTotal ? null : (
              <InputField
                className="inputBox"
                type="number"
                name="masterMRP"
                value={row.masterMRP || 0}
                disabled={true}
              />
            ),
        },
        // {
        //   name: "Selling Price",
        //   width: "120px",
        //   cell: (row) =>
        //     row.isTotal ? null : (
        //       <InputField
        //         className="inputBox"
        //         type="number"
        //         name="sellingPrice"
        //         value={row.sellingPrice}
        //         onChange={(e) =>
        //           handleLineItemChange(
        //             row.local_id,
        //             e,
        //             type === "Rent" ? "Rent" : "Sale",
        //           )
        //         }
        //         disabled={type === "Rent" ? !isRentAmountEditable : true}
        //       />
        //     ),
        // },

        // ── SERVICE-SPECIFIC: Show calculated Unit Price ──────────────────
        ...(type === "Service"
          ? [
              {
                name: "Master Price (₹)",
                width: "130px",
                cell: (row) =>
                  row.isTotal ? null : (
                    <InputField
                      className="inputBox"
                      type="number"
                      value={row.masterSellingPrice || 0}
                      disabled
                      title="Reference only"
                    />
                  ),
              },
              {
                name: "Unit Price (₹)",
                width: "130px",
                cell: (row) =>
                  row.isTotal ? null : (
                    <InputField
                      className="inputBox"
                      type="number"
                      value={
                        row.orderedQuantity &&
                        parseFloat(row.orderedQuantity) > 0
                          ? parseFloat(
                              (
                                parseFloat(row.grossValue || 0) /
                                parseFloat(row.orderedQuantity)
                              ).toFixed(2),
                            )
                          : 0
                      }
                      disabled
                      title="Auto-calculated: Total ÷ Qty"
                    />
                  ),
              },
            ]
          : type === "Sale"
            ? [
                {
                  name: "Master Price (₹)",
                  width: "130px",
                  cell: (row) =>
                    row.isTotal ? null : (
                      <InputField
                        className="inputBox"
                        type="number"
                        value={row.masterSellingPrice || 0}
                        disabled
                        title="Reference only"
                      />
                    ),
                },
                {
                  name: "Unit Price (₹)",
                  width: "130px",
                  cell: (row) =>
                    row.isTotal ? null : (
                      <InputField
                        className="inputBox"
                        type="number"
                        value={
                          row.orderedQuantity &&
                          parseFloat(row.orderedQuantity) > 0
                            ? parseFloat(
                                (
                                  parseFloat(row.grossValue || 0) /
                                  parseFloat(row.orderedQuantity)
                                ).toFixed(2),
                              )
                            : 0
                        }
                        disabled
                        title="Auto-calculated: Total ÷ Qty"
                      />
                    ),
                },
              ]
            : [
                {
                  name: "Rate/Day (₹)",
                  width: "130px",
                  cell: (row) =>
                    row.isTotal ? null : (
                      <InputField
                        className="inputBox"
                        type="number"
                        name="sellingPrice"
                        value={row.sellingPrice}
                        onChange={(e) =>
                          handleLineItemChange(row.local_id, e, "Rent")
                        }
                        // disabled={!isRentAmountEditable}
                        // disabled={true}
                        disabled={!isEditable || !isRentAmountEditable}
                      />
                    ),
                },
              ]),

        {
          name: "Net Value",
          width: "140px",
          cell: (row) =>
            row.isTotal ? (
              <div className="fw-bold">{row.netValue}</div>
            ) : (
              <InputField
                className="inputBox"
                type="number"
                name="netValue"
                value={row.netValue}
                disabled={true}
              />
            ),
        },
        {
          name: "GST (%)",
          width: "100px",
          cell: (row) =>
            row.isTotal ? (
              <div className="fw-bold">Total</div>
            ) : (
              <InputField
                className="inputBox"
                type="number"
                name="taxPercentage"
                value={row.taxPercentage}
                onChange={(e) =>
                  handleLineItemChange(
                    row.local_id,
                    e,
                    type === "Rent" ? "Rent" : type,
                  )
                }
                // disabled={
                //   type === "Rent" ? !isRentTaxEditable : !isSaleTaxEditable
                // }
                disabled={true}
              />
            ),
        },
        {
          name: "Tax Amount",
          width: "120px",
          cell: (row) =>
            row.isTotal ? (
              <div className="fw-bold">{row.taxAmount}</div>
            ) : (
              <InputField
                className="inputBox"
                type="number"
                name="taxAmount"
                value={row.taxAmount}
                disabled={true}
              />
            ),
        },
        {
          name: "Total",
          width: "120px",
          cell: (row) =>
            row.isTotal ? (
              <div className="fw-bold">{row.grossValue}</div>
            ) : (
              <InputField
                className="inputBox"
                type="number"
                name="grossValue"
                value={row.grossValue}
                onChange={(e) =>
                  handleLineItemChange(
                    row.local_id,
                    e,
                    type === "Rent" ? "Rent" : type,
                  )
                }
                // disabled={
                //   type === "Rent"
                //     ? !isRentAmountEditable
                //     : !isSaleAmountEditable
                // }
                // disabled={true}
                disabled={
                  !isEditable ||
                  (type === "Rent"
                    ? !isRentAmountEditable
                    : !isSaleAmountEditable)
                }
              />
            ),
        },
      ];

      let endCols = [];
      if (type === "Rent") {
        endCols = [
          {
            name: "Actual Security",
            omit: isHospital,
            width: "120px",
            cell: (row) =>
              row.isTotal ? (
                <div className="fw-bold">{row.actualSecurityAmt}</div>
              ) : (
                <InputField
                  className="inputBox"
                  type="number"
                  name="actualSecurityAmt"
                  value={row.actualSecurityAmt}
                  onChange={(e) =>
                    handleLineItemChange(row.local_id, e, "Rent")
                  }
                  // disabled={!isRentSecurityEditable}
                  disabled={true}
                />
              ),
          },
          {
            name: "Security",
            width: "120px",
            omit: isHospital,
            cell: (row) =>
              row.isTotal ? (
                <div className="fw-bold">{row.securityAmt}</div>
              ) : (
                <InputField
                  className="inputBox"
                  type="number"
                  name="securityAmt"
                  value={row.securityAmt}
                  onChange={(e) =>
                    handleLineItemChange(row.local_id, e, "Rent")
                  }
                  // disabled={!isRentSecurityEditable}
                  // disabled={true}
                  disabled={!isEditable || !isRentSecurityEditable}
                />
              ),
          },
        ];
      }
      const creditNoteColumns = [
        {
          name: "CN / Stock Status",
          cell: (row) =>
            row.creditNoteStatus === "CreditNote" ? (
              <span
                className="badge"
                style={{ backgroundColor: "#dc3545", color: "#fff" }}
              >
                Credit Note
              </span>
            ) : row.creditNoteStatus === "StockIn" || row.productReturned ? (
              <span
                className="badge"
                style={{ backgroundColor: "#28a745", color: "#fff" }}
              >
                Returned / Stock In
              </span>
            ) : (
              <span className="text-muted">--</span>
            ),
          width: "150px",
        },
      ];

      const actionCol = [
        {
          name: "Action",
          width: "80px",
          cell: (row) =>
            row.isTotal || !isEditable || row.assignBarcode ? null : (
              <button
                className="btn btn-danger btn-sm mt-1"
                onClick={() =>
                  removeItemRow(row.local_id, type === "Rent" ? "Rent" : "Sale")
                }
              >
                <i className="bi bi-trash3"></i>
              </button>
            ),
        },
      ];
      const storeActionCols = false
        ? // const storeActionCols = true
          [
            {
              name: "Barcode Management",
              width: "350px",
              cell: (row) =>
                row.isTotal ? null : (
                  <BarcodeSelectorMRCustomerV5
                    lineItemId={row.local_id}
                    materialCode={row.materialCode}
                    currentBarcode={row.barcode}
                    equipmentItemNr={row.equipmentItemNr || ""}
                    onFetchBarcodes={fetchBarcodesForMaterial}
                    barcodeOptions={barcodeOptionsMap[row.materialCode] || []}
                    onAssign={handleBarcodeAssignment}
                    onRemove={handleBarcodeRemoval}
                    disabled={storeDisabled}
                    isAssigning={assigningBarcode[row.local_id] || false}
                  />
                ),
            },
          ]
        : [];

      return [
        ...baseCols,
        ...storeActionCols,
        ...middleCols,
        ...financialCols,
        ...endCols,
        ...creditNoteColumns,
        ...actionCol,
      ];
    },
    // eslint-disable-next-line
    [
      formInputs.privilegeCase,
      disabled,
      isFormEnabled,
      isStoreUser,
      fetchBarcodesForMaterial,
      barcodeOptionsMap,
      handleBarcodeAssignment,
      handleBarcodeRemoval,
      storeDisabled,
      assigningBarcode,
      showExtenssionModla,
      formInputs.extension,
      handleLineItemChange,
    ],
  );

  const CONDITIONAL_ROW_STYLES = [
    {
      when: (row) => !!row.isTotal,
      style: {
        backgroundColor: "#cfe2ff",
        color: "#084298",
        fontWeight: "bold",
      },
    },
  ];
  //handle reamarks
  const [updateModalShow, setUpdateModalShow] = useState(false);

  //update remarsk modal
  // eslint-disable-next-line
  const showApproveModal = async (name) => {
    setUpdateModalShow(true);
  };
  const updateHideModal = () => {
    setUpdateModalShow(false);
  };

  const handleValueChange = (remarks) => {
    const incrementVersion = (version = "V0") => {
      const num = parseInt(version.replace("V", ""), 10) || 0;
      return `V${num + 1}`;
    };

    const newVersion = incrementVersion(formInputs.version);

    const newRemarks = [
      ...(formInputs.extenssion || []),
      {
        remark: remarks,
        version: newVersion,
      },
    ];

    setFormInputs((prev) => ({
      ...prev,
      version: newVersion, //  header updated
      updateRemarks: newRemarks, //  extension updated
    }));

    handleUpdate(newRemarks);
  };

  const [getItemLevelData, setItemLevelData] = useState([]);
  const [getItemLevelColumns, setItemLevelColumns] = useState([]);
  const [getModalTitle, setModalTitle] = useState("");
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);

  const itemLevelHideModal = () => {
    setItemLevelModalShow(false);
  };

  const remarkLineItemColInfo = [
    {
      name: "Remarks",
      cell: (row) => <span>{row?.remark || ""} </span>,
      width: "180px",
    },
    {
      name: "Version",
      cell: (row) => <span>{row?.version || ""} </span>,
      width: "180px",
    },
  ];
  // eslint-disable-next-line
  const remarkItemShowModal = (itemLevelData) => {
    setItemLevelData(itemLevelData);
    setItemLevelColumns(remarkLineItemColInfo);
    setModalTitle("Updated Remarks / Version");
    setItemLevelModalShow(true);
  };
  //handle Chnage box
  // const handleCheckBoxInputChange = (e) => {
  //   const { name, checked } = e.target;

  //   setFormInputs((prev) => ({
  //     ...prev,
  //     [name]: checked,
  //   }));
  // };
  //extended modal
  const [getModalShowExtenssionModal, setModalShowExtenssionModal] =
    useState(false);
  const [getExtenssionData, setExtenssionData] = useState(false);

  // const showExtenssionModla = (data) => {
  //   setModalShowExtenssionModal(true);
  //   setExtenssionData(data);
  // };

  const hideModalExtenssionModal = () => {
    setModalShowExtenssionModal(false);
  };
  //extension model save
  const handleSaveExtensions = (extensionData) => {
    setRentItems((prevItems) =>
      prevItems.map((item) =>
        item.local_id === extensionData.local_id
          ? {
              ...item,
              extensionItemsdetails: extensionData.extensionItemsdetails || [],
            }
          : item,
      ),
    );
  };

  //scan
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectProduct, setSelectProduct] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [selectedAccessoryForScan, setSelectedAccessoryForScan] =
    useState(null);

  //eslint-disable-next-line
  const loadBarCodeData = async (slaetype, barcodes, materialCodes) => {
    let obj = {};
    if (slaetype === "SE") {
      obj = {
        queryName: "getMaterilMasterList",
        rowFilter: {
          $and: [
            {
              materialCode: selectProduct,
              materialGroup: barcodes,
              // materialGroup: "SERVICES",
            },
          ],
        },
      };
    } else {
      obj = {
        queryName: "MaterilBarcodeList",
        page: 1,
        per_page: 10,
        searchTerm: "",
        rowFilter: {
          $and: [
            {
              godownCode: slaetype,
              materialStatus: "StockIn",
              barcode: barcodes,
              materialCode: materialCodes,
            },
          ],
        },
      };
    }

    try {
      const item = await ItemService.generateQuery(obj);

      if (item?.status === 200 && item?.data?.length > 0) {
        return true; //  barcode valid
      } else {
        return false;
      }
    } catch (error) {
      console.log("API Error:", error);
      return false;
    }
  };
  const handleBarcodeSuccess = async (barcodeValue) => {
    // Case 1: Row is selected (user clicked scan icon first)
    if (selectedRowId && selectedType) {
      let isValid = false;

      if (selectedType === "Sale") {
        isValid = await loadBarCodeData("SL", barcodeValue, selectProduct);
      } else if (selectedType === "Rent") {
        isValid = await loadBarCodeData("RE", barcodeValue, selectProduct);
      } else if (selectedType === "Nursing") {
        isValid = await loadBarCodeData("SL", barcodeValue, selectProduct);
      } else if (selectedType === "Service") {
        isValid = await loadBarCodeData("SE", barcodeValue, "Service");
      }

      if (!isValid) {
        toast.error(
          "Invalid or already used barcode! or Mismatch product code",
        );
        return;
      }

      //  If valid → update state
      if (selectedType === "Sale" || selectedType === "Service") {
        const updatedItems = saleItems.map((item) =>
          item.local_id === selectedRowId
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        );
        setSaleItems(updatedItems);
      } else if (selectedType === "Nursing") {
        const updatedItems = nursingItems.map((item) =>
          item.local_id === selectedRowId
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        );
        setNursingItems(updatedItems);
      } else {
        const updatedItems = rentItems.map((item) =>
          item.local_id === selectedRowId
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        );
        setRentItems(updatedItems);
      }

      toast.success(`Barcode ${barcodeValue} scanned!`);
      setShowBarcodeModal(false);
      setSelectedRowId(null);
      return;
    }

    // Case 2: Accessory row scan — user clicked scan icon on an accessory row
    if (selectedAccessoryForScan) {
      const row = selectedAccessoryForScan;
      const godownCode = row.parentGodownCode || "RE";

      const isValid = await loadBarCodeData(
        godownCode,
        barcodeValue,
        row.materialCode,
      );

      if (!isValid) {
        toast.error("Invalid barcode or barcode not found for this accessory.");
        setSelectedAccessoryForScan(null);
        setShowBarcodeModal(false);
        return;
      }

      // Barcode valid → update only this accessory row's barcode
      setAccessoryItems((prev) =>
        prev.map((a) =>
          a.local_id === row.local_id
            ? { ...a, barcode: barcodeValue, barcodeValidated: true }
            : a,
        ),
      );

      toast.success(`Accessory barcode ${barcodeValue} scanned ✓`);
      setSelectedAccessoryForScan(null);
      setShowBarcodeModal(false);
      return;
    }

    // Case 2: No row selected - use the same logic as handleScannedBarcode
    // This allows barcode gun to work when modal is open without clicking scan icon
    const allCurrent = [
      ...rentItems.map((i) => ({ ...i, _listType: "Rent" })),
      ...saleItems.map((i) => ({ ...i, _listType: i.itemType })),
      ...nursingItems.map((i) => ({ ...i, _listType: "Nursing" })),
    ];

    const unassigned = allCurrent.find(
      (item) => !item.assignBarcode && !item.barcode,
    );

    if (!unassigned) {
      // No empty row - iterate through all items and find matching materialCode
      for (const item of allCurrent) {
        if (!item.materialCode) continue;

        const godownCode =
          item._listType === "Sale" || item._listType === "Service"
            ? "SL"
            : "RE";

        const isValid = await loadBarCodeData(
          godownCode,
          barcodeValue,
          item.materialCode,
        );

        if (isValid) {
          const targetRow = allCurrent.find(
            (r) =>
              r.materialCode === item.materialCode &&
              !r.barcode &&
              !r.assignBarcode,
          );

          if (targetRow) {
            if (targetRow._listType === "Rent") {
              setRentItems((prev) =>
                prev.map((it) =>
                  it.local_id === targetRow.local_id
                    ? { ...it, barcode: barcodeValue, barcodeValidated: true }
                    : it,
                ),
              );
            } else if (targetRow._listType === "Nursing") {
              setNursingItems((prev) =>
                prev.map((it) =>
                  it.local_id === targetRow.local_id
                    ? { ...it, barcode: barcodeValue, barcodeValidated: true }
                    : it,
                ),
              );
            } else {
              setSaleItems((prev) =>
                prev.map((it) =>
                  it.local_id === targetRow.local_id
                    ? { ...it, barcode: barcodeValue, barcodeValidated: true }
                    : it,
                ),
              );
            }
            toast.success(
              `✓ ${barcodeValue} validated & assigned to ${item.materialCode}`,
            );
          } else {
            toast.success(
              `✓ ${barcodeValue} is valid for ${item.materialCode}`,
            );
          }
          setShowBarcodeModal(false);
          return;
        }
      }

      toast.error("Invalid barcode - not matching any item in this ERF");
      return;
    }

    // Found empty row - validate and assign
    const godownCode =
      unassigned._listType === "Sale" || unassigned._listType === "Service"
        ? "SL"
        : "RE";

    const isValid = await loadBarCodeData(
      godownCode,
      barcodeValue,
      unassigned.materialCode,
    );

    if (!isValid) {
      toast.error("Invalid or already used barcode!");
      return;
    }

    if (unassigned._listType === "Rent") {
      setRentItems((prev) =>
        prev.map((item) =>
          item.local_id === unassigned.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
    } else if (unassigned._listType === "Nursing") {
      setNursingItems((prev) =>
        prev.map((item) =>
          item.local_id === unassigned.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
    } else {
      setSaleItems((prev) =>
        prev.map((item) =>
          item.local_id === unassigned.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
    }

    toast.success(`Barcode ${barcodeValue} → ${unassigned.materialCode}`);
    setShowBarcodeModal(false);
  };
  //manual barcode upadate
  // const handleManualBarcodeChange = (localId, value) => {
  //   if (selectedType === "Sale") {
  //     setSaleItems((prev) =>
  //       prev.map((item) =>
  //         item.local_id === localId
  //           ? { ...item, barcode: value, barcodeValidated: false }
  //           : item,
  //       ),
  //     );
  //   } else {
  //     setRentItems((prev) =>
  //       prev.map((item) =>
  //         item.local_id === localId
  //           ? { ...item, barcode: value, barcodeValidated: false }
  //           : item,
  //       ),
  //     );
  //   }
  // };
  const handleManualBarcodeChange = (localId, value, itemType) => {
    const resolvedType = itemType || selectedType;
    if (resolvedType === "Sale" || resolvedType === "Service") {
      setSaleItems((prev) =>
        prev.map((item) =>
          item.local_id === localId
            ? { ...item, barcode: value, barcodeValidated: false }
            : item,
        ),
      );
    } else if (resolvedType === "Nursing") {
      setNursingItems((prev) =>
        prev.map((item) =>
          item.local_id === localId
            ? { ...item, barcode: value, barcodeValidated: false }
            : item,
        ),
      );
    } else {
      setRentItems((prev) =>
        prev.map((item) =>
          item.local_id === localId
            ? { ...item, barcode: value, barcodeValidated: false }
            : item,
        ),
      );
    }
  };

  const handleAddAccessories = useCallback(
    (selectedMaterials, parentItem) => {
      const timestamp = Date.now();
      const isParentSaleItem = saleItems.some(
        (s) => s.local_id === parentItem.local_id,
      );
      const parentGodownCode = isParentSaleItem ? "SL" : "RE";

      const newItems = selectedMaterials.map((item, idx) => ({
        local_id: `acc-${timestamp}-${idx}`,
        parentLocalId: parentItem.local_id,
        parentMaterialCode: parentItem.materialCode,
        parentDescription: parentItem.materialDescription,
        parentGodownCode: parentGodownCode,

        materialCode: item.materialCode || "",
        materialCategory: item.materialCategory || "",
        materialDescription: item.materialDescription || "",
        materialGroup: item.materialGroup || "",
        materialSubCategory: item.materialSubCategory || "",
        hsnCode: item.hsnCode || "",
        brand: item.brand || "",
        godown: item.godown || "",
        masterTaxPercent: parseFloat(item.taxCode) || 0,
        masterMRP: parseFloat(item.mrPrice) || 0,
        masterSellingPrice: parseFloat(item.salePrice) || 0,
        masterUnitPrice: parseFloat(item.unitPrice) || 0,
        orderUnitOfMeasure: item.unit || "EA",
        storageLocn: "Main Store",
        currencyCode: "INR",

        barcode: null,
        barcodeValidated: false,
        assignBarcode: false,
        savedToBackend: false,
        equipmentItemNr: "",

        // itemType: isParentSaleItem ? "Sale" : "Rent",
        itemType: isParentSaleItem ? parentItem.itemType || "Sale" : "Rent",
        orderedQuantity: 1,
        isAccessory: true,
        productReturned: false,

        sellingPrice: 0,
        rentPerDay: 0,
        rent7Days: 0,
        rent15Days: 0,
        rent30Days: 0,
        rentSlab: globalRentConfig.rentSlab,
        rentSlabCode: globalRentConfig.rentSlabCode,
        rentSlabDescription: globalRentConfig.rentSlabDescription,
        fromDate: globalRentConfig.fromDate,
        toDate: globalRentConfig.toDate,
        days: globalRentConfig.days,
        netValue: 0,
        grossValue: 0,
        taxPercentage: 0,
        taxAmount: 0,
        actualSecurityAmt: 0,
        securityAmt: 0,
        refundAmt: 0,
        refundDays: 0,
      }));

      setAccessoryItems((prev) => {
        // ── Only block truly duplicate barcodes that are already assigned ──────
        // Allow same materialCode multiple times (different physical units)
        // Block only if same barcode already exists AND is assigned
        const assignedBarcodes = new Set(
          prev
            .filter((a) => a.assignBarcode && a.barcode)
            .map((a) => a.barcode),
        );

        const uniqueNew = newItems.filter((item) => {
          // item.barcode is null at this point (user hasn't assigned yet)
          // so this check is future-proofing only
          if (item.barcode && assignedBarcodes.has(item.barcode)) {
            return false;
          }
          return true;
        });

        if (uniqueNew.length < newItems.length) {
          toast.info(
            `${newItems.length - uniqueNew.length} item(s) skipped — barcode already assigned.`,
          );
        }

        return [...prev, ...uniqueNew];
      });
    },
    // eslint-disable-next-line
    // [globalRentConfig],
    [globalRentConfig, saleItems],
  );

  const handleAccessoryBarcodeChange = useCallback((localId, value) => {
    setAccessoryItems((prev) =>
      prev.map((item) =>
        item.local_id === localId
          ? { ...item, barcode: value, barcodeValidated: false }
          : item,
      ),
    );
  }, []);

  const handleAccessoryBarcodeSubmit = useCallback(
    async (row, barcodeValue) => {
      if (!barcodeValue?.trim()) return;

      // Accessories travel with rent items → use Rent godown "RE"
      // const isValid = await loadBarCodeData(
      //   "RE",
      //   barcodeValue,
      //   row.materialCode,
      // );
      const godownCode = row.parentGodownCode || "RE"; // fallback to "RE"
      const isValid = await loadBarCodeData(
        godownCode,
        barcodeValue,
        row.materialCode,
      );

      if (!isValid) {
        toast.error("Invalid barcode or barcode not found for this accessory.");
        setAccessoryItems((prev) =>
          prev.map((item) =>
            item.local_id === row.local_id
              ? { ...item, barcodeValidated: false }
              : item,
          ),
        );
        return;
      }

      setAccessoryItems((prev) =>
        prev.map((item) =>
          item.local_id === row.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
      toast.success(`Accessory barcode ${barcodeValue} validated ✓`);
    },
    [loadBarCodeData],
  );

  // const handleAccessoryBarcodeAssign = useCallback(
  //   async (row) => {
  //     if (!row?.barcode?.trim()) {
  //       toast.error("Please enter a barcode first.");
  //       return;
  //     }
  //     if (!row.barcodeValidated) {
  //       toast.error("Barcode not validated. Enter barcode and press Enter.");
  //       return;
  //     }

  //     // Guard: must be saved to backend with equipmentItemNr
  //     if (!row.equipmentItemNr) {
  //       toast.error(
  //         "Please click 'Update Accessories' first before assigning.",
  //       );
  //       return;
  //     }

  //     const { local_id, barcode, materialCode } = row;
  //     const item = accessoryItems.find((a) => a.local_id === local_id);
  //     if (!item) {
  //       toast.error("Accessory item not found.");
  //       return;
  //     }

  //     setAssigningAccessoryBarcode((prev) => ({ ...prev, [local_id]: true }));

  //     try {
  //       // ── Pass equipmentItemNr so backend targets THIS specific row only ──────
  //       const assignPayload = {
  //         equipmentRentNr: documentNumber,
  //         erfTypeCode: formInputs.erfTypeCode,
  //         materialCode: materialCode,
  //         barcode: barcode,
  //         equipmentItemNr: item.equipmentItemNr, // ← unique per row
  //         assignBarcode: true,
  //         isAccessory: true,
  //         parentLocalId: item.parentLocalId, // ← which rent item owns this
  //         parentMaterialCode: item.parentMaterialCode,
  //       };

  //       const response =
  //         await ItemService.assignBarcodeForEquipmentRentForm(assignPayload);

  //       if (response?.status !== 200) {
  //         toast.error(response?.message || "Failed to assign barcode.");
  //         return;
  //       }

  //       toast.success(
  //         `Barcode ${barcode} assigned to ${materialCode} (Item #${item.equipmentItemNr})!`,
  //       );

  //       // Update only THIS specific item in local state using local_id
  //       const updatedAccessories = accessoryItems.map(
  //         (a) =>
  //           a.local_id === local_id
  //             ? { ...a, barcode, barcodeValidated: true, assignBarcode: true }
  //             : a, // ← other rows with same materialCode are NOT touched
  //       );
  //       setAccessoryItems(updatedAccessories);

  //       // $set only accessoryItems — server keeps rent/sale/service/nursing intact
  //       await ItemService.updateEquipmentRentForm({
  //         documentNumber: formInputs.documentNumber,
  //         equipmentRentNr: documentNumber,
  //         accessoryItems: updatedAccessories,
  //       });

  //       // Update barcode master
  //       await ItemService.updateMaterialBarcodeItem({
  //         barcode: barcode,
  //         materialStatus: "StockOut",
  //         materialCode: materialCode,
  //         barCodeAssignedDateTime: new Date().toISOString(),
  //         barCode: "Assigned",
  //       });

  //       await loadInitialPageData();
  //     } catch (err) {
  //       console.error("Accessory barcode assign error:", err);
  //       toast.error(
  //         err?.response?.data?.message || "Failed to assign barcode.",
  //       );
  //     } finally {
  //       setAssigningAccessoryBarcode((prev) => ({
  //         ...prev,
  //         [local_id]: false,
  //       }));
  //     }
  //   },
  //   [
  //     documentNumber,
  //     formInputs.documentNumber,
  //     formInputs.erfTypeCode,
  //     accessoryItems,
  //     loadInitialPageData,
  //   ],
  // );

  const handleAccessoryBarcodeAssign = useCallback(
    async (row) => {
      if (!row?.barcode?.trim()) {
        toast.error("Please enter a barcode first.");
        return;
      }
      if (!row.barcodeValidated) {
        toast.error("Barcode not validated. Enter barcode and press Enter.");
        return;
      }
      if (!row.equipmentItemNr) {
        toast.error(
          "Please click 'Update Accessories' first before assigning.",
        );
        return;
      }

      if (isAssigningBarcodeRef.current) {
        toast.warning("Please wait for the current assignment to complete");
        return;
      }

      const { local_id, barcode, materialCode } = row;
      const item = accessoryItems.find((a) => a.local_id === local_id);
      if (!item) {
        toast.error("Accessory item not found.");
        return;
      }

      isAssigningBarcodeRef.current = true;
      setIsAssigningBarcode(true);
      setAssigningAccessoryBarcode((prev) => ({ ...prev, [local_id]: true }));

      try {
        const assignPayload = {
          equipmentRentNr: documentNumber,
          erfTypeCode: formInputs.erfTypeCode,
          materialCode: materialCode,
          barcode: barcode,
          equipmentItemNr: item.equipmentItemNr,
          assignBarcode: true,
          isAccessory: true,
          parentLocalId: item.parentLocalId,
          parentMaterialCode: item.parentMaterialCode,
        };

        const response =
          await ItemService.assignBarcodeForEquipmentRentForm(assignPayload);

        if (response?.status !== 200) {
          toast.error(response?.message || "Failed to assign barcode.");
          return;
        }

        toast.success(
          `Barcode ${barcode} assigned to ${materialCode} (Item #${item.equipmentItemNr})!`,
        );

        const updatedAccessories = accessoryItems.map((a) =>
          a.local_id === local_id
            ? { ...a, barcode, barcodeValidated: true, assignBarcode: true }
            : a,
        );
        setAccessoryItems(updatedAccessories);

        await ItemService.updateEquipmentRentForm({
          documentNumber: formInputs.documentNumber,
          equipmentRentNr: documentNumber,
          accessoryItems: updatedAccessories,
        });

        await ItemService.updateMaterialBarcodeItem({
          barcode: barcode,
          materialStatus: "StockOut",
          materialCode: materialCode,
          barCodeAssignedDateTime: new Date().toISOString(),
          barCode: "Assigned",
        });

        await loadInitialPageData();
      } catch (err) {
        console.error("Accessory barcode assign error:", err);
        toast.error(
          err?.response?.data?.message || "Failed to assign barcode.",
        );
      } finally {
        isAssigningBarcodeRef.current = false;
        setIsAssigningBarcode(false);
        setAssigningAccessoryBarcode((prev) => ({
          ...prev,
          [local_id]: false,
        }));
      }
    },
    [
      documentNumber,
      formInputs.documentNumber,
      formInputs.erfTypeCode,
      accessoryItems,
      loadInitialPageData,
    ],
  );

  const handleRemoveAccessory = useCallback(
    async (localId) => {
      const target = accessoryItems.find((a) => a.local_id === localId);
      if (!target) return;

      if (target.assignBarcode) {
        toast.error(
          "Cannot remove — barcode is assigned. Remove barcode first.",
        );
        return;
      }

      const updated = accessoryItems.filter((a) => a.local_id !== localId);
      setAccessoryItems(updated);

      // If it was never saved server-side, nothing more to do
      if (!target.savedToBackend) return;

      try {
        await ItemService.updateEquipmentRentForm({
          documentNumber: formInputs.documentNumber,
          equipmentRentNr: documentNumber,
          accessoryItems: updated,
        });
        toast.success("Accessory removed.");
      } catch (err) {
        console.error("Remove accessory error:", err);
        toast.error("Failed to remove accessory. Please try again.");
        // rollback local state
        setAccessoryItems(accessoryItems);
      }
    },
    [accessoryItems, documentNumber, formInputs.documentNumber],
  );

  const handleManualBarcodeSubmit = async (row, barcodeValue, itemType) => {
    if (!barcodeValue?.trim()) return;

    // Determine godown code from item type
    const godownCode =
      itemType === "Sale" || itemType === "Service" ? "SL" : "RE";

    const isValid = await loadBarCodeData(
      godownCode,
      barcodeValue,
      row.materialCode,
    );

    if (!isValid) {
      toast.error("Invalid or already used barcode! or Mismatch product code");

      // Clear validation flag
      if (itemType === "Sale" || itemType === "Service") {
        setSaleItems((prev) =>
          prev.map((item) =>
            item.local_id === row.local_id
              ? { ...item, barcodeValidated: false }
              : item,
          ),
        );
      } else if (itemType === "Nursing") {
        setNursingItems((prev) =>
          prev.map((item) =>
            item.local_id === row.local_id
              ? { ...item, barcodeValidated: false }
              : item,
          ),
        );
      } else {
        setRentItems((prev) =>
          prev.map((item) =>
            item.local_id === row.local_id
              ? { ...item, barcodeValidated: false }
              : item,
          ),
        );
      }

      return;
    }

    // Set barcode and validated = true
    if (itemType === "Sale" || itemType === "Service") {
      setSaleItems((prev) =>
        prev.map((item) =>
          item.local_id === row.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
    } else if (itemType === "Nursing") {
      setNursingItems((prev) =>
        prev.map((item) =>
          item.local_id === row.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
    } else {
      setRentItems((prev) =>
        prev.map((item) =>
          item.local_id === row.local_id
            ? { ...item, barcode: barcodeValue, barcodeValidated: true }
            : item,
        ),
      );
    }

    toast.success("Barcode validated successfully");
  };
  const [allAssignBarcode, setAllAssignBarcode] = useState(false);
  // 1. Add isSavingAccessories state near other loading states
  const [isSavingAccessories, setIsSavingAccessories] = useState(false);

  const handleSaveAccessories = useCallback(async () => {
    if (accessoryItems.length === 0) {
      toast.warning("No accessories to save.");
      return;
    }
    setIsSavingAccessories(true);
    try {
      // ── Assign equipmentItemNr to each accessory that doesn't have one ──────
      // We generate a unique sequential number per parent group
      const accessoriesWithItemNr = accessoryItems.map((item, idx) => ({
        ...item,
        // If already has equipmentItemNr keep it, else assign new one
        equipmentItemNr: item.equipmentItemNr || idx + 1,
      }));
      const updatePayload = {
        documentNumber: formInputs.documentNumber,
        equipmentRentNr: documentNumber,
        accessoryItems: accessoriesWithItemNr,
      };

      await ItemService.updateEquipmentRentForm(updatePayload);

      toast.success("Accessories saved. You can now assign barcodes.");
      await loadInitialPageData(); // reload → items come back with savedToBackend: true + equipmentItemNr
    } catch (err) {
      console.error("Save accessories error:", err);
      toast.error("Failed to save accessories.");
    } finally {
      setIsSavingAccessories(false);
    }
  }, [
    documentNumber,
    formInputs.documentNumber,
    accessoryItems,
    loadInitialPageData,
  ]);

  // Replace checkAssignBarcode entirely
  const checkAssignBarcode = (
    rentItems,
    saleItems,
    nursingItems,
    serviceItems,
    accessoryItems = [],
  ) => {
    let itemsToCheck = [];

    switch (formInputs?.erfTypeCode) {
      case "RE":
        itemsToCheck = rentItems || [];
        break;
      case "SL":
        itemsToCheck = saleItems || [];
        break;
      case "NR":
        itemsToCheck = nursingItems || [];
        break;
      case "SV":
        itemsToCheck = serviceItems || [];
        break;
      case "SLRE":
        itemsToCheck = [...(saleItems || []), ...(rentItems || [])];
        break;
      default:
        return false;
    }

    if (!itemsToCheck?.length) return false;

    // All main items must be assigned
    const mainItemsAssigned = itemsToCheck.every(
      (item) => item.assignBarcode === true,
    );
    if (!mainItemsAssigned) return false;

    // All accessories (for items with accessories: true) must also be assigned
    const accessoryCheck = accessoryItems.every(
      (acc) => acc.assignBarcode === true,
    );

    return accessoryCheck;
  };

  // Replace your entire scanner useEffect with this:
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.key) {
        return;
      }
      const now = Date.now();
      // eslint-disable-next-line
      const timeDiff = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      const activeTag = document.activeElement?.tagName ?? "";
      // eslint-disable-next-line
      const isInputFocused =
        activeTag === "INPUT" ||
        activeTag === "TEXTAREA" ||
        activeTag === "SELECT";

      // If Enter pressed
      if (e.key === "Enter") {
        const buffer = (barcodeBufferRef.current ?? "").trim();

        if (buffer.length >= 3) {
          handleScannedBarcode(buffer);
        }

        barcodeBufferRef.current = "";
        clearTimeout(scanTimerRef.current);
        return;
      }

      // Only buffer printable characters
      // if (e.key.length === 1) {
      //   barcodeBufferRef.current += e.key;
      // }
      // Only buffer single printable characters — guard e.key length
      if (typeof e.key === "string" && e.key.length === 1) {
        // Ensure current is always a string before concatenating
        barcodeBufferRef.current = (barcodeBufferRef.current ?? "") + e.key;
      }

      // Auto-clear buffer after 500ms (safety)
      clearTimeout(scanTimerRef.current);
      scanTimerRef.current = setTimeout(() => {
        barcodeBufferRef.current = "";
      }, 500);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(scanTimerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     console.log(">>> KEY:", JSON.stringify(e.key), "| code:", e.code, "| charCode:", e.charCode);

  //     const now = Date.now();
  //     const timeDiff = now - lastKeyTimeRef.current;
  //     lastKeyTimeRef.current = now;
  //     console.log(">>> timeDiff:", timeDiff);
  //   };

  //   window.addEventListener("keydown", handleKeyDown);
  //   return () => window.removeEventListener("keydown", handleKeyDown);
  // }, []);
  // Replace handleScannedBarcode with this:
  const handleScannedBarcode = useCallback(
    async (barcodeValue) => {
      if (!barcodeValue?.trim()) return;

      // Case 1: Row already selected via icon click or input focus
      if (selectedRowId && selectedType) {
        const godownCode =
          selectedType === "Sale" || selectedType === "Service" ? "SL" : "RE";

        const isValid = await loadBarCodeData(
          godownCode,
          barcodeValue,
          selectProduct,
        );

        if (!isValid) {
          toast.error("Invalid or already used barcode!");
          return;
        }

        if (selectedType === "Sale" || selectedType === "Service") {
          setSaleItems((prev) =>
            prev.map((item) =>
              item.local_id === selectedRowId
                ? { ...item, barcode: barcodeValue, barcodeValidated: true }
                : item,
            ),
          );
        } else if (selectedType === "Nursing") {
          setNursingItems((prev) =>
            prev.map((item) =>
              item.local_id === selectedRowId
                ? { ...item, barcode: barcodeValue, barcodeValidated: true }
                : item,
            ),
          );
        } else {
          setRentItems((prev) =>
            prev.map((item) =>
              item.local_id === selectedRowId
                ? { ...item, barcode: barcodeValue, barcodeValidated: true }
                : item,
            ),
          );
        }

        toast.success(`Barcode ${barcodeValue} scanned!`);
        setShowBarcodeModal(false);
        setSelectedRowId(null);
        return;
      }

      const allCurrent = [
        ...rentItems.map((i) => ({ ...i, _listType: "Rent" })),
        ...saleItems.map((i) => ({ ...i, _listType: i.itemType })),
        ...nursingItems.map((i) => ({ ...i, _listType: "Nursing" })),
      ];

      const unassigned = allCurrent.find(
        (item) => !item.assignBarcode && !item.barcode,
      );

      if (!unassigned) {
        // No empty row - iterate through all items and find matching materialCode
        const allItems = [
          ...rentItems.map((i) => ({ ...i, _listType: "Rent" })),
          ...saleItems.map((i) => ({ ...i, _listType: i.itemType })),
          ...nursingItems.map((i) => ({ ...i, _listType: "Nursing" })),
        ];

        // Try to validate barcode against each item's materialCode
        for (const item of allItems) {
          if (!item.materialCode) continue;

          const godownCode =
            item._listType === "Sale" || item._listType === "Service"
              ? "SL"
              : "RE";

          const isValid = await loadBarCodeData(
            godownCode,
            barcodeValue,
            item.materialCode,
          );

          if (isValid) {
            // Find the first row without barcode for this material
            const targetRow = allItems.find(
              (r) =>
                r.materialCode === item.materialCode &&
                !r.barcode &&
                !r.assignBarcode,
            );

            if (targetRow) {
              // Assign barcode to this row
              if (targetRow._listType === "Rent") {
                setRentItems((prev) =>
                  prev.map((it) =>
                    it.local_id === targetRow.local_id
                      ? { ...it, barcode: barcodeValue, barcodeValidated: true }
                      : it,
                  ),
                );
              } else if (targetRow._listType === "Nursing") {
                setNursingItems((prev) =>
                  prev.map((it) =>
                    it.local_id === targetRow.local_id
                      ? { ...it, barcode: barcodeValue, barcodeValidated: true }
                      : it,
                  ),
                );
              } else {
                setSaleItems((prev) =>
                  prev.map((it) =>
                    it.local_id === targetRow.local_id
                      ? { ...it, barcode: barcodeValue, barcodeValidated: true }
                      : it,
                  ),
                );
              }
              toast.success(
                `✓ ${barcodeValue} validated & assigned to ${item.materialCode}`,
              );
            } else {
              // console.error(`✓ ${barcodeValue} is valid for ${item.materialCode}`)
              toast.success(
                `✓ ${barcodeValue} is valid for ${item.materialCode}`,
              );
            }
            return;
          }
        }

        // Barcode didn't match any material in this ERF
        console.error("Invalid barcode - not matching any item in this ERF");
        // toast.error("Invalid barcode - not matching any item in this ERF");
        return;
      }

      const godownCode =
        unassigned._listType === "Sale" || unassigned._listType === "Service"
          ? "SL"
          : "RE";

      const isValid = await loadBarCodeData(
        godownCode,
        barcodeValue,
        unassigned.materialCode,
      );

      if (!isValid) {
        toast.error("Invalid or already used barcode!");
        return;
      }

      if (unassigned._listType === "Rent") {
        setRentItems((prev) =>
          prev.map((item) =>
            item.local_id === unassigned.local_id
              ? { ...item, barcode: barcodeValue, barcodeValidated: true }
              : item,
          ),
        );
      } else if (unassigned._listType === "Nursing") {
        setNursingItems((prev) =>
          prev.map((item) =>
            item.local_id === unassigned.local_id
              ? { ...item, barcode: barcodeValue, barcodeValidated: true }
              : item,
          ),
        );
      } else {
        setSaleItems((prev) =>
          prev.map((item) =>
            item.local_id === unassigned.local_id
              ? { ...item, barcode: barcodeValue, barcodeValidated: true }
              : item,
          ),
        );
      }

      toast.success(`Barcode ${barcodeValue} → ${unassigned.materialCode}`);
    },
    // eslint-disable-next-line
    [
      selectedRowId,
      selectedType,
      selectProduct,
      rentItems,
      saleItems,
      nursingItems,
    ],
  );
  useEffect(() => {
    const result = checkAssignBarcode(
      formInputs?.rentItems,
      formInputs?.saleItems,
      formInputs?.nursingItems,
      formInputs?.serviceItems,
      accessoryItems,
    );

    setAllAssignBarcode(result);
    // eslint-disable-next-line
  }, [
    formInputs?.rentItems,
    formInputs?.saleItems,
    formInputs?.nursingItems,
    formInputs?.serviceItems,
    formInputs?.erfTypeCode,
    accessoryItems,
  ]);
  //services
  // const [getServiceData, setServiceData] = useState([]);
  // const [getServiceCountByBarcode, setServiceCountByBarcode] = useState({});  // 🆕
  // const [getServiceRentItems, setServiceRentItems] = useState([]);

  const [getShowServiceModal, setShowServiceModal] = useState(false);
  const hideSupplierInviteModal = () => setShowServiceModal(false);
  // eslint-disable-next-line
  const vehicleServices = () => {
    setShowServiceModal(true);
  };

  const handleSaveService = async (data) => {
    const serviceHistory = data?.serviceHistory?.map((item) => ({
      ...item,
      // serviceUpdate: true,
    }));

    const updatedRentItems = data?.rentItems;

    // Update local state first
    setFormInputs((prev) => ({
      ...prev,
      serviceHistory,
      serviceCountByBarcode: data?.serviceCountByBarcode,
      rentItems: updatedRentItems,
    }));

    setLoading(true);
    try {
      const payload = {
        ...formInputs,
        serviceHistory,
        serviceCountByBarcode: data?.serviceCountByBarcode,
        rentItems: updatedRentItems,
        version: incrementVersion(formInputs?.version),
      };

      // Remove mongo internals before sending
      if (payload._id) {
        delete payload._id;
      }
      if (payload.data) {
        delete payload.data;
      }

      await ItemService.updateEquipmentRentForm(payload);
      toast.success("ERF Service Updated Successfully");
    } catch (err) {
      console.error("Error updating service:", err);
      toast.error(err?.response?.data?.message || "Failed to update service");
    } finally {
      setLoading(false);
    }
  };
  //table
  const serviceHistoryColumns = [
    {
      name: "Service ID",
      selector: (row) => row?.serviceId || "--",
      cell: (row) => (
        <span className="bg-table-status bg-table-new-status">
          {row.serviceId}
        </span>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "Equipment",
      selector: (row) => row?.linkedMaterialDescription || "--",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 fw-bold" style={{ fontSize: "12px" }}>
            {row.linkedMaterialDescription || "--"}
          </p>
          <p className="m-0 p-0 text-muted" style={{ fontSize: "11px" }}>
            {row.linkedBarcode || ""}
          </p>
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Service Provider",
      selector: (row) => row?.serviceProvider || "--",
      sortable: true,
      width: "180px",
    },
    {
      name: "Service Status",
      selector: (row) => row?.serviceStatus || "--",
      // cell: (row) => (
      //   <span
      //     className={`badge ${
      //       row?.serviceStatus === SERVICE_STATUS.COMPLETED
      //         ? "bg-success"
      //         : "bg-warning text-dark"
      //     }`}
      //   >
      //     {row?.serviceStatus || "--"}
      //   </span>
      // ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Service Date/Time",
      selector: (row) => row?.serviceDate || "--",
      cell: (row) => (
        <div>
          <p className="m-0">{row?.serviceDate}</p>
          <p className="m-0 text-muted" style={{ fontSize: "11px" }}>
            {row?.serviceTime}
          </p>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Service Type",
      selector: (row) => row?.serviceType || "--",
      sortable: true,
      width: "150px",
    },
    {
      name: "Issues Reported",
      selector: (row) => row?.issuesReported || "--",
      sortable: true,
      width: "180px",
    },
    {
      name: "Issues Details",
      selector: (row) => row?.issuesDetails || "--",
      sortable: true,
      width: "180px",
    },
    {
      name: "Total Cost",
      selector: (row) => row?.totalCost || "--",
      cell: (row) => <p className="m-0 p-0 fw-bold">{row.totalCost}</p>,
      sortable: true,
      width: "150px",
    },
    {
      name: "Next Service Due",
      selector: (row) => row?.nextServiceDue || "--",
      cell: (row) => (
        <p className="m-0 p-0 pt-1 text-primary fw-bold">
          {row.nextServiceDue}
        </p>
      ),
      sortable: true,
      width: "170px",
    },
    {
      name: "Work Performed",
      selector: (row) => row?.workPerformed || "--",
      sortable: true,
      width: "170px",
    },
    {
      name: "Service Completed Date",
      selector: (row) => row?.serviceCompletedDate || "--",
      sortable: true,
      width: "200px",
    },
    {
      name: "Invoice Number",
      selector: (row) => row?.invoiceNumber || "--",
      sortable: true,
      width: "160px",
    },
    {
      name: "Remarks",
      selector: (row) => row?.remarks || "--",
      sortable: true,
      width: "200px",
    },
  ];

  // ── Payment / Security disable helpers-----
  const isPaymentModeSelected =
    formInputs.paymentMode !== "Select" && formInputs.paymentMode !== "";

  const isSecurityAmtModeSelected =
    formInputs.securityAmtMode !== "Select" &&
    formInputs.securityAmtMode !== "";

  const paymentRules = PAYMENT_MODE_RULES[formInputs.paymentMode] || {};
  const securityAmtRules = PAYMENT_MODE_RULES[formInputs.securityAmtMode] || {};

  //rent and sale chartage
  const [cartageCheck, setCartageCheck] = useState({
    salesCartage: false,
    rentCartage: false,
  });

  const handleCartageCheck = (e) => {
    const { name, checked } = e.target;

    // Mutually exclusive: checking one unchecks the other
    const newCartageCheck = {
      salesCartage: name === "salesCartage" ? checked : false,
      rentCartage: name === "rentCartage" ? checked : false,
    };

    setCartageCheck(newCartageCheck);

    // Read cartage from functional prev to avoid stale closure bug
    setFormInputs((prev) => ({
      ...prev,
      salesCartage: newCartageCheck.salesCartage
        ? Number(prev.cartage) || 0
        : 0,
      rentCartage: newCartageCheck.rentCartage ? Number(prev.cartage) || 0 : 0,
    }));
  };

  // Auto-set checkboxes whenever ERF Type changes
  useEffect(() => {
    if (!formInputs.erfTypeCode) return;

    switch (formInputs.erfTypeCode) {
      case "SL": // Sale → force salesCartage, lock both
        setCartageCheck({ salesCartage: true, rentCartage: false });
        setFormInputs((prev) => ({
          ...prev,
          salesCartage: Number(prev.cartage) || 0,
          rentCartage: 0,
        }));
        break;

      case "RE": // Rent → force rentCartage, lock both
        setCartageCheck({ salesCartage: false, rentCartage: true });
        setFormInputs((prev) => ({
          ...prev,
          salesCartage: 0,
          rentCartage: Number(prev.cartage) || 0,
        }));
        break;

      case "SLRE": // Sale & Rent → default to salesCartage, user can switch
        setCartageCheck({ salesCartage: true, rentCartage: false });
        setFormInputs((prev) => ({
          ...prev,
          salesCartage: Number(prev.cartage) || 0,
          rentCartage: 0,
        }));
        break;

      default: // Service, Nursing — clear both
        setCartageCheck({ salesCartage: false, rentCartage: false });
        setFormInputs((prev) => ({ ...prev, salesCartage: 0, rentCartage: 0 }));
        break;
    }
    // eslint-disable-next-line
  }, [formInputs.erfTypeCode]);

  useEffect(() => {
    if (formInputs.erfTypeCode === "SL" || formInputs.erfTypeCode === "SLRE") {
      setCartageCheck({
        salesCartage: true,
        rentCartage: false,
      });

      setFormInputs((prev) => ({
        ...prev,
        salesCartage: prev.cartage || 0,
        rentCartage: 0,
      }));
    }

    if (formInputs.erfTypeCode === "RE") {
      setCartageCheck({
        salesCartage: false,
        rentCartage: false,
      });

      setFormInputs((prev) => ({
        ...prev,
        salesCartage: 0,
        rentCartage: 0,
      }));
    }
  }, [formInputs.erfTypeCode]);
  //barcode print

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            {/* HEADER */}
            <Row className="mb-4">
              <Row>
                <Col xs={12} md={12}>
                  <h2 className="mainTitle">
                    ERF Detail - {formInputs.patientName}
                    <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage ms-2">
                      {documentNumber}
                    </span>
                    <span className="headerStatusDetailedPage ms-2">
                      <TableStatusField status={formInputs.erfStatus} />
                    </span>
                    <span className="headerStatusDetailedPage ms-2">
                      <TableStatusField status={formInputs.erfType} />
                    </span>
                  </h2>
                  <p className="subText">Equipment Rental Details</p>
                </Col>
              </Row>
              {/* {false &&
                showEditButtons &&
                formInputs.status !== "Cancelled" && (
                  <Row>
                    <Col
                      xs={6}
                      md={{ span: 2, offset: 10 }}
                      lg={{ span: 2, offset: 10 }}
                      className="text-left"
                    >
                      {edit ? (
                        <button
                          className="btn btn-primary"
                          onClick={handleEdit}
                        >
                          <i className="bi bi-pencil-square px-2"></i> Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={() => showApproveModal("Update")}
                            disabled={!isFormEnabled}
                          >
                            <i className="bi bi-arrow-repeat px-2"></i> Update
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={handleCancelEdit}
                          >
                            <i className="bi bi-x-circle px-2"></i> Cancel
                          </button>
                        </>
                      )}
                    </Col>
                  </Row>
                )}
              {canEditCancelledERF && (
                <>
                  <Row className="alert alert-warning">
                    <strong>Cancelled ERF</strong>
                    <span className="d-block small text-muted">
                      This ERF has been cancelled. Admin can edit and resubmit.
                    </span>
                  </Row>
                  <Row>
                    <Col
                      xs={6}
                      md={{ span: 2, offset: 10 }}
                      lg={{ span: 2, offset: 10 }}
                      className="text-left"
                    >
                      {edit ? (
                        <button
                          className="btn btn-primary"
                          onClick={handleEdit}
                        >
                          <i className="bi bi-pencil-square px-2"></i> Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={() => showApproveModal("Update")}
                            disabled={!isFormEnabled}
                          >
                            <i className="bi bi-arrow-repeat px-2"></i> Update
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={handleCancelEdit}
                          >
                            <i className="bi bi-x-circle px-2"></i> Cancel
                          </button>
                        </>
                      )}
                    </Col>
                  </Row>
                </>
              )} */}
              {/* ─────────── NORMAL EDIT (live ERF) ─────────── */}
              {showEditButtons && canEditERF && (
                <Row>
                  <Col
                    xs={6}
                    md={{ span: 2, offset: 10 }}
                    lg={{ span: 2, offset: 10 }}
                    className="text-left"
                  >
                    {edit ? (
                      <button className="btn btn-primary" onClick={handleEdit}>
                        <i className="bi bi-pencil-square px-2"></i> Edit
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-primary"
                          onClick={() => showApproveModal("Update")}
                          disabled={!isFormEnabled}
                        >
                          <i className="bi bi-arrow-repeat px-2"></i> Update
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={handleCancelEdit}
                        >
                          <i className="bi bi-x-circle px-2"></i> Cancel
                        </button>
                      </>
                    )}
                  </Col>
                </Row>
              )}

              {/* ─────────── CANCELLED ERF — EDIT & RESUBMIT ─────────── */}
              {canEditCancelledERF && (
                <>
                  <Row className="alert alert-warning">
                    <strong>Cancelled ERF</strong>
                    <span className="d-block small text-muted">
                      This ERF has been cancelled. Admin can edit and resubmit.
                    </span>
                  </Row>
                  <Row>
                    <Col
                      xs={6}
                      md={{ span: 2, offset: 10 }}
                      lg={{ span: 2, offset: 10 }}
                      className="text-left"
                    >
                      {edit ? (
                        <button
                          className="btn btn-warning"
                          onClick={handleResubmitCancelled}
                        >
                          <i className="bi bi-arrow-counterclockwise px-2"></i>{" "}
                          Edit & Resubmit
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-primary"
                            onClick={() => showApproveModal("Update")}
                            disabled={!isFormEnabled}
                          >
                            <i className="bi bi-arrow-repeat px-2"></i> Resubmit
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={handleCancelEdit}
                          >
                            <i className="bi bi-x-circle px-2"></i> Cancel
                          </button>
                        </>
                      )}
                    </Col>
                  </Row>
                </>
              )}

              {allAssignBarcode && showStoreSection && (
                <>
                  {isStoreUser && (
                    <Row>
                      {/* ERF For Type Selection */}

                      <Col
                        xs={6}
                        md={{ span: 2, offset: 10 }}
                        lg={{ span: 2, offset: 10 }}
                        className="text-left"
                      >
                        {editForStore ? (
                          <>
                            <button
                              className="btn btn-primary"
                              onClick={handleEditForStore}
                              disabled={formInputs?.erfDispatchIndicator}
                            >
                              <i className="bi bi-pencil-square px-2"></i>{" "}
                              Approve Dispatch
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn btn-primary"
                              onClick={handleUpdateForStore}
                              disabled={
                                !isFormEnabled ||
                                !erfDispatchIndicator ||
                                !dispatchRemarks.trim()
                              }
                              title={
                                !erfDispatchIndicator
                                  ? 'Check "Ready To Dispatch" to enable'
                                  : !dispatchRemarks.trim()
                                    ? "Enter dispatch remarks to enable"
                                    : ""
                              }
                            >
                              <i className="bi bi-arrow-repeat px-2"></i> Update
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={handleCancelEditForStore}
                            >
                              <i className="bi bi-x-circle px-2"></i> Cancel
                            </button>
                          </>
                        )}
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Row>
            <hr />

            {props.topSlot}

            <Loader isLoading={loading} className={"text-center"} />

            {!loading && (
              <>
                {/* ORDER DETAILS ACCORDION */}
                <div className="accordion mb-4" id="accordionExample">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${
                              isCardVisible
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                            onClick={toggleCardVisibility}
                          ></i>
                        </Col>
                        <Col xs={1} className="text-center logoBtn">
                          <span className="iconLogo">OD</span>
                        </Col>
                        <Col xs={10} md={8} lg={8}>
                          <span className="headerTitle">Order Details</span>
                          <span className="subHeaderTitle">
                            Basic information
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisible ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body pb-4">
                        {/* Order & Patient Fields */}
                        <Row className="pt-2">
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="ERF Type"
                              data={erfTypeOptions}
                              name="erfType"
                              textField="name"
                              setValue={formInputs?.erfType}
                              getvalue={setDropdownData}
                              // disabled={disabled}
                              disabled={true} // always disabled for editing ERF type
                              required={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Privilege Case"
                              data={privilegeOptions}
                              name="privilegeCase"
                              textField="name"
                              setValue={formInputs.privilegeCase}
                              getvalue={setDropdownData}
                              disabled={true} // always disabled for editing Privilage type
                              required={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              type="date"
                              label="ERF Order Date"
                              name="orderDate"
                              value={formInputs.orderDate || ""}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                              error={validation.expectedDeliveryDate}
                              min={new Date().toISOString().split("T")[0]}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Sale Point"
                              data={SALE_POINT_OPTIONS}
                              name="salePoint"
                              textField="name"
                              setValue={formInputs.salePoint}
                              getvalue={setDropdownData}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Company"
                              data={companyList}
                              name="selectedCompanyId"
                              textField="name"
                              setValue={selectedCompany?.name || ""}
                              getvalue={setDropdownData}
                              disabled={
                                disabled || !isFormEnabled || isLoadingCompany
                              }
                              placeholder={
                                isLoadingCompany
                                  ? "Loading companies..."
                                  : "Select Company"
                              }
                              required={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Order Type"
                              data={ORDER_TYPE_OPTIONS}
                              name="orderType"
                              textField="name"
                              setValue={formInputs.orderType}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Source of Call"
                              data={SOURCE_OF_CALL_OPTIONS}
                              name="sourceOfCall"
                              textField="name"
                              setValue={formInputs.sourceOfCall}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Cost Center"
                              name="costCenter"
                              value={formInputs.costCenter || ""}
                              onChange={handleInputChange}
                              disabled={true}
                            />
                          </Col>

                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Billing Name"
                              name="billingName"
                              value={formInputs?.billingName || ""}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>

                          {!editForStore && (
                            <Row className="ps-2 pt-3 pb-3">
                              <Col
                                xs={12}
                                md={6}
                                lg={6}
                                className="text-left mt-2"
                              >
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                  {/* ── Checkbox ── */}
                                  <div className="form-check mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      name="erfDispatchIndicator"
                                      id="erfDispatchIndicator"
                                      checked={erfDispatchIndicator}
                                      onChange={(e) => {
                                        setIsReadyToDispatch(e.target.checked);
                                        if (!e.target.checked) {
                                          setDispatchRemarks("");
                                          // clear validation error when unchecked
                                          setValidation((prev) => ({
                                            ...prev,
                                            dispatchRemarks: "",
                                          }));
                                        }
                                      }}
                                    />

                                    <label
                                      className="form-check-label ms-2 badge bg-primary text-white px-3 py-2 fs-6 shadow-sm"
                                      htmlFor="erfDispatchIndicator"
                                      style={{ cursor: "pointer" }}
                                    >
                                      Ready To Dispatch
                                    </label>
                                  </div>
                                </div>

                                {/* ── Remarks — shown only when checkbox is checked, uses company InputField ── */}
                                {erfDispatchIndicator && (
                                  <div
                                    className="mt-2"
                                    style={{ maxWidth: "400px" }}
                                  >
                                    <TextAreaField
                                      className="inputBox"
                                      label="Dispatch Remarks"
                                      name="dispatchRemarks"
                                      value={dispatchRemarks || ""}
                                      onChange={(e) => {
                                        setDispatchRemarks(e.target.value);
                                        // clear error as user types
                                        if (e.target.value.trim()) {
                                          setValidation((prev) => ({
                                            ...prev,
                                            dispatchRemarks: "",
                                          }));
                                        }
                                      }}
                                      required
                                      error={validation.dispatchRemarks}
                                      placeholder="Enter dispatch remarks..."
                                      disabled={false}
                                    />
                                  </div>
                                )}
                              </Col>
                            </Row>
                          )}

                          {formInputs.approvers?.length > 0 && (
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Selected Approver"
                                data={APPROVED_BY_OPTIONS_LEVEL_ONE}
                                name="approver_1"
                                textField="name"
                                required="true"
                                getvalue={(data) =>
                                  handleApproverSelection(0, data)
                                }
                                setValue={formInputs.approvers?.[0]?.name || ""} // Load existing approver if available
                                disabled={true}
                              />
                            </Col>
                          )}

                          {/* <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <span className="fw-medium text-black">
                              Update Remarks / Version
                            </span>
                            <div>
                              <span
                                style={{ marginLeft: "12px" }}
                                onClick={() =>
                                  remarkItemShowModal(formInputs.updateRemarks)
                                }
                              >
                                <i className="bi bi-calendar2-week iconTable"></i>
                              </span>
                              <span className="suppliersIcon">
                                {formInputs?.updateRemarks?.length > 0
                                  ? `${formInputs.updateRemarks.length}/${formInputs?.version}`
                                  : `0/${formInputs?.version}`}
                              </span>
                            </div>
                          </Col> */}
                          {/* {formInputs?.erfType === "Rent" && (
                            <Col
                              xs={12}
                              md={4}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <label
                                htmlFor="extensionCheckbox"
                                className="fw-medium mb-0"
                                style={{ cursor: "pointer" }}
                              >
                                Extension
                              </label>
                              <input
                                id="extensionCheckbox"
                                label=""
                                type="checkbox"
                                name="extension"
                                checked={formInputs?.extension}
                                onChange={handleCheckBoxInputChange}
                              />
                            </Col>
                          )} */}

                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                type="date"
                                label="Expected Delivery Date"
                                name="expectedDeliveryDate"
                                value={formInputs.expectedDeliveryDate || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                                error={validation.expectedDeliveryDate}
                                min={new Date().toISOString().split("T")[0]}
                              />
                            </Col>

                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                type="time"
                                label="Expected Delivery Time (24hr)"
                                name="expectedDeliveryTime"
                                value={formInputs.expectedDeliveryTime || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                          </Row>
                          {(formInputs.erfType === "Rent" ||
                            formInputs.erfType === "Sale & Rent" ||
                            formInputs.erfType === "Nursing Service") && (
                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  type="date"
                                  label="ERF Start Date"
                                  name="erfStartDate"
                                  value={formInputs.erfStartDate || ""}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                  error={validation.erfStartDate}
                                  min={new Date().toISOString().split("T")[0]}
                                />
                              </Col>

                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  type="date"
                                  label="ERF End Date"
                                  name="erfEndDate"
                                  value={formInputs.erfEndDate || ""}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                  error={validation.erfEndDate}
                                  min={new Date().toISOString().split("T")[0]}
                                />
                              </Col>
                            </Row>
                          )}
                        </Row>

                        {/* ERF For Type Selection */}
                        <Row className="pt-2 mb-4 bg-light p-3 rounded">
                          <Col xs={12}>
                            <h6 className="text-primary mb-3">
                              ERF Type Selection
                            </h6>
                            <div className="d-flex gap-4">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="erfForType"
                                  id="erfForPatient"
                                  value="Patient"
                                  checked={erfForType === "Patient"}
                                  onChange={(e) => {
                                    setErfForType(e.target.value);
                                    setSelectedHospital(null);
                                  }}
                                  disabled={true}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="erfForPatient"
                                >
                                  <strong>Patient</strong>
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="erfForType"
                                  id="erfForHospital"
                                  value="Hospital"
                                  checked={erfForType === "Hospital"}
                                  onChange={(e) => {
                                    setErfForType(e.target.value);
                                    // Clear patient data
                                    setFormInputs((prev) => ({
                                      ...prev,
                                      patientName: "",
                                      mobileNr: "",
                                      age: "",
                                      weight: "",
                                      aadharNo: "",
                                    }));
                                  }}
                                  disabled={true}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="erfForHospital"
                                >
                                  <strong>Hospital</strong>
                                </label>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* HOSPITAL INFORMATION SECTION - Only show for B2B */}
                {erfForType === "Hospital" && (
                  <div className="accordion mb-4" id="accordionHospital">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col xs={1} className="text-left arrowBtn">
                            <i className="bi bi-chevron-down"></i>
                          </Col>
                          <Col xs={1} className="text-center logoBtn">
                            <span className="iconLogo">HI</span>
                          </Col>
                          <Col xs={10} md={8} lg={8}>
                            <span className="headerTitle">
                              Hospital Information
                            </span>
                          </Col>
                        </Row>
                      </h2>

                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body pb-4">
                          {/* Hospital Selection */}
                          <Row className="mb-4">
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                label="Select Hospital For ERF"
                                data={erfHospitalList}
                                name="selectedHospitalId"
                                textField="name"
                                // setValue={selectedHospital?.userId || ""}
                                setValue={
                                  selectedHospital
                                    ? selectedHospital.hospitalName ||
                                      selectedHospital.organisationName
                                    : ""
                                } // Show the NAME, not userId
                                getvalue={setDropdownData}
                                // disabled={!isFormEnabled}
                                isLoading={isErfLoadingHospitals}
                                placeholder="Select Hospital"
                                required={true}
                                disabled={true}
                              />
                            </Col>
                          </Row>

                          {/* Hospital Details Display (Read-Only) */}
                          <>
                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Hospital Name"
                                  name="hospitalName"
                                  value={formInputs.hospitalName || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Hospital Association Number"
                                  name="hospitalAssociateNumber"
                                  value={
                                    formInputs.hospitalAssociateNumber || ""
                                  }
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Contact Number"
                                  name="hospitalMobileNr"
                                  value={formInputs.hospitalMobileNr || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Email"
                                  name="hospitalEmail"
                                  value={formInputs.hospitalEmail || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                            </Row>

                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="GST No"
                                  name="hospitalGstNo"
                                  value={formInputs.hospitalGstNo || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 1"
                                  name="hospitalAddress"
                                  value={formInputs.hospitalAddress || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 2"
                                  name="hospitalAddress2"
                                  value={formInputs.hospitalAddress2 || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="City"
                                  name="hospitalCity"
                                  value={formInputs.hospitalCity || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Billing Address"
                                  name="hospitalBillingAddress"
                                  value={
                                    formInputs.hospitalBillingAddress || ""
                                  }
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="State"
                                  name="hospitalState"
                                  value={formInputs.hospitalState || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Postal Code"
                                  name="hospitalPinCode"
                                  value={formInputs.hospitalPinCode || ""}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                          </>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PATIENT INFORMATION  */}
                <div className="accordion mb-4" id="accordionExample">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${
                              isCardVisible3
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                            onClick={toggleCardVisibility3}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">PI</span>
                        </Col>
                        <Col xs={10} md={8} lg={8}>
                          <span className="headerTitle">
                            Patient Information
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisible3 ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body pb-4">
                        {false && !edit && (
                          <>
                            {/* ERF Autocomplete Search */}
                            <Row className="mb-4">
                              <Col xs={12}>
                                <div className="p-3 bg-light rounded border">
                                  <h6 className="text-primary mb-3">
                                    Quick Fill from previous ERF
                                  </h6>

                                  <ERFAutocompleteSearch
                                    onERFSelect={handleERFSelection}
                                    disabled={disabled || !isFormEnabled}
                                    placeholder="Type patient name or ERF number (min. 2 characters)..."
                                  />

                                  {/* Preview of Loaded ERF */}
                                  {isERFLoaded && selectedERF && (
                                    <div
                                      className="alert alert-soft-primary d-flex align-items-center mt-3 border-0 shadow-sm"
                                      role="alert"
                                    >
                                      <div className="fs-4 me-3 text-primary">
                                        <i className="bi bi-check-circle-fill"></i>
                                      </div>
                                      <div className="flex-grow-1">
                                        <h6 className="alert-heading fw-bold mb-1">
                                          ERF Loaded Successfully
                                        </h6>
                                        <p className="mb-0 small text-muted">
                                          <strong>ID:</strong>{" "}
                                          {selectedERF.documentNumber} &bull;
                                          <strong> Patient:</strong>{" "}
                                          {selectedERF.patientName} &bull;
                                          <strong> Contact:</strong>{" "}
                                          {selectedERF.mobileNr}
                                        </p>
                                      </div>
                                      <button
                                        className="btn btn-sm btn-outline-danger ms-3"
                                        onClick={() => {
                                          setSelectedERF(null);
                                          setIsERFLoaded(false);
                                        }}
                                      >
                                        <i className="bi bi-x-lg me-1"></i>{" "}
                                        Clear
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </>
                        )}

                        <Row className="pt-3">
                          {!isHospital && (
                            <Row>
                              <h6
                                className="text-primary mb-3 ps-2"
                                style={{ fontSize: "14px", fontWeight: "600" }}
                              >
                                Doctor Information
                              </h6>
                              <Row>
                                {/* ── Column 1: Dr. Type ── */}
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    label="Dr. Type"
                                    data={DOCTOR_TYPE_OPTIONS}
                                    name="drType"
                                    textField="name"
                                    setValue={formInputs.drType || ""}
                                    getvalue={setDropdownData}
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>

                                {formInputs.drType === "Listed" && (
                                  <>
                                    <Col
                                      xs={12}
                                      md={6}
                                      lg={3}
                                      className="commTopButtonRightLeftPadding mb-3"
                                    >
                                      <ComboboxField
                                        label="Search By"
                                        data={DOCTOR_TYPE_SELECTION_OPTIONS}
                                        name="drListedType"
                                        textField="name"
                                        setValue={formInputs.drListedType || ""}
                                        getvalue={setDropdownData}
                                        disabled={disabled || !isFormEnabled}
                                      />
                                    </Col>

                                    {formInputs.drListedType === "Hospital" && (
                                      <Col
                                        xs={12}
                                        md={6}
                                        lg={3}
                                        className="commTopButtonRightLeftPadding mb-3"
                                      >
                                        <ComboboxField
                                          label="Select Hospital"
                                          data={hospitalList}
                                          name="patientErfHospitalName"
                                          textField="name"
                                          setValue={
                                            formInputs.patientErfHospitalName ||
                                            ""
                                          }
                                          getvalue={setDropdownData}
                                          disabled={disabled || !isFormEnabled}
                                          isLoading={isLoadingHospitals}
                                          placeholder="Select Hospital"
                                        />
                                      </Col>
                                    )}

                                    {(formInputs.drListedType === "Doctor" ||
                                      (formInputs.drListedType === "Hospital" &&
                                        formInputs.patientErfHospitalName)) && (
                                      <Col
                                        xs={12}
                                        md={6}
                                        lg={3}
                                        className="commTopButtonRightLeftPadding mb-3"
                                      >
                                        {/* <ComboboxField
                                          label="Select Doctor"
                                          data={doctorsList}
                                          name="doctorName"
                                          textField="name"
                                          setValue={formInputs.doctorName || ""}
                                          getvalue={setDropdownData}
                                          disabled={
                                            disabled ||
                                            !isFormEnabled ||
                                            isLoadingDoctors
                                          }
                                          isLoading={isLoadingDoctors}
                                          placeholder={
                                            isLoadingDoctors
                                              ? "Loading doctors..."
                                              : "Select Doctor"
                                          }
                                        /> */}
                                        <ComboboxField
                                          label="Select Doctor"
                                          data={doctorsList}
                                          name="doctorName"
                                          textField="name"
                                          setValue={
                                            doctorsList.find(
                                              (d) =>
                                                d.name ===
                                                formInputs.doctorName,
                                            ) ||
                                            formInputs.doctorName ||
                                            ""
                                          }
                                          getvalue={setDropdownData}
                                          disabled={
                                            disabled ||
                                            !isFormEnabled ||
                                            isLoadingDoctors
                                          }
                                          isLoading={isLoadingDoctors}
                                          placeholder={
                                            isLoadingDoctors
                                              ? "Loading doctors..."
                                              : "Select Doctor"
                                          }
                                        />
                                      </Col>
                                    )}

                                    {formInputs.doctorName && (
                                      <Col
                                        xs={12}
                                        md={6}
                                        lg={3}
                                        className="commTopButtonRightLeftPadding mb-3"
                                      >
                                        <InputField
                                          className="inputBox"
                                          label="Doctor Name"
                                          name="doctorName"
                                          value={formInputs.doctorName || ""}
                                          disabled={true}
                                        />
                                      </Col>
                                    )}

                                    {formInputs.doctorName && (
                                      <Col
                                        xs={12}
                                        md={6}
                                        lg={3}
                                        className="commTopButtonRightLeftPadding mb-3"
                                      >
                                        <InputField
                                          className="inputBox"
                                          label="Dr. Code"
                                          name="drCode"
                                          value={formInputs.drCode || ""}
                                          disabled={true}
                                        />
                                      </Col>
                                    )}
                                  </>
                                )}

                                {formInputs.drType === "Non-Listed" && (
                                  <>
                                    <Col
                                      xs={12}
                                      md={6}
                                      lg={3}
                                      className="commTopButtonRightLeftPadding mb-3"
                                    >
                                      <InputField
                                        className="inputBox"
                                        label="Doctor Name"
                                        name="doctorName"
                                        value={formInputs.doctorName || ""}
                                        onChange={handleInputChange}
                                        disabled={disabled || !isFormEnabled}
                                        placeholder="Enter doctor name"
                                      />
                                    </Col>

                                    <Col
                                      xs={12}
                                      md={6}
                                      lg={3}
                                      className="commTopButtonRightLeftPadding mb-3"
                                    >
                                      <InputField
                                        className="inputBox"
                                        label="Dr. Code"
                                        name="drCode"
                                        value={formInputs.drCode || ""}
                                        onChange={handleInputChange}
                                        disabled={disabled || !isFormEnabled}
                                        placeholder="Enter doctor code"
                                      />
                                    </Col>
                                  </>
                                )}

                                {formInputs.drType === "Not Given" && (
                                  <>
                                    <Col
                                      xs={12}
                                      md={6}
                                      lg={3}
                                      className="commTopButtonRightLeftPadding mb-3"
                                    >
                                      <InputField
                                        className="inputBox"
                                        label="Doctor Name"
                                        name="doctorName"
                                        value=""
                                        disabled={true}
                                        placeholder="Not applicable"
                                      />
                                    </Col>

                                    <Col
                                      xs={12}
                                      md={6}
                                      lg={3}
                                      className="commTopButtonRightLeftPadding mb-3"
                                    >
                                      <InputField
                                        className="inputBox"
                                        label="Dr. Code"
                                        name="drCode"
                                        value=""
                                        disabled={true}
                                        placeholder="Not applicable"
                                      />
                                    </Col>
                                  </>
                                )}
                              </Row>
                            </Row>
                          )}

                          {!isHospital && <hr className="my-3 text-muted" />}

                          {/* Patient Info */}

                          <h6
                            className="text-primary mb-3 ps-2"
                            style={{ fontSize: "14px", fontWeight: "600" }}
                          >
                            Patient Information
                          </h6>
                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Patient Type"
                                data={PATIENT_TYPE_OPTIONS}
                                name="patientType"
                                textField="name"
                                setValue={formInputs.patientType || ""}
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>

                            {/* Show patient dropdown only when "Listed" is selected */}
                            {formInputs.patientType === "Listed" && (
                              <>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    label="Select Patient"
                                    data={patientList}
                                    name="patient"
                                    textField="name"
                                    setValue={formInputs.patient || ""}
                                    getvalue={setDropdownData}
                                    disabled={disabled || !isFormEnabled}
                                    isLoading={isLoadingPatient}
                                    placeholder="Select Patient"
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Association Number"
                                    name="AssociateNumber"
                                    value={formInputs.AssociateNumber || ""}
                                    disabled={true}
                                  />
                                </Col>
                              </>
                            )}
                          </Row>

                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Patient Name"
                                name="patientName"
                                value={formInputs.patientName || ""}
                                onChange={handleInputChange}
                                required
                                error={validation.patientName}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Contact No"
                                name="mobileNr"
                                value={formInputs.mobileNr || ""}
                                onChange={handleInputChange}
                                required
                                type="number"
                                error={validation.mobileNr}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={6}
                              md={3}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Age"
                                name="age"
                                value={formInputs.age || ""}
                                onChange={handleInputChange}
                                type="number"
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={6}
                              md={3}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Weight (Kg)"
                                name="weight"
                                value={formInputs.weight || ""}
                                onChange={handleInputChange}
                                type="number"
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>

                            <Col
                              xs={12}
                              md={6}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Gender"
                                data={GENDER_OPTIONS}
                                name="gender"
                                textField="name"
                                setValue={formInputs.gender || ""}
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            {/* <Col
                              xs={12}
                              md={6}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="GST No"
                                name="gstNo"
                                value={formInputs.gstNo || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col> */}
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="PAN No"
                                name="panNo"
                                value={formInputs.panNo || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Aadhaar No"
                                name="aadharNo"
                                value={formInputs.aadharNo || ""}
                                onChange={handleInputChange}
                                // required
                                error={validation.aadharNo}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Is Diabetic?"
                                data={IS_DIABETIC_OPTIONS}
                                name="isDiabetic"
                                textField="name"
                                setValue={formInputs.isDiabetic || ""}
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={12}
                              lg={12}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <TextAreaField
                                className="inputBox"
                                label="Patient Challenges"
                                name="patientChallenges"
                                value={formInputs.patientChallenges || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                          </Row>

                          <hr className="my-3 text-muted" />

                          {/* Caregiver Info */}
                          <h6
                            className="text-primary mb-3 ps-2"
                            style={{ fontSize: "14px", fontWeight: "600" }}
                          >
                            Caregiver Information
                          </h6>
                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Caregiver Name"
                                name="caregiverName"
                                value={formInputs.caregiverName || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Caregiver Contact No"
                                name="caregiverMobileNr"
                                value={formInputs.caregiverMobileNr}
                                onChange={handleInputChange}
                                required
                                type="number"
                                error={validation.caregiverMobileNr}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Email ID"
                                name="emailId"
                                value={formInputs.emailId || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Relationship"
                                name="caregiverRelation"
                                value={formInputs.caregiverRelation || ""}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={2}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="ID Proof Type"
                                data={ID_PROOF_OPTIONS}
                                name="idProof"
                                textField="name"
                                setValue={formInputs.idProof || ""}
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col>
                            {formInputs.idProof &&
                              formInputs.idProof !== "Select" && (
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={2}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label={`${formInputs.idProof} Number`}
                                    name="idProofNumber"
                                    placeholder={`Enter ${formInputs.idProof} number`}
                                    value={formInputs.idProofNumber || ""}
                                    onChange={handleInputChange}
                                    disabled={disabled || !isFormEnabled}
                                    // required
                                    error={validation.idProofNumber}
                                  />
                                </Col>
                              )}
                          </Row>

                          {/* Addresses */}
                          <Row className="mt-2">
                            <Col md={6} className="pe-md-4">
                              <h6
                                className="text-primary mb-3 ps-2"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                }}
                              >
                                Billing Address
                              </h6>
                              <Row>
                                <Col
                                  xs={12}
                                  md={12}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <TextAreaField
                                    className="inputBox"
                                    label="Address"
                                    name="billingAddress"
                                    value={formInputs.billingAddress || ""}
                                    onChange={handleInputChange}
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={6}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    label="State"
                                    data={STATE_OPTIONS}
                                    name="billingState"
                                    textField="name"
                                    setValue={formInputs.billingState || ""}
                                    getvalue={setDropdownData}
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={6}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Pin Code"
                                    name="billingPinCode"
                                    value={formInputs.billingPinCode || ""}
                                    onChange={handleInputChange}
                                    type="number"
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                              </Row>
                            </Col>
                            <Col md={6} className="ps-md-4 border-start-md">
                              <Row>
                                <Col md={3} lg={3}>
                                  <h6
                                    className="text-primary mb-3 ps-2"
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Delivery Address
                                  </h6>
                                </Col>
                                <Col md={3} lg={3}>
                                  {/* Same as Billing Checkbox */}
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="sameAsBilling"
                                      checked={isSameAddress}
                                      onChange={(e) => {
                                        setIsSameAddress(e.target.checked);
                                        if (e.target.checked) {
                                          // Copy billing to delivery
                                          setFormInputs((prev) => ({
                                            ...prev,
                                            deliveryAddress:
                                              prev.billingAddress,
                                            deliveryState: prev.billingState,
                                            deliveryPinCode:
                                              prev.billingPinCode,
                                          }));
                                        }
                                      }}
                                      disabled={disabled || !isFormEnabled}
                                    />
                                    <label
                                      className="form-check-label small"
                                      htmlFor="sameAsBilling"
                                    >
                                      Same as Billing Address
                                    </label>
                                  </div>
                                </Col>
                              </Row>
                              <Row>
                                <Col
                                  xs={12}
                                  md={12}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <TextAreaField
                                    className="inputBox"
                                    label="Address"
                                    name="deliveryAddress"
                                    value={formInputs.deliveryAddress || ""}
                                    onChange={handleInputChange}
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={6}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    label="State"
                                    data={STATE_OPTIONS}
                                    name="deliveryState"
                                    textField="name"
                                    setValue={formInputs.deliveryState || ""}
                                    getvalue={setDropdownData}
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={6}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Pin Code"
                                    name="deliveryPinCode"
                                    value={formInputs.deliveryPinCode || ""}
                                    onChange={handleInputChange}
                                    type="number"
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {formInputs.erfType === "Nursing Service" && (
                  <div className="accordion mb-4" id="accordionNurseInfo">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-left arrowBtn"
                          >
                            <i className="bi bi-chevron-down"></i>
                          </Col>
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-center logoBtn"
                          >
                            <span className="iconLogo">NI</span>
                          </Col>
                          <Col xs={10} md={6} lg={8}>
                            <span className="headerTitle">
                              Nurse Information
                            </span>
                          </Col>
                        </Row>
                      </h2>

                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body pb-4">
                          <Row className="mb-4">
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                key={`erfNurse-${formInputs.erfType}`}
                                label="Select Nurse For ERF"
                                data={erfNurseList}
                                name="selectedNurseId"
                                textField="name"
                                setValue={
                                  selectedNurse
                                    ? selectedNurse.hospitalName ||
                                      selectedNurse.organisationName
                                    : formInputs.nurseName || ""
                                }
                                getvalue={setDropdownData}
                                disabled={disabled || !isFormEnabled}
                                isLoading={isLoadingErfNurse}
                                placeholder="Select Nurse"
                                required={true}
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Nurse Name"
                                name="nurseName"
                                value={formInputs.nurseName || ""}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Association Number"
                                name="nurseAssociateNumber"
                                value={formInputs.nurseAssociateNumber || ""}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Contact Number"
                                name="nurseMobileNr"
                                value={formInputs.nurseMobileNr || ""}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Email"
                                name="nurseEmail"
                                value={formInputs.nurseEmail || ""}
                                disabled={true}
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Pan No"
                                name="nursePanNo"
                                value={formInputs.nursePanNo || ""}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Address Line 1"
                                name="nurseAddress"
                                value={formInputs.nurseAddress || ""}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Address Line 2"
                                name="nurseAddress2"
                                value={formInputs.nurseAddress2 || ""}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="City"
                                name="nurseCity"
                                value={formInputs.nurseCity || ""}
                                disabled={true}
                              />
                            </Col>

                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Service Provider Code"
                                name="serviceProviderCode"
                                value={formInputs.serviceProviderCode}
                                disabled={true}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="Service Provider Name"
                                name="serviceProviderName"
                                value={formInputs.serviceProviderName}
                                disabled={true}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ADD BEFORE: formInputs.erfType !== "Nursing Service" && Product Details accordion */}
                {showApprovalRequired &&
                  priceViolations.length > 0 &&
                  !(
                    formInputs.approvers[0]?.userId ||
                    (formInputs.approverList || [])[0]?.userId
                  ) && (
                    <div
                      className="accordion mb-4"
                      id="accordionPriceViolation"
                    >
                      <div className="accordion-item border-warning">
                        <h2 className="accordion-header">
                          <Row className="accordion-button bg-warning bg-opacity-10">
                            <Col xs={1} className="text-center">
                              <i className="bi bi-exclamation-triangle-fill text-warning fs-4"></i>
                            </Col>
                            <Col xs={10}>
                              <span className="headerTitle text-dark">
                                Price Violations Detected
                              </span>
                              <span className="subHeaderTitle text-muted d-block">
                                Please review and either correct prices or
                                select an approver
                              </span>
                            </Col>
                            <Col xs={1} className="text-end">
                              {/* Dismiss button only when approver already selected */}
                              {/* {(formInputs.approvers[0]?.userId ||
    (formInputs.approverList || [])[0]?.userId) && (
    <button
      className="btn btn-sm btn-outline-secondary"
      onClick={() => setShowApprovalRequired(false)}
      title="Acknowledge violations"
    >
      <i className="bi bi-x-lg"></i>
    </button>
  )} */}
                            </Col>
                          </Row>
                        </h2>

                        <div className="accordion-collapse collapse show visible">
                          <div className="accordion-body pb-4 bg-light">
                            {/* Info Box */}
                            {/* <div className="alert alert-warning mb-3 d-flex align-items-start">
                            <i className="bi bi-info-circle-fill me-3 fs-5"></i>
                            <div className="flex-grow-1">
                              <h6 className="fw-bold mb-2">Action Required:</h6>
                              <p className="mb-0 small">
                                Select an approver below to proceed with current
                                prices.
                              </p>
                            </div>
                          </div> */}

                            {/* Approver Selection */}
                            <Row className="align-items-end">
                              <Col
                                xs={12}
                                md={6}
                                lg={4}
                                className="commTopButtonRightLeftPadding"
                              >
                                <ComboboxField
                                  label="Select Approver (Required for Price Violations)"
                                  data={APPROVED_BY_OPTIONS_LEVEL_ONE}
                                  name="violation_approver"
                                  textField="name"
                                  required={true}
                                  getvalue={(data) =>
                                    handleApproverSelection(0, data)
                                  }
                                  // setValue={formInputs.approvers[0]?.name || ""}
                                  setValue={
                                    formInputs.approvers[0]?.name ||
                                    (formInputs.approverList || [])[0]?.name ||
                                    ""
                                  }
                                  // disabled={disabled}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={4}
                                className="commTopButtonRightLeftPadding"
                              >
                                {formInputs.approvers[0]?.userId ||
                                (formInputs.approverList || [])[0]?.userId ? (
                                  <div className="alert alert-success mb-0 py-2">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    <strong>Approver Selected:</strong>{" "}
                                    {formInputs.approvers[0]?.name ||
                                      (formInputs.approverList || [])[0]?.name}
                                  </div>
                                ) : (
                                  <div className="alert alert-danger mb-0 py-2">
                                    <i className="bi bi-x-circle-fill me-2"></i>
                                    <strong>No approver selected yet</strong>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* GLOBAL RENT CONFIGURATION PANEL */}
                {(formInputs.erfType === "Rent" ||
                  formInputs.erfType === "Sale & Rent" ||
                  formInputs.erfType === "Nursing Service") && (
                  <div className="mb-2">
                    <div className="p-2 border rounded bg-white">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="text-primary mb-0">
                          Rent Period Configuration
                        </h6>
                      </div>
                      <GlobalRentConfigPanel
                        config={globalRentConfig}
                        onChange={setGlobalRentConfig}
                        disabled={disabled || !isFormEnabled}
                        itemCount={
                          formInputs.erfType === "Nursing Service"
                            ? nursingItems.length
                            : rentItems.length
                        }
                      />
                    </div>
                  </div>
                )}
                {isStoreUser &&
                  (formInputs.status === "In progress" ||
                    formInputs.status === "Cancelled") && (
                    <div
                      className="alert d-flex align-items-center mb-4"
                      role="alert"
                      style={{
                        backgroundColor:
                          formInputs.status === "Cancelled"
                            ? "#f8d7da"
                            : formInputs.erfStatus === "Dispatched"
                              ? "#d1e7dd"
                              : "#fff3cd",
                        borderColor:
                          formInputs.status === "Cancelled"
                            ? "#f5c2c7"
                            : formInputs.erfStatus === "Dispatched"
                              ? "#badbcc"
                              : "#ffecb5",
                      }}
                    >
                      <i
                        className={`bi ${
                          formInputs.status === "Cancelled"
                            ? "bi-x-circle-fill text-danger"
                            : formInputs.erfStatus === "Dispatched"
                              ? "bi-check-circle-fill text-success"
                              : "bi-info-circle-fill text-warning"
                        } fs-5 me-3`}
                      ></i>
                      <div>
                        <strong>
                          {formInputs.status === "Cancelled"
                            ? "ERF Cancelled"
                            : "ERF Not Yet Approved"}
                        </strong>
                        <span className="d-block small text-muted">
                          {formInputs.status === "Cancelled"
                            ? "This ERF has been cancelled. Barcode assignment and accessories management are disabled."
                            : `Current status: "${formInputs.status}". ERF must be approved before barcodes can be assigned.`}
                        </span>
                      </div>
                    </div>
                  )}

                {/* PRICE SCHEDULE (PRODUCT DETAILS) ACCORDION */}
                <div className="accordion mb-4" id="accordionProduct">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${
                              isCardVisible2
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                            onClick={toggleCardVisibility2}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">P</span>
                        </Col>
                        <Col xs={10} md={6} lg={8}>
                          <span className="headerTitle">Product Details</span>
                        </Col>
                        {/*    {formInputs.erfTypeCode === "SV" && (
                          <Col xs={2} md={2} lg={2} className="me-2">
                            <button
                              className="btnTable"
                              onClick={handlePrintBarcode}
                              // onClick={onClickPrint}
                            >
                              <span className="mx-2"><i class="bi bi-printer"></i></span> Service Barcode
                            </button>
                          </Col>
                        )} */}
                      </Row>
                    </h2>
                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisible2 ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body">
                        {/* RENT TABLE */}
                        {(formInputs.erfType === "Rent" ||
                          formInputs.erfType === "Sale & Rent") && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  Rent Products
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails("Rent");
                                  }}
                                  disabled={disabled || !isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add Rent
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              columns={getColumns("Rent")}
                              data={getDataWithTotal(rentItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No Rent items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                          </div>
                        )}

                        {/* SALE / SERVICE TABLE */}
                        {(formInputs.erfType === "Sale" ||
                          formInputs.erfType === "Sale & Rent" ||
                          formInputs.erfType === "Service") && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  {formInputs.erfType === "Service"
                                    ? "Service Items"
                                    : "Sale Products"}
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails(
                                      formInputs.erfType === "Sale & Rent"
                                        ? "Sale"
                                        : formInputs.erfType,
                                    );
                                  }}
                                  disabled={disabled || !isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add{" "}
                                  {formInputs.erfType === "Service"
                                    ? "Service"
                                    : "Sale"}{" "}
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              columns={getColumns(
                                formInputs.erfType === "Service"
                                  ? "Service"
                                  : "Sale",
                              )}
                              data={getDataWithTotal(saleItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                          </div>
                        )}

                        {/* NURSING SERVICE TABLE */}
                        {formInputs.erfType === "Nursing Service" && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  Nursing Service Items
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails("Nursing");
                                  }}
                                  disabled={disabled || !isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add Nursing
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              columns={getColumns("Nursing")}
                              data={getDataWithTotalNursing(nursingItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No Nursing items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {["Rent", "Sale & Rent", "Sale", "Service"].includes(
                  formInputs.erfType,
                ) && (
                  <ERFAccessoriesSection
                    rentItems={rentItems}
                    saleItems={saleItems}
                    accessoryItems={accessoryItems}
                    isStoreUser={isStoreUser}
                    isAdmin={isSalesAdmin}
                    status={formInputs.status}
                    erfStatusCode={formInputs.erfStatusCode}
                    isSaving={isSavingAccessories}
                    onOpenModal={(item) => {
                      setSelectedParentForAccessory(item);
                      setShowAccessoriesModal(true);
                    }}
                    onSaveAccessories={handleSaveAccessories}
                    onBarcodeChange={handleAccessoryBarcodeChange}
                    onBarcodeSubmit={handleAccessoryBarcodeSubmit}
                    onBarcodeAssign={handleAccessoryBarcodeAssign}
                    onRemoveAccessory={handleRemoveAccessory}
                    assigningBarcode={assigningAccessoryBarcode}
                    onOpenScanModal={(row) => {
                      setSelectedAccessoryForScan(row);
                      setShowBarcodeModal(true);
                    }}
                    isAssigningBarcode={isAssigningBarcode}
                  />
                )}

                {false && (
                  <div className="accordion my-4" id="accordionExample2">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-left arrowBtn"
                          >
                            <i
                              className={`bi ${isCardVisible3 ? "bi-chevron-right" : "bi-chevron-down"}`}
                              onClick={toggleCardVisibility3}
                            ></i>
                          </Col>
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-center logoBtn"
                          >
                            <span className="iconLogo">SH</span>
                          </Col>
                          <Col xs={6} md={6} lg={6}>
                            <span className="headerTitle">Service History</span>
                            <span className="subHeaderTitle">
                              Service History Details
                            </span>
                          </Col>
                          <Col
                            xs={4}
                            md={4}
                            lg={4}
                            className="d-flex gap-2 align-items-center justify-content-end flex-wrap"
                          >
                            {/*                       {rentItems.map((item) => {
                        const count = serviceCountByLocalId[String(item.local_id)] || 0;
                        return (
                          <span
                            key={item.local_id}
                            className={`badge ${count > 0 ? "bg-success" : "bg-secondary"}`}
                            title={item.materialDescription}
                            style={{ fontSize: "11px" }}
                          >
                            {item.barcode}: {count}
                          </span>
                        );
                      })} */}
                          </Col>
                        </Row>
                      </h2>

                      <div
                        className={`accordion-collapse collapse show ${isCardVisible3 ? "visible" : ""}`}
                      >
                        <div className="accordion-body">
                          <Row>
                            <Col xs={12} md={12} lg={12}>
                              <DataTable
                                title=""
                                columns={serviceHistoryColumns}
                                data={formInputs?.serviceHistory}
                                pagination
                                selectableRows
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PAYMENT DETAILS */}
                <div className="accordion mb-4" id="accordionPayment">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${visible.PAY ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={() => toggleVisibility("PAY")}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">PD</span>
                        </Col>
                        <Col xs={10} md={6} lg={8}>
                          <span className="headerTitle">Payment Details</span>
                        </Col>
                      </Row>
                    </h2>
                    <div
                      className={`accordion-collapse collapse show ${visible.PAY ? "visible" : ""}`}
                    >
                      <div className="accordion-body">
                        <Row>
                          <Col md={6} className="pe-md-4">
                            <h6 className="text-primary mb-3 mt-3 ps-2">
                              Payment Details
                            </h6>
                            <Row className="mb-3">
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Total Amount"
                                  value={totalPayable || ""}
                                  disabled
                                />
                              </Col>
                              {(formInputs?.erfTypeCode === "SL" ||
                                formInputs?.erfTypeCode === "RE" ||
                                formInputs?.erfTypeCode === "SLRE") && (
                                <Col
                                  md={12}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Cartage"
                                    name="cartage"
                                    onChange={handleInputChange}
                                    type="number"
                                    value={formInputs.cartage || ""}
                                    disabled={
                                      disabled ||
                                      !isFormEnabled ||
                                      (
                                        PRIVILEGE_SETTINGS[
                                          formInputs.privilegeCase
                                        ] || DEFAULT_PRIVILEGE_SETTINGS
                                      ).disableCartage
                                    }
                                  />
                                  {/* <Row>
                                      <Col
                                        md={4}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <label>
                                          <input
                                            type="checkbox"
                                            name="salesCartage"
                                            checked={cartageCheck.salesCartage}
                                            onChange={handleCartageCheck}
                                            disabled={
                                              disabled ||
                                              !isFormEnabled ||
                                              (
                                                PRIVILEGE_SETTINGS[
                                                formInputs.privilegeCase
                                                ] || DEFAULT_PRIVILEGE_SETTINGS
                                              ).disableCartage
                                            }
                                          />
                                          Sales Cartage
                                        </label>
                                      </Col>

                                      <Col
                                        md={4}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <label>
                                          <input
                                            type="checkbox"
                                            name="rentCartage"
                                            checked={cartageCheck?.rentCartage}
                                            onChange={handleCartageCheck}
                                            disabled={
                                              disabled ||
                                              !isFormEnabled ||
                                              (
                                                PRIVILEGE_SETTINGS[
                                                formInputs.privilegeCase
                                                ] || DEFAULT_PRIVILEGE_SETTINGS
                                              ).disableCartage
                                            }
                                          />
                                          Rent Cartage
                                        </label>
                                      </Col>
                                    </Row> */}
                                  <Row>
                                    <Col
                                      md={4}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <label
                                        style={{
                                          cursor:
                                            formInputs.erfTypeCode === "SLRE"
                                              ? "pointer"
                                              : "not-allowed",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          name="salesCartage"
                                          checked={cartageCheck.salesCartage}
                                          onChange={handleCartageCheck}
                                          // Only SLRE lets the user pick; SL/RE are auto-set and locked
                                          disabled={
                                            formInputs.erfTypeCode !== "SLRE" ||
                                            disabled ||
                                            !isFormEnabled
                                          }
                                        />
                                        Sales
                                      </label>
                                    </Col>

                                    <Col
                                      md={4}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <label
                                        style={{
                                          cursor:
                                            formInputs.erfTypeCode === "SLRE"
                                              ? "pointer"
                                              : "not-allowed",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          name="rentCartage"
                                          checked={cartageCheck.rentCartage}
                                          onChange={handleCartageCheck}
                                          disabled={
                                            formInputs.erfTypeCode !== "SLRE" ||
                                            disabled ||
                                            !isFormEnabled
                                          }
                                        />
                                        Rent
                                      </label>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              {formInputs.erfType !== "Nursing Service" && (
                                <Col
                                  md={12}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Installation Charges"
                                    name="installationCharges"
                                    onChange={handleInputChange}
                                    value={
                                      formInputs?.installationCharges || ""
                                    }
                                    type="number"
                                    disabled={disabled || !isFormEnabled}
                                  />
                                </Col>
                              )}
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Net Amount"
                                  value={netAmount}
                                  disabled
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Enter Received Amount"
                                  name="receivedAmount"
                                  onChange={handleInputChange}
                                  value={formInputs.receivedAmount || ""}
                                  type="number"
                                  // disabled={disabled || !isFormEnabled}
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    formInputs.paymentMode === "NR"
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Balance Amount"
                                  value={balanceAmount}
                                  disabled
                                />
                              </Col>
                            </Row>
                          </Col>
                          <Col md={6} className="ps-md-4 border-start-md">
                            <h6 className="text-primary mb-3 mt-3 ps-2">
                              Received Amt. Breakup
                            </h6>
                            <Row className="mb-3">
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <ComboboxField
                                  label="Payment Mode"
                                  data={paymentModeDropdown}
                                  name="paymentMode"
                                  textField="name"
                                  setValue={formInputs.paymentMode || ""}
                                  getvalue={setDropdownData}
                                  disabled={disabled || !isFormEnabled}
                                  required={true}
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cash"
                                  name="cashAmount"
                                  onChange={handleInputChange}
                                  value={formInputs.cashAmount || ""}
                                  type="number"
                                  // disabled={
                                  //   disabled ||
                                  //   !isFormEnabled ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableCash ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableAll
                                  // }
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableCash ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <Row>
                                  <Col md={4}>
                                    <label className="mt-2">
                                      {/* Dynamic Label (Req 3) */}
                                      {formInputs.paymentMode === "Card&Cash"
                                        ? "Card"
                                        : formInputs.paymentMode ===
                                            "Cheque&Cash"
                                          ? "Cheque"
                                          : formInputs.paymentMode ===
                                              "Card&Cheque"
                                            ? "Cheque"
                                            : "Bal. Amt"}
                                    </label>
                                  </Col>
                                  <Col md={8}>
                                    <InputField
                                      className="inputBox"
                                      name="balanceAmtBreakup"
                                      value={formInputs.balanceAmtBreakup}
                                      disabled
                                    />
                                  </Col>
                                </Row>
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cheque No."
                                  name="chequeNo"
                                  onChange={handleInputChange}
                                  value={formInputs.chequeNo || ""}
                                  // disabled={
                                  //   disabled ||
                                  //   !isFormEnabled ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableCheque ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableAll
                                  // }
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableCheque ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Card Trans. No."
                                  name="cardTransNo"
                                  onChange={handleInputChange}
                                  value={formInputs.cardTransNo || ""}
                                  // disabled={
                                  //   disabled ||
                                  //   !isFormEnabled ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableCard ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableAll
                                  // }
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableCard ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Bank Name"
                                  name="bankName"
                                  onChange={handleInputChange}
                                  value={formInputs.bankName || ""}
                                  // disabled={
                                  //   disabled ||
                                  //   !isFormEnabled ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableBank ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableAll
                                  // }
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableBank ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Receipt No."
                                  name="receiptNo"
                                  onChange={handleInputChange}
                                  value={formInputs.receiptNo || ""}
                                  // disabled={
                                  //   disabled ||
                                  //   !isFormEnabled ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableReceipt ||
                                  //   (
                                  //     PAYMENT_MODE_RULES[
                                  //       formInputs.paymentMode
                                  //     ] || {}
                                  //   ).disableAll
                                  // }
                                  disabled={
                                    disabled ||
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableReceipt ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECURITY DETAILS */}
                {(formInputs.erfType === "Rent" ||
                  formInputs.erfType === "Sale & Rent") &&
                  !isHospital && (
                    <div className="accordion mb-4" id="accordionSecurity">
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <Row className="accordion-button">
                            <Col
                              xs={1}
                              md={1}
                              lg={1}
                              className="text-left arrowBtn"
                            >
                              <i
                                className={`bi ${visible.SEC ? "bi-chevron-down" : "bi-chevron-right"}`}
                                onClick={() => toggleVisibility("SEC")}
                              ></i>
                            </Col>
                            <Col
                              xs={1}
                              md={1}
                              lg={1}
                              className="text-center logoBtn"
                            >
                              <span className="iconLogo">SD</span>
                            </Col>
                            <Col xs={10} md={6} lg={8}>
                              <span className="headerTitle">
                                Security Details
                              </span>
                            </Col>
                          </Row>
                        </h2>
                        <div
                          className={`accordion-collapse collapse show ${visible.SEC ? "visible" : ""}`}
                        >
                          <div className="accordion-body">
                            <Row>
                              <Col md={6} className="pe-md-4">
                                <Row className="mb-3">
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Standard Security Amount"
                                      // value={
                                      //   formInputs.standardSecurityAmt || ""
                                      // }
                                      value={totals.standardSecurityAmt.toFixed(
                                        2,
                                      )}
                                      disabled
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Total Security Amount"
                                      value={totals.security.toFixed(2)}
                                      disabled={true}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Enter Recd. Security Amount"
                                      name="receivedSecurityAmt"
                                      onChange={handleInputChange}
                                      value={
                                        formInputs.receivedSecurityAmt || ""
                                      }
                                      type="number"
                                      // disabled={
                                      //   disabled ||
                                      //   !isFormEnabled ||
                                      //   (
                                      //     PRIVILEGE_SETTINGS[
                                      //       formInputs.privilegeCase
                                      //     ] || DEFAULT_PRIVILEGE_SETTINGS
                                      //   ).disableSecurityDetails
                                      // }
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !isSecurityAmtModeSelected ||
                                        formInputs.securityAmtMode === "NR" ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Balance Security Amount"
                                      value={balanceSecurityAmt || ""}
                                      disabled
                                    />
                                  </Col>
                                </Row>
                              </Col>
                              <Col md={6} className="ps-md-4 border-start-md">
                                <Row className="mb-3">
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Enter Pincode"
                                      name="securityPincode"
                                      value={formInputs.securityPincode || ""}
                                      onChange={handleInputChange}
                                      disabled={disabled || !isFormEnabled}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <ComboboxField
                                      label="Security Amount Mode"
                                      data={(() => {
                                        // Filter options based on Privilege Case settings
                                        const settings =
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS;
                                        if (settings.securityAmtModeOptions) {
                                          return paymentModeDropdown.filter(
                                            (opt) =>
                                              settings.securityAmtModeOptions.includes(
                                                opt.name,
                                              ),
                                          );
                                        }
                                        return paymentModeDropdown;
                                      })()}
                                      name="securityAmtMode"
                                      textField="name"
                                      setValue={
                                        formInputs.securityAmtMode || ""
                                      }
                                      getvalue={setDropdownData}
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        !formInputs.securityPincode
                                      }
                                      required={true}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Cash Amount Received"
                                      name="securityCashAmt"
                                      onChange={handleInputChange}
                                      value={formInputs.securityCashAmt || ""}
                                      type="number"
                                      // disabled={
                                      //   disabled ||
                                      //   !isFormEnabled ||
                                      //   !formInputs.securityPincode ||
                                      //   (
                                      //     PRIVILEGE_SETTINGS[
                                      //       formInputs.privilegeCase
                                      //     ] || DEFAULT_PRIVILEGE_SETTINGS
                                      //   ).disableSecurityDetails ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableCash ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableAll
                                      // }
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        securityAmtRules.disableCash ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>

                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <Row>
                                      <Col md={4}>
                                        <label className="mt-2">
                                          {formInputs.securityAmtMode ===
                                          "Card&Cash"
                                            ? "Card"
                                            : formInputs.securityAmtMode ===
                                                "Cheque&Cash"
                                              ? "Cheque"
                                              : formInputs.securityAmtMode ===
                                                  "Card&Cheque"
                                                ? "Cheque"
                                                : "Bal. Amt"}
                                        </label>
                                      </Col>
                                      <Col md={8}>
                                        <InputField
                                          className="inputBox"
                                          name="balanceSecurityBreakup"
                                          value={
                                            formInputs.balanceSecurityBreakup ||
                                            ""
                                          }
                                          disabled
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Cheque No."
                                      name="securityChequeNo"
                                      onChange={handleInputChange}
                                      value={formInputs.securityChequeNo || ""}
                                      // disabled={
                                      //   disabled ||
                                      //   !isFormEnabled ||
                                      //   !formInputs.securityPincode ||
                                      //   (
                                      //     PRIVILEGE_SETTINGS[
                                      //       formInputs.privilegeCase
                                      //     ] || DEFAULT_PRIVILEGE_SETTINGS
                                      //   ).disableSecurityDetails ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableCheque ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableAll
                                      // }
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        securityAmtRules.disableCheque ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Card Trans. No."
                                      name="securityCardTransNo"
                                      onChange={handleInputChange}
                                      value={
                                        formInputs.securityCardTransNo || ""
                                      }
                                      // disabled={
                                      //   disabled ||
                                      //   !isFormEnabled ||
                                      //   !formInputs.securityPincode ||
                                      //   (
                                      //     PRIVILEGE_SETTINGS[
                                      //       formInputs.privilegeCase
                                      //     ] || DEFAULT_PRIVILEGE_SETTINGS
                                      //   ).disableSecurityDetails ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableCard ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableAll
                                      // }
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        securityAmtRules.disableCard ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Bank Name"
                                      name="securityBankName"
                                      onChange={handleInputChange}
                                      value={formInputs.securityBankName || ""}
                                      // disabled={
                                      //   disabled ||
                                      //   !isFormEnabled ||
                                      //   !formInputs.securityPincode ||
                                      //   (
                                      //     PRIVILEGE_SETTINGS[
                                      //       formInputs.privilegeCase
                                      //     ] || DEFAULT_PRIVILEGE_SETTINGS
                                      //   ).disableSecurityDetails ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableBank ||
                                      //   (
                                      //     PAYMENT_MODE_RULES[
                                      //       formInputs.securityAmtMode
                                      //     ] || {}
                                      //   ).disableAll
                                      // }
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        (
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS
                                        ).disableSecurityDetails ||
                                        securityAmtRules.disableBank ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>

                            {/* Security Mode Would be (Req 4) */}
                            {(formInputs.erfType === "Rent" ||
                              formInputs.erfType === "Sale & Rent") && (
                              <div
                                className="accordion mb-4"
                                id="accordionSecurityMode"
                              >
                                <div className="accordion-item">
                                  <div className="accordion-body p-4">
                                    <h6 className="text-primary mb-3">
                                      Security Mode Would be
                                    </h6>
                                    <Row className="align-items-center">
                                      <Col
                                        md={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <ComboboxField
                                          label="Security Mode"
                                          data={paymentModeDropdown.filter(
                                            (m) => m.name !== "NR",
                                          )}
                                          name="securityMode"
                                          textField="name"
                                          // setValue={formInputs.securityMode || ""}
                                          setValue={
                                            paymentModeDropdown.find(
                                              (m) =>
                                                m.name ===
                                                formInputs.securityMode,
                                            ) || ""
                                          }
                                          getvalue={setDropdownData}
                                          disabled={disabled || !isFormEnabled}
                                          required={true}
                                        />
                                      </Col>
                                      <Col
                                        md={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <InputField
                                          className="inputBox"
                                          label="Cash Amount"
                                          name="securityModeCash"
                                          onChange={handleInputChange}
                                          value={
                                            formInputs.securityModeCash || ""
                                          }
                                          type="number"
                                          disabled={
                                            disabled ||
                                            !isFormEnabled ||
                                            ![
                                              "Card&Cash",
                                              "Cheque&Cash",
                                            ].includes(formInputs.securityMode)
                                          }
                                        />
                                      </Col>
                                      <Col
                                        md={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <InputField
                                          className="inputBox"
                                          label="Balance Security Amount"
                                          value={(() => {
                                            // Point 4, 25, 26, 27
                                            if (
                                              [
                                                "Cash",
                                                "Euro",
                                                "Dollar",
                                              ].includes(
                                                formInputs.securityMode,
                                              )
                                            ) {
                                              return 0;
                                            }
                                            if (
                                              [
                                                "Card",
                                                "Cheque",
                                                "Online",
                                                "Paytm",
                                                "UPI",
                                                "Card&Cheque",
                                              ].includes(
                                                formInputs.securityMode,
                                              )
                                            ) {
                                              return balanceSecurityAmt;
                                            }
                                            // Else (Card&Cash, Cheque&Cash) - calculated via useEffect/State, displayed here
                                            return formInputs.securityModeBal;
                                          })()}
                                          disabled
                                        />
                                      </Col>
                                    </Row>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Remarks (Moved out of accordion) */}
                <div className="mb-4 p-4 border rounded bg-white">
                  <TextAreaField
                    className="inputBox"
                    label="Remarks"
                    name="remarks"
                    value={formInputs.remarks || ""}
                    onChange={handleInputChange}
                    disabled={disabled || !isFormEnabled}
                  />
                </div>

                {props.children}

                {/* {documentNumber && (
                  <ERFCreditNoteSection documentNumber={documentNumber} />
                )} */}
                {documentNumber && (
                  <ERFCreditNoteSection
                    documentNumber={documentNumber}
                    isRecordExtended={
                      !!formInputs.childExtensionNr ||
                      formInputs.extensionLifecycleStatus === "EXTENDED"
                    }
                  />
                )}

                {documentNumber && (
                  <ERFPaymentSection
                    documentNumber={documentNumber}
                    allowAddBilling={true}
                    allowAddSecurity={true}
                    allowAddCreditNote={isSalesAdmin || isPCDUser}
                    allowUpdatePayentButton={
                      isSalesAdmin || isPCDUser || isServiceExecutive
                    }
                  />
                )}

                {documentNumber && (
                  <ERFTravelSection
                    travelHistory={travelHistory}
                    erfStatusCode={formInputs.erfStatusCode ?? ""}
                    onSave={handleTravelSave}
                    onConfirmRow={handleTravelConfirmRow}
                    disabled={false}
                  />
                )}

                {documentNumber && (
                  <ERFTallyStatusSection documentNumber={documentNumber} />
                )}

                {/* FOOTER - Back Button */}
                <div className="pageFooter mt-4 mb-4">
                  <Row className="justify-content-end">
                    <Col xs={6} md={2} className="pb-2">
                      <button
                        className="btnTable btn-success text-white w-100"
                        onClick={() =>
                          navigate(Constants.CUSTOMER_V5_MR_ERF_LIST)
                        }
                      >
                        Back
                      </button>
                    </Col>
                  </Row>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* <MaterialSelectionModalV5
        show={modalShow}
        onHide={hideModal}
        onSelectedData={handleGetSelectedMaterial}
      /> */}

      <MaterialSelectionModalV5
        show={modalShow}
        onHide={hideModal}
        onSelectedData={handleGetSelectedMaterial}
        itemType={activeItemType}
        itemTypeCode={activeItemTypeCode}
        selectedHospitalCode={selectedHospital?.AssociateNumber || ""}
        selectedHospitalName={selectedHospital?.hospitalName || ""}
        erfForType={erfForType || "Patient"}
      />

      <FailureModal
        show={getShowFailureModal}
        onHide={hideFailureModal}
        failuremodaldata={failureModalData}
        requiredfields={getErrorFields}
      />

      <UpdateRemarksModal
        show={updateModalShow}
        onHide={() => updateHideModal()}
        onvaluechange={handleValueChange}
      />

      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={() => itemLevelHideModal()}
        columns={getItemLevelColumns}
        data={getItemLevelData}
        title={getModalTitle}
      />

      <ErfExtenssionModal
        show={getModalShowExtenssionModal}
        onHide={hideModalExtenssionModal}
        data={getExtenssionData}
        onSave={handleSaveExtensions}
      />

      <BarcodeScannerModal
        show={showBarcodeModal}
        onHide={() => setShowBarcodeModal(false)}
        onScanSuccess={handleBarcodeSuccess}
      />

      <ERFServiceModal
        show={getShowServiceModal}
        onHide={hideSupplierInviteModal}
        onSave={handleSaveService}
        // getServiceData={getServiceData}
        // editMode={editMode}
        // vehicleRegistrationNr={formInputs?.vehicleRegistrationNr}

        // onSubmit={inviteSupplier}
        data={formInputs}
      />

      <ERFAccessoriesModal
        show={showAccessoriesModal}
        onHide={() => {
          setShowAccessoriesModal(false);
          setSelectedParentForAccessory(null);
        }}
        godownCode={
          saleItems.some(
            (s) => s.local_id === selectedParentForAccessory?.local_id,
          )
            ? "SL"
            : "RE"
        }
        parentItem={selectedParentForAccessory}
        erfTypeCode={formInputs.erfTypeCode}
        onSave={handleAddAccessories}
      />
    </main>
  );
};

export default ERFDetail;
